import { t } from "./i18n";
import { appendCodexDebugLog } from "./codexDebugLog";
import { RandomTeamsAudio } from "./randomTeamsAudio";
import {
    RandomTeamsCanvasRenderer,
    type RandomTeamsCanvasFramePerfSample,
    type RandomTeamsCanvasConnectorGlow,
    type RandomTeamsCanvasImpactBurst
} from "./randomTeamsCanvasRenderer";
import { SITE_TUNING } from "./siteTuning";
import { renderZiarcadiaIcon } from "./ziarcadiaIcons";
import { inflectRandomTeamsName, MAX_RANDOM_TEAMS_NAME_LENGTH, normalizeRandomTeamsName } from "./randomTeamsNaming";
import {
    parseRandomTeamsRosterText,
    readRandomTeamsRosterFile,
    type RandomTeamsRosterIssue,
    type RandomTeamsRosterMember
} from "./randomTeamsRoster";
import { createRandomTeamsLaunchMotion } from "./randomTeamsLaunchMotion";
import { fitRandomTeamsName } from "./randomTeamsNameLayout";

type RandomTeamsMode = "teams" | "orders";
type RandomTeamsRosterMode = "ids" | "names";
type LateIdMode = "add" | "remove";

const RANDOM_TEAMS_WIPE_ICON = renderZiarcadiaIcon("clear", {
    className: "random-teams-wipe-icon",
    legacyClassPrefix: "random-teams-wipe"
});

type DropDestination =
    | {
          zone: "team";
          teamIndex: number;
          index: number;
      }
    | {
          zone: "floor";
          index: number;
      };

type DragContext = {
    memberId: number;
    pointerId: number;
    ghost: HTMLElement;
    stageRect: DOMRect;
    teamTokenRects: Map<number, TeamTokenRect[]>;
    teamDropRects: TeamDropRect[];
    floorRect: DOMRect | null;
    floorTokenRects: FloorTokenRect[];
    offsetX: number;
    offsetY: number;
    lastClientX: number;
    lastClientY: number;
};

type TeamTokenRect = {
    memberId: number;
    index: number;
    rect: DOMRect;
};

type TeamDropRect = {
    teamIndex: number;
    rect: DOMRect;
};

type FloorTokenRect = {
    index: number;
    rect: DOMRect;
};

type LaunchToken = {
    memberId: number;
    element: HTMLElement;
    zone: "team" | "floor";
};

type Validation = {
    teamCount: number | null;
    memberCount: number | null;
    namedRoster: RandomTeamsRosterMember[] | null;
    rosterIssues: RandomTeamsRosterIssue[];
    isNamedRoster: boolean;
    validExcludedIds: number[];
    invalidExcludedIndices: Set<number>;
    includedIds: number[];
    canGenerate: boolean;
    currentSignature: string | null;
    isRepeatAction: boolean;
    actionLabel: string;
    helperText: string;
};

type PreviewSnapshot = {
    mode: RandomTeamsMode;
    teamCount: number;
    includedCount: number;
};

type RandomTeamsDragPerfSession = {
    memberId: number;
    mode: RandomTeamsMode | null;
    startedAt: number;
    captureMs: number;
    teamDropRectCount: number;
    teamTokenRectCount: number;
    floorTokenRectCount: number;
    frameCount: number;
    totalFrameMs: number;
    maxFrameMs: number;
    slowFrameCount: number;
    hoverChangeCount: number;
};

type RandomTeamsCanvasPerfWindow = {
    startedAt: number;
    frameCount: number;
    totalMs: number;
    maxMs: number;
    slowFrameCount: number;
    phaseTotals: Record<string, number>;
    maxConnectorGlowCount: number;
    maxImpactBurstCount: number;
    canvasCssWidth: number;
    canvasCssHeight: number;
    devicePixelRatio: number;
};

type RandomTeamsPresentationScaleCache = {
    signature: string;
    scale: number;
};

type TranslationKey = Parameters<typeof t>[0];

const getRandomTeamsRosterIssueKey = (issue: RandomTeamsRosterIssue): TranslationKey => {
    switch (issue) {
        case "duplicate-id":
            return "randomTeamsRosterDuplicateId";
        case "invalid-id":
            return "randomTeamsRosterInvalidId";
        case "missing-name":
            return "randomTeamsRosterMissingName";
        case "no-names":
            return "randomTeamsRosterNoNames";
    }
};

export type RandomTeamsResult = {
    configSignature: string;
    mode: RandomTeamsMode;
    teamCount: number;
    memberCount: number;
    excludedIds: number[];
    teams: number[][];
    orderSlotCounts: number[];
    floorIds: number[];
    participants?: RandomTeamsRosterMember[];
};

export type RandomTeamsToolState = {
    mode: RandomTeamsMode;
    groupName?: string;
    teamCount: string;
    memberCount: string;
    excludedIds: string[];
    result: RandomTeamsResult | null;
    lastGeneratedSignature: string | null;
    resultCreatedAt: number | null;
    showExcludedOnFloor?: boolean;
    rosterText?: string;
    rosterMode?: RandomTeamsRosterMode;
};

type RandomTeamsToolRefs = {
    configCard: HTMLElement | null;
    modeToggle: HTMLButtonElement | null;
    rosterModeToggle: HTMLButtonElement | null;
    namesModeLabel: HTMLElement | null;
    namesPanel: HTMLElement | null;
    namesTextarea: HTMLTextAreaElement | null;
    namesFileInput: HTMLInputElement | null;
    namesImportButton: HTMLButtonElement | null;
    namesStatus: HTMLElement | null;
    namesPreview: HTMLElement | null;
    namesEditButton: HTMLButtonElement | null;
    countSuffix: HTMLElement | null;
    groupNameInput: HTMLInputElement | null;
    memberPrefix: HTMLElement | null;
    memberLabel: HTMLElement | null;
    teamCountInput: HTMLInputElement | null;
    memberCountInput: HTMLInputElement | null;
    todayCount: HTMLElement | null;
    stageAttendance: HTMLElement | null;
    exclusionList: HTMLElement | null;
    helper: HTMLElement | null;
    includedChip: HTMLElement | null;
    excludedChip: HTMLElement | null;
    mobileExclusionCommitButton: HTMLButtonElement | null;
    startButton: HTMLButtonElement | null;
    stageCard: HTMLElement | null;
    stageLead: HTMLElement | null;
    stageStatus: HTMLElement | null;
    board: HTMLElement | null;
    stage: HTMLElement | null;
    stageCanvas: HTMLCanvasElement | null;
    dragLayer: HTMLElement | null;
    fullscreenButton: HTMLButtonElement | null;
    soundButton: HTMLButtonElement | null;
    animationButton: HTMLButtonElement | null;
    excludedFloorButton: HTMLButtonElement | null;
    wipeButton: HTMLButtonElement | null;
    lateAddToggleButton: HTMLButtonElement | null;
    lateAddPanel: HTMLElement | null;
    lateAddInput: HTMLInputElement | null;
    lateAddSubmitButton: HTMLButtonElement | null;
    lateAddHelper: HTMLElement | null;
    lateRemoveToggleButton: HTMLButtonElement | null;
    lateRemovePanel: HTMLElement | null;
    lateRemoveInput: HTMLInputElement | null;
    lateRemoveSubmitButton: HTMLButtonElement | null;
    lateRemoveHelper: HTMLElement | null;
};

const MAX_DIGITS = 4;
const RANDOM_TEAMS_SETTINGS_STORAGE_KEY = "ziarcadia:random-teams-settings";
const RANDOM_TEAMS_RESULT_STORAGE_KEY = "ziarcadia:random-teams-last-result";
const RANDOM_TEAMS_RESULT_RESTORE_TTL_MS = 8 * 60 * 60 * 1000;
const RANDOM_TEAMS_SOUND_STORAGE_KEY = "ziarcadia:random-teams-sound";
const RANDOM_TEAMS_ANIMATION_STORAGE_KEY = "ziarcadia:random-teams-animations";

const escapeHtml = (value: string): string =>
    value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

const sanitizeDigits = (value: string): string => value.replace(/\D/g, "").slice(0, MAX_DIGITS);

const parsePositiveInteger = (value: string): number | null => {
    if (!value) {
        return null;
    }

    const parsed = Number.parseInt(value, 10);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

const formatRosterText = (participants: RandomTeamsRosterMember[] | undefined): string =>
    participants
        ?.map((participant) =>
            participant.studentNumber === null
                ? `"${participant.name.replaceAll('"', '""')}"`
                : `${participant.studentNumber}, "${participant.name.replaceAll('"', '""')}"`
        )
        .join("\n") ?? "";

const readStoredSoundEnabled = (): boolean => {
    try {
        return localStorage.getItem(RANDOM_TEAMS_SOUND_STORAGE_KEY) !== "0";
    } catch {
        return true;
    }
};

const writeStoredSoundEnabled = (isEnabled: boolean) => {
    try {
        localStorage.setItem(RANDOM_TEAMS_SOUND_STORAGE_KEY, isEnabled ? "1" : "0");
    } catch {
        // Ignore storage failures; the in-memory toggle still works for this page.
    }
};

const readStoredAnimationsEnabled = (): boolean => {
    try {
        return localStorage.getItem(RANDOM_TEAMS_ANIMATION_STORAGE_KEY) !== "0";
    } catch {
        return true;
    }
};

const writeStoredAnimationsEnabled = (isEnabled: boolean) => {
    try {
        localStorage.setItem(RANDOM_TEAMS_ANIMATION_STORAGE_KEY, isEnabled ? "1" : "0");
    } catch {
        // Ignore storage failures; the in-memory toggle still works for this page.
    }
};

const shuffle = <Item>(items: Item[]): Item[] => {
    const next = [...items];

    for (let index = next.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(Math.random() * (index + 1));
        [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    }

    return next;
};

const pickRandomItem = <Item>(items: Item[]): Item | null => {
    if (items.length === 0) {
        return null;
    }

    if (items.length === 1) {
        return items[0] ?? null;
    }

    return items[Math.floor(Math.random() * items.length)] ?? null;
};

const buildRandomizedTeamSizes = (teamCount: number, memberCount: number): number[] => {
    if (teamCount <= 0) {
        return [];
    }

    const baseSize = Math.floor(memberCount / teamCount);
    const remainder = memberCount % teamCount;
    const sizes = Array.from({ length: teamCount }, () => baseSize);

    shuffle(Array.from({ length: teamCount }, (_, index) => index))
        .slice(0, remainder)
        .forEach((teamIndex) => {
            sizes[teamIndex] += 1;
        });

    return sizes;
};

const ensureTrailingExclusionInput = (values: string[]): string[] => {
    const filled = values.map(sanitizeDigits).filter(Boolean);
    return [...filled, ""];
};

const clampIndex = (value: number, length: number): number =>
    Math.max(0, Math.min(value, length));

const clampNumber = (value: number, min: number, max: number): number =>
    Math.max(min, Math.min(value, max));

const parseCssPixels = (value: string): number | null => {
    const parsed = Number.parseFloat(value);

    return Number.isFinite(parsed) ? parsed : null;
};

const getPointDistance = (fromX: number, fromY: number, toX: number, toY: number): number =>
    Math.hypot(toX - fromX, toY - fromY);

const MEMBER_TOKEN_SIZE = SITE_TUNING.randomTeams.layout.memberTokenSizePx;
const PRESENTATION_MEMBER_TOKEN_SIZE = SITE_TUNING.randomTeams.layout.presentationMemberTokenSizePx;
const DESKTOP_TEAM_PRESENTATION_MAX_SCALE = SITE_TUNING.randomTeams.layout.desktopTeamPresentationMaxScale;
const ORDER_TOKEN_SLOT_WIDTH = SITE_TUNING.randomTeams.layout.orderTokenSlotWidthPx;
const ORDER_ROW_COLUMN_GAP = SITE_TUNING.randomTeams.layout.orderRowColumnGapPx;
const ORDER_ROW_SIDE_PADDING = SITE_TUNING.randomTeams.layout.orderRowSidePaddingPx;
const ORDER_MEMBERS_INLINE_PADDING = SITE_TUNING.randomTeams.layout.orderMembersInlinePaddingPx;
const ORDER_CONNECTOR_LANE_TOKEN_RESERVE = SITE_TUNING.randomTeams.layout.orderConnectorLaneTokenReserve;
const MAX_ORDER_TOKENS_PER_ROW = SITE_TUNING.randomTeams.layout.maxOrderTokensPerRow;
const MIN_ORDER_TOKENS_PER_ROW = SITE_TUNING.randomTeams.layout.minOrderTokensPerRow;
const MAX_ANIMATED_LAUNCH_MEMBER_COUNT = SITE_TUNING.randomTeams.animation.maxAnimatedLaunchMemberCount;
const ORDER_FINISHED_LANE_MIN_WIDTH = SITE_TUNING.randomTeams.layout.orderFinishedLaneMinWidthPx;
const ORDER_FINISHED_LANE_WIDTH = SITE_TUNING.randomTeams.layout.orderFinishedLaneWidthPx;
const ORDER_FINISHED_LANE_GAP = SITE_TUNING.randomTeams.layout.orderFinishedLaneGapPx;
const MOBILE_KEYBOARD_MIN_INSET_PX = SITE_TUNING.randomTeams.layout.mobileKeyboardMinInsetPx;
const ORDER_ROW_COLUMN_GAP_MOBILE = SITE_TUNING.randomTeams.layout.orderRowColumnGapMobilePx;
const LAUNCH_STAGGER_MS = SITE_TUNING.randomTeams.animation.launchStaggerMs;
const SIDE_BOUNCE_LAUNCH_STAGGER_MS = SITE_TUNING.randomTeams.animation.sideBounceLaunchStaggerMs;
const MAX_LAUNCH_STAGGER_WINDOW_MS = SITE_TUNING.randomTeams.animation.maxLaunchStaggerWindowMs;
const LAUNCH_TIME_SCALE = SITE_TUNING.randomTeams.animation.launchTimeScale;
const AUDIO_SYNC_LEAD_MS = SITE_TUNING.randomTeams.animation.audioSyncLeadMs;
const MANUAL_DROP_AUDIO_SYNC_LEAD_MS = SITE_TUNING.randomTeams.animation.manualDropAudioSyncLeadMs;
const STALE_AUDIO_DELAY_MS = SITE_TUNING.randomTeams.animation.staleAudioDelayMs;
const PRESENTATION_UPSCALE_DAMPING = 0.88;
const RANDOM_TEAMS_PERF_PREFIX = "[Ziarcadia][RandomTeamsPerf]";
const RANDOM_TEAMS_SLOW_FRAME_MS = 8;
const RANDOM_TEAMS_CANVAS_WINDOW_MS = 2000;
const RANDOM_TEAMS_CANVAS_SLOW_LOG_COOLDOWN_MS = 400;
const DEFAULT_CONFIG_BY_MODE: Record<RandomTeamsMode, { teamCount: string; memberCount: string }> = {
    teams: {
        teamCount: "4",
        memberCount: "20"
    },
    orders: {
        teamCount: "1",
        memberCount: "15"
    }
};

type StoredRandomTeamsSettings = {
    version: 1;
    mode: RandomTeamsMode;
    groupName: string;
    teamCount: string;
    memberCount: string;
    rosterMode: RandomTeamsRosterMode;
};

type StoredRandomTeamsResultSnapshot = {
    version: 1;
    createdAt: number;
    updatedAt: number;
    mode: RandomTeamsMode;
    groupName?: string;
    teamCount: string;
    memberCount: string;
    excludedIds: string[];
    result: RandomTeamsResult;
    lastGeneratedSignature: string | null;
    showExcludedOnFloor: boolean;
    rosterText?: string;
};

const roundRandomTeamsPerfMs = (value: number): number => Number(value.toFixed(2));

const formatRandomTeamsPerfPhases = (phases: Record<string, number>): string =>
    Object.entries(phases)
        .filter(([, value]) => value > 0.05)
        .sort((left, right) => right[1] - left[1])
        .slice(0, 4)
        .map(([phase, value]) => `${phase}:${roundRandomTeamsPerfMs(value)}ms`)
        .join(", ");

const recordRandomTeamsPerfPhase = (phases: Record<string, number>, phase: string, startedAt: number) => {
    phases[phase] = (phases[phase] ?? 0) + (performance.now() - startedAt);
};

const logRandomTeamsPerf = (message: string) => {
    if (!isRandomTeamsPerfEnabled()) {
        return;
    }

    appendCodexDebugLog("random-teams", "info", message);
    console.info(RANDOM_TEAMS_PERF_PREFIX, message);
};

const isRandomTeamsPerfEnabled = (() => {
    let cached: boolean | null = null;

    return (): boolean => {
        if (cached !== null) {
            return cached;
        }

        try {
            const params = new URLSearchParams(window.location.search);
            const localOverride = window.localStorage.getItem("ziarcadia:random-teams-perf");
            cached =
                localOverride === "0"
                    ? false
                    : params.has("randomTeamsPerfOff") ||
                        params.has("perfOff")
                      ? false
                      : params.has("randomTeamsPerf") ||
                params.has("perf") ||
                params.has("randomTeamsDebug") ||
                params.has("debug") ||
                localOverride === "1" ||
                localOverride === null ||
                window.localStorage.getItem("ziarcadia:random-teams-debug") === "1" ||
                window.localStorage.getItem("ziarcadia:debug") === "1";
        } catch {
            cached = true;
        }

        return cached;
    };
})();

const formatDropDestination = (destination: DropDestination | null): string => {
    if (!destination) {
        return "none";
    }

    return destination.zone === "team" ? `team:${destination.teamIndex}:${destination.index}` : `floor:${destination.index}`;
};

const isRandomTeamsMode = (value: unknown): value is RandomTeamsMode =>
    value === "teams" || value === "orders";

const isRandomTeamsRosterMode = (value: unknown): value is RandomTeamsRosterMode =>
    value === "ids" || value === "names";

const normalizeStoredCount = (value: unknown, fallback: string): string => {
    if (typeof value !== "string") {
        return fallback;
    }

    const sanitizedValue = sanitizeDigits(value);
    return parsePositiveInteger(sanitizedValue) === null ? fallback : sanitizedValue;
};

const normalizeStoredExcludedInputValues = (value: unknown): string[] => {
    if (!Array.isArray(value)) {
        return [""];
    }

    return ensureTrailingExclusionInput(
        value.map((entry) => (typeof entry === "string" || typeof entry === "number" ? sanitizeDigits(String(entry)) : ""))
    );
};

const isPositiveSafeInteger = (value: unknown): value is number =>
    typeof value === "number" && Number.isSafeInteger(value) && value > 0;

const isNonNegativeSafeInteger = (value: unknown): value is number =>
    typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

const normalizePositiveIntegerArray = (value: unknown): number[] | null =>
    Array.isArray(value) && value.every(isPositiveSafeInteger) ? [...value] : null;

const normalizeNonNegativeIntegerArray = (value: unknown): number[] | null =>
    Array.isArray(value) && value.every(isNonNegativeSafeInteger) ? [...value] : null;

const normalizePositiveIntegerMatrix = (value: unknown): number[][] | null =>
    Array.isArray(value)
        ? value.reduce<number[][] | null>((normalizedRows, row) => {
              if (normalizedRows === null) {
                  return null;
              }

              const normalizedRow = normalizePositiveIntegerArray(row);
              return normalizedRow ? [...normalizedRows, normalizedRow] : null;
          }, [])
        : null;

const hasDuplicateMemberIds = (memberIds: number[]): boolean => new Set(memberIds).size !== memberIds.length;

const cloneRandomTeamsResult = (result: RandomTeamsResult): RandomTeamsResult => ({
    configSignature: result.configSignature,
    mode: result.mode,
    teamCount: result.teamCount,
    memberCount: result.memberCount,
    excludedIds: [...result.excludedIds],
    teams: result.teams.map((members) => [...members]),
    orderSlotCounts: [...result.orderSlotCounts],
    floorIds: [...result.floorIds],
    ...(result.participants ? { participants: result.participants.map((participant) => ({ ...participant })) } : {})
});

const normalizeStoredParticipants = (value: unknown, memberCount: number): RandomTeamsRosterMember[] | null => {
    if (!Array.isArray(value) || value.length !== memberCount) return null;

    const participants: RandomTeamsRosterMember[] = [];
    const seenKeys = new Set<number>();
    const seenStudentNumbers = new Set<number>();

    for (const entry of value) {
        if (!entry || typeof entry !== "object") return null;
        const candidate = entry as Partial<RandomTeamsRosterMember>;
        if (
            !isPositiveSafeInteger(candidate.id) ||
            candidate.id > memberCount ||
            typeof candidate.name !== "string" ||
            candidate.name.trim().length === 0 ||
            (candidate.studentNumber !== null &&
                (!isPositiveSafeInteger(candidate.studentNumber) || candidate.studentNumber > 10 ** MAX_DIGITS - 1)) ||
            seenKeys.has(candidate.id)
        ) {
            return null;
        }

        if (candidate.studentNumber !== null) {
            if (seenStudentNumbers.has(candidate.studentNumber)) return null;
            seenStudentNumbers.add(candidate.studentNumber);
        }
        seenKeys.add(candidate.id);
        participants.push({
            id: candidate.id,
            name: candidate.name.trim(),
            studentNumber: candidate.studentNumber ?? null
        });
    }

    return participants;
};

const normalizeStoredRandomTeamsResult = (value: unknown): RandomTeamsResult | null => {
    if (!value || typeof value !== "object") {
        return null;
    }

    const candidate = value as Partial<RandomTeamsResult>;
    const mode = isRandomTeamsMode(candidate.mode) ? candidate.mode : null;
    const teamCount = isPositiveSafeInteger(candidate.teamCount) ? candidate.teamCount : null;
    const memberCount = isPositiveSafeInteger(candidate.memberCount) ? candidate.memberCount : null;
    const excludedIds = normalizePositiveIntegerArray(candidate.excludedIds);
    const teams = normalizePositiveIntegerMatrix(candidate.teams);
    const orderSlotCounts = normalizeNonNegativeIntegerArray(candidate.orderSlotCounts);
    const floorIds = normalizePositiveIntegerArray(candidate.floorIds);
    const configSignature = typeof candidate.configSignature === "string" ? candidate.configSignature : null;
    const participants =
        candidate.participants === undefined ? undefined : normalizeStoredParticipants(candidate.participants, memberCount ?? 0);

    if (
        mode === null ||
        teamCount === null ||
        memberCount === null ||
        excludedIds === null ||
        teams === null ||
        orderSlotCounts === null ||
        floorIds === null ||
        (candidate.participants !== undefined && participants === null) ||
        configSignature === null ||
        teamCount !== teams.length ||
        orderSlotCounts.length !== teams.length ||
        hasDuplicateMemberIds([...teams.flat(), ...floorIds])
    ) {
        return null;
    }

    return {
        configSignature,
        mode,
        teamCount,
        memberCount,
        excludedIds,
        teams,
        orderSlotCounts,
        floorIds,
        ...(participants ? { participants } : {})
    };
};

const isSameLocalDate = (leftMs: number, rightMs: number): boolean => {
    const left = new Date(leftMs);
    const right = new Date(rightMs);

    return (
        left.getFullYear() === right.getFullYear() &&
        left.getMonth() === right.getMonth() &&
        left.getDate() === right.getDate()
    );
};

const isFreshRandomTeamsResultTimestamp = (createdAt: unknown, now = Date.now()): createdAt is number =>
    typeof createdAt === "number" &&
    Number.isFinite(createdAt) &&
    createdAt > 0 &&
    createdAt <= now &&
    now - createdAt <= RANDOM_TEAMS_RESULT_RESTORE_TTL_MS &&
    isSameLocalDate(createdAt, now);

const getDefaultSettings = (mode: RandomTeamsMode = "teams"): StoredRandomTeamsSettings => ({
    version: 1,
    mode,
    groupName: "",
    teamCount: DEFAULT_CONFIG_BY_MODE[mode].teamCount,
    memberCount: DEFAULT_CONFIG_BY_MODE[mode].memberCount,
    rosterMode: "ids"
});

const readStoredRandomTeamsSettings = (): StoredRandomTeamsSettings => {
    const fallback = getDefaultSettings();

    try {
        const rawSettings = localStorage.getItem(RANDOM_TEAMS_SETTINGS_STORAGE_KEY);

        if (!rawSettings) {
            return fallback;
        }

        const parsedSettings = JSON.parse(rawSettings) as Partial<StoredRandomTeamsSettings>;
        const mode = isRandomTeamsMode(parsedSettings.mode) ? parsedSettings.mode : fallback.mode;
        const modeDefaults = getDefaultSettings(mode);

        return {
            version: 1,
            mode,
            groupName: normalizeRandomTeamsName(parsedSettings.groupName),
            teamCount: normalizeStoredCount(parsedSettings.teamCount, modeDefaults.teamCount),
            memberCount: normalizeStoredCount(parsedSettings.memberCount, modeDefaults.memberCount),
            rosterMode: isRandomTeamsRosterMode(parsedSettings.rosterMode) ? parsedSettings.rosterMode : "ids"
        };
    } catch {
        return fallback;
    }
};

const clearStoredRandomTeamsResultSnapshot = () => {
    try {
        localStorage.removeItem(RANDOM_TEAMS_RESULT_STORAGE_KEY);
    } catch {
        // Ignore storage failures; the active page state still works in memory.
    }
};

const readStoredRandomTeamsResultSnapshot = (): StoredRandomTeamsResultSnapshot | null => {
    try {
        const rawSnapshot = localStorage.getItem(RANDOM_TEAMS_RESULT_STORAGE_KEY);

        if (!rawSnapshot) {
            return null;
        }

        const parsedSnapshot = JSON.parse(rawSnapshot) as Partial<StoredRandomTeamsResultSnapshot>;
        const now = Date.now();

        if (parsedSnapshot.version !== 1 || !isFreshRandomTeamsResultTimestamp(parsedSnapshot.createdAt, now)) {
            clearStoredRandomTeamsResultSnapshot();
            return null;
        }

        const result = normalizeStoredRandomTeamsResult(parsedSnapshot.result);

        if (!result) {
            clearStoredRandomTeamsResultSnapshot();
            return null;
        }

        const mode = isRandomTeamsMode(parsedSnapshot.mode) ? parsedSnapshot.mode : result.mode;
        const fallbackTeamCount = String(result.teamCount);
        const fallbackMemberCount = String(result.memberCount);

        return {
            version: 1,
            createdAt: parsedSnapshot.createdAt,
            updatedAt: typeof parsedSnapshot.updatedAt === "number" ? parsedSnapshot.updatedAt : parsedSnapshot.createdAt,
            mode,
            groupName: typeof parsedSnapshot.groupName === "string" ? normalizeRandomTeamsName(parsedSnapshot.groupName) : undefined,
            teamCount: normalizeStoredCount(parsedSnapshot.teamCount, fallbackTeamCount),
            memberCount: normalizeStoredCount(parsedSnapshot.memberCount, fallbackMemberCount),
            excludedIds: normalizeStoredExcludedInputValues(parsedSnapshot.excludedIds),
            result,
            lastGeneratedSignature:
                typeof parsedSnapshot.lastGeneratedSignature === "string" ? parsedSnapshot.lastGeneratedSignature : null,
            showExcludedOnFloor: parsedSnapshot.showExcludedOnFloor === true,
            ...(typeof parsedSnapshot.rosterText === "string" ? { rosterText: parsedSnapshot.rosterText } : {})
        };
    } catch {
        clearStoredRandomTeamsResultSnapshot();
        return null;
    }
};

const writeStoredRandomTeamsResultSnapshot = (
    state: Pick<
        RandomTeamsToolState,
        "mode" | "groupName" | "teamCount" | "memberCount" | "excludedIds" | "result" | "lastGeneratedSignature" | "resultCreatedAt" | "rosterText"
    >,
    showExcludedOnFloor: boolean
) => {
    try {
        if (!state.result) {
            localStorage.removeItem(RANDOM_TEAMS_RESULT_STORAGE_KEY);
            return;
        }

        const now = Date.now();

        if (!isFreshRandomTeamsResultTimestamp(state.resultCreatedAt, now)) {
            localStorage.removeItem(RANDOM_TEAMS_RESULT_STORAGE_KEY);
            return;
        }

        const snapshot: StoredRandomTeamsResultSnapshot = {
            version: 1,
            createdAt: state.resultCreatedAt,
            updatedAt: now,
            mode: state.mode,
            groupName: normalizeRandomTeamsName(state.groupName),
            teamCount: normalizeStoredCount(state.teamCount, String(state.result.teamCount)),
            memberCount: normalizeStoredCount(state.memberCount, String(state.result.memberCount)),
            excludedIds: normalizeStoredExcludedInputValues(state.excludedIds),
            result: cloneRandomTeamsResult(state.result),
            lastGeneratedSignature: state.lastGeneratedSignature,
            showExcludedOnFloor,
            ...(state.result.participants && state.rosterText ? { rosterText: state.rosterText } : {})
        };

        localStorage.setItem(RANDOM_TEAMS_RESULT_STORAGE_KEY, JSON.stringify(snapshot));
    } catch {
        // Ignore storage failures; the tool still keeps the live result in memory.
    }
};

const writeStoredRandomTeamsSettings = (
    settings: Pick<RandomTeamsToolState, "mode" | "groupName" | "teamCount" | "memberCount" | "rosterMode">
) => {
    try {
        const previousSettings = readStoredRandomTeamsSettings();
        const mode = settings.mode;
        const modeDefaults = getDefaultSettings(mode);
        const fallbackSettings = previousSettings.mode === mode ? previousSettings : modeDefaults;
        const nextSettings: StoredRandomTeamsSettings = {
            version: 1,
            mode,
            groupName: normalizeRandomTeamsName(settings.groupName),
            teamCount: normalizeStoredCount(settings.teamCount, fallbackSettings.teamCount),
            memberCount: normalizeStoredCount(settings.memberCount, fallbackSettings.memberCount),
            rosterMode: settings.rosterMode === "names" ? "names" : "ids"
        };

        localStorage.setItem(RANDOM_TEAMS_SETTINGS_STORAGE_KEY, JSON.stringify(nextSettings));
    } catch {
        // Ignore storage failures; the tool still keeps the active setup in memory.
    }
};

const getMemberHue = (memberId: number): number => {
    const palette = [18, 36, 54, 92, 142, 176, 204, 228, 276, 324];
    const base = palette[(memberId - 1) % palette.length];
    const offset = Math.floor((memberId - 1) / palette.length) * 11;
    return (base + offset) % 360;
};

const getAverageHue = (memberIds: number[]): number | null => {
    if (memberIds.length === 0) {
        return null;
    }

    let x = 0;
    let y = 0;

    memberIds.forEach((memberId) => {
        const radians = (getMemberHue(memberId) * Math.PI) / 180;
        x += Math.cos(radians);
        y += Math.sin(radians);
    });

    if (Math.abs(x) < 0.0001 && Math.abs(y) < 0.0001) {
        return getMemberHue(memberIds[0]);
    }

    return (Math.atan2(y, x) * 180) / Math.PI >= 0
        ? (Math.atan2(y, x) * 180) / Math.PI
        : ((Math.atan2(y, x) * 180) / Math.PI) + 360;
};

const getMemberStyle = (memberId: number): string => {
    const hue = getMemberHue(memberId);
    return [
        `--member-hue:${hue}`,
        `--playground-avatar-hue:${hue}`,
        `--member-color-start:hsl(${hue} 92% 78%)`,
        `--member-color-end:hsl(${(hue + 22) % 360} 74% 58%)`,
        `--member-shadow:hsla(${hue} 78% 52% / 0.36)`,
        `--member-ring:hsla(${hue} 100% 92% / 0.84)`
    ].join(";") + ";";
};

const renderRandomTeamsCapyMarkup = (className: string): string => `
    <span class="lobby-playground-square capy-shield ${className}" aria-hidden="true">
        <span class="lobby-playground-capy-ears capy-shield-ears"></span>
        <span class="lobby-playground-capy-shield capy-shield-face"></span>
        <span class="lobby-playground-capy-muzzle capy-shield-muzzle"></span>
    </span>
`;

let nameMeasureContext: CanvasRenderingContext2D | null | undefined;
const renderRandomTeamsMemberLabel = (memberId: number, participant?: RandomTeamsRosterMember): string => {
    if (participant) {
        nameMeasureContext ??= document.createElement("canvas").getContext("2d");
        const family = getComputedStyle(document.body).fontFamily;
        if (nameMeasureContext) nameMeasureContext.font = `800 100px ${family}`;
        const layout = fitRandomTeamsName(participant.name, text => nameMeasureContext?.measureText(text).width ?? text.length * 60,
            SITE_TUNING.randomTeams.layout.namedMemberFontSizeCqw);
        return `
            ${participant.studentNumber === null ? "" : `<span class="member-token-id-badge">${participant.studentNumber}</span>`}
            <span class="member-token-label is-named-member-label" style="font-family:${escapeHtml(family)}">
                <span class="member-token-name" style="font-size:${layout.sizeCqw.toFixed(3)}cqw">${layout.lines.map(line => `<span class="member-token-name-line">${escapeHtml(line)}</span>`).join("")}</span>
            </span>
        `;
    }

    const memberText = String(memberId);
    const digitCount = Math.min(memberText.length, MAX_DIGITS);

    return `<span class="member-token-label" data-member-digits="${digitCount}">${escapeHtml(memberText)}</span>`;
};

const TEAM_ACCENTS = [
    {
        solid: "#6fd6ff",
        paint: "rgba(111, 214, 255, 0.82)",
        soft: "rgba(111, 214, 255, 0.18)",
        glow: "rgba(111, 214, 255, 0.28)",
        edge: "rgba(111, 214, 255, 0.34)",
        panelTop: "rgba(25, 79, 111, 0.92)",
        panelBottom: "rgba(8, 27, 43, 0.98)"
    },
    {
        solid: "#71ffc6",
        paint: "rgba(113, 255, 198, 0.8)",
        soft: "rgba(113, 255, 198, 0.18)",
        glow: "rgba(113, 255, 198, 0.28)",
        edge: "rgba(113, 255, 198, 0.34)",
        panelTop: "rgba(24, 87, 74, 0.92)",
        panelBottom: "rgba(8, 30, 29, 0.98)"
    },
    {
        solid: "#ffe07a",
        paint: "rgba(255, 224, 122, 0.82)",
        soft: "rgba(255, 224, 122, 0.18)",
        glow: "rgba(255, 224, 122, 0.28)",
        edge: "rgba(255, 224, 122, 0.34)",
        panelTop: "rgba(97, 76, 26, 0.92)",
        panelBottom: "rgba(33, 25, 11, 0.98)"
    },
    {
        solid: "#ff9fd8",
        paint: "rgba(255, 159, 216, 0.82)",
        soft: "rgba(255, 159, 216, 0.18)",
        glow: "rgba(255, 159, 216, 0.28)",
        edge: "rgba(255, 159, 216, 0.34)",
        panelTop: "rgba(98, 39, 82, 0.92)",
        panelBottom: "rgba(30, 12, 29, 0.98)"
    },
    {
        solid: "#b490ff",
        paint: "rgba(180, 144, 255, 0.82)",
        soft: "rgba(180, 144, 255, 0.18)",
        glow: "rgba(180, 144, 255, 0.28)",
        edge: "rgba(180, 144, 255, 0.34)",
        panelTop: "rgba(64, 42, 104, 0.92)",
        panelBottom: "rgba(22, 14, 35, 0.98)"
    },
    {
        solid: "#8aff7d",
        paint: "rgba(138, 255, 125, 0.82)",
        soft: "rgba(138, 255, 125, 0.18)",
        glow: "rgba(138, 255, 125, 0.28)",
        edge: "rgba(138, 255, 125, 0.34)",
        panelTop: "rgba(34, 89, 31, 0.92)",
        panelBottom: "rgba(10, 31, 16, 0.98)"
    }
] as const;

const TEAM_ACCENT_HUES = [196, 154, 46, 322, 260, 114];

const blendHue = (fromHue: number, toHue: number, amount: number): number => {
    const delta = ((toHue - fromHue + 540) % 360) - 180;
    return (fromHue + delta * amount + 360) % 360;
};

const parseCssPixelValue = (value: string): number => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const getTeamAccentStyle = (teamIndex: number, memberIds: number[] = []): string => {
    const averageHue = getAverageHue(memberIds);
    const baseHue = TEAM_ACCENT_HUES[teamIndex % TEAM_ACCENT_HUES.length];
    const accent =
        averageHue === null
            ? TEAM_ACCENTS[teamIndex % TEAM_ACCENTS.length]
            : {
                  solid: `hsl(${blendHue(baseHue, averageHue, 0.46)} 82% 76%)`,
                  paint: `hsla(${averageHue} 88% 68% / 0.76)`,
                  soft: `hsla(${blendHue(baseHue, averageHue, 0.38)} 74% 62% / 0.14)`,
                  glow: `hsla(${blendHue(baseHue, averageHue, 0.4)} 78% 64% / 0.2)`,
                  edge: `hsla(${blendHue(baseHue, averageHue, 0.44)} 82% 74% / 0.3)`,
                  panelTop: `hsla(${blendHue(baseHue, averageHue, 0.34)} 40% 24% / 0.94)`,
                  panelBottom: `hsla(${blendHue((baseHue + 16) % 360, averageHue, 0.28)} 34% 13% / 0.98)`
              };

    return [
        `--team-accent-solid:${accent.solid}`,
        `--team-accent-paint:${accent.paint}`,
        `--team-accent-soft:${accent.soft}`,
        `--team-accent-glow:${accent.glow}`,
        `--team-accent-edge:${accent.edge}`,
        `--team-accent-panel-top:${accent.panelTop}`,
        `--team-accent-panel-bottom:${accent.panelBottom}`
    ].join(";") + ";";
};

const buildResult = (
    mode: RandomTeamsMode,
    teamCount: number,
    memberCount: number,
    excludedIds: number[],
    signature: string,
    participants: RandomTeamsRosterMember[] | null = null
): RandomTeamsResult => {
    const excludedIdSet = new Set(excludedIds);
    const rosterIds = participants?.map((participant) => participant.id) ?? Array.from({ length: memberCount }, (_, index) => index + 1);
    const availableIds = rosterIds.filter((memberId) => !excludedIdSet.has(memberId));
    const shuffledIds = shuffle(availableIds);
    const teams = Array.from({ length: teamCount }, () => [] as number[]);
    const teamSizes = buildRandomizedTeamSizes(teamCount, shuffledIds.length);
    let nextMemberOffset = 0;

    teamSizes.forEach((teamSize, teamIndex) => {
        teams[teamIndex] = shuffledIds.slice(nextMemberOffset, nextMemberOffset + teamSize);
        nextMemberOffset += teamSize;
    });

    return {
        configSignature: signature,
        mode,
        teamCount,
        memberCount,
        excludedIds: [...excludedIds],
        teams,
        orderSlotCounts: teams.map((team) => team.length),
        floorIds: [],
        ...(participants ? { participants: participants.map((participant) => ({ ...participant })) } : {})
    };
};

export const createInitialRandomTeamsToolState = (): RandomTeamsToolState => {
    const storedSettings = readStoredRandomTeamsSettings();
    const storedResultSnapshot = readStoredRandomTeamsResultSnapshot();

    if (storedResultSnapshot) {
        return {
            mode: storedResultSnapshot.mode,
            rosterMode: storedResultSnapshot.result.participants?.length ? "names" : storedSettings.rosterMode,
            groupName: storedResultSnapshot.groupName ?? storedSettings.groupName,
            teamCount: storedResultSnapshot.teamCount,
            memberCount: storedResultSnapshot.memberCount,
            excludedIds: storedResultSnapshot.excludedIds,
            result: storedResultSnapshot.result,
            rosterText: storedResultSnapshot.rosterText ?? formatRosterText(storedResultSnapshot.result.participants),
            lastGeneratedSignature: storedResultSnapshot.lastGeneratedSignature,
            resultCreatedAt: storedResultSnapshot.createdAt,
            showExcludedOnFloor: storedResultSnapshot.showExcludedOnFloor
        };
    }

    return {
        mode: storedSettings.mode,
        rosterMode: storedSettings.rosterMode,
        groupName: storedSettings.groupName,
        teamCount: storedSettings.teamCount,
        memberCount: storedSettings.memberCount,
        rosterText: "",
        excludedIds: [""],
        result: null,
        lastGeneratedSignature: null,
        resultCreatedAt: null,
        showExcludedOnFloor: false
    };
};

class RandomTeamsTool {
    private readonly refs: RandomTeamsToolRefs = {
        configCard: null,
        modeToggle: null,
        rosterModeToggle: null,
        namesModeLabel: null,
        namesPanel: null,
        namesTextarea: null,
        namesFileInput: null,
        namesImportButton: null,
        namesStatus: null,
        namesPreview: null,
        namesEditButton: null,
        countSuffix: null,
        groupNameInput: null,
        memberPrefix: null,
        memberLabel: null,
        teamCountInput: null,
        memberCountInput: null,
        todayCount: null,
        stageAttendance: null,
        exclusionList: null,
        helper: null,
        includedChip: null,
        excludedChip: null,
        mobileExclusionCommitButton: null,
        startButton: null,
        stageCard: null,
        stageLead: null,
        stageStatus: null,
        board: null,
        stage: null,
        stageCanvas: null,
        dragLayer: null,
        fullscreenButton: null,
        soundButton: null,
        animationButton: null,
        excludedFloorButton: null,
        wipeButton: null,
        lateAddToggleButton: null,
        lateAddPanel: null,
        lateAddInput: null,
        lateAddSubmitButton: null,
        lateAddHelper: null,
        lateRemoveToggleButton: null,
        lateRemovePanel: null,
        lateRemoveInput: null,
        lateRemoveSubmitButton: null,
        lateRemoveHelper: null
    };

    private readonly activeAnimations = new Set<Animation>();
    private readonly activeTimeouts = new Set<number>();
    private readonly animatingMemberIds = new Set<number>();
    private readonly landedTeamMembers = new Map<number, number[]>();
    private readonly audio = new RandomTeamsAudio();
    private readonly stageCanvasRenderer: RandomTeamsCanvasRenderer | null;
    private readonly reducedMotionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)") ?? null;
    private stageVisibilityObserver: IntersectionObserver | null = null;
    private stageIsNearViewport = true;
    private duplicateExclusionFlashTimeout: number | null = null;
    private duplicateFeedbackFlashElement: HTMLElement | null = null;
    private dragPerf: RandomTeamsDragPerfSession | null = null;
    private canvasPerfWindow: RandomTeamsCanvasPerfWindow | null = null;
    private presentationScaleCache: RandomTeamsPresentationScaleCache | null = null;
    private lastSlowCanvasPerfLoggedAt = 0;
    private controlTooltip: HTMLDivElement | null = null;
    private controlTooltipButton: HTMLButtonElement | null = null;

    private drag: DragContext | null = null;
    private dragFrameId: number | null = null;
    private resizeFrameId: number | null = null;
    private orderPresentationRefreshFrameId: number | null = null;
    private presentationSettleFrameIds: number[] = [];
    private lastOrderPresentationLayoutLogSignature: string | null = null;
    private orderLayoutRefreshPending = false;
    private launchInProgress = false;
    private launchFrameIds: number[] = [];
    private highlightedTeamIndex: number | null = null;
    private highlightedTeamElement: HTMLElement | null = null;
    private pendingExclusionFocusIndex: number | null = null;
    private focusedExclusionIndex: number | null = null;
    private presentationOpen = false;
    private soundEnabled = readStoredSoundEnabled();
    private animationsPreferred = readStoredAnimationsEnabled();
    private animationAutoSuppressed = false;
    private largeAnimationAutoOffApplied = false;
    private showExcludedOnFloor = false;
    private lateAddPanelOpen = false;
    private lateAddDraft = "";
    private lateAddErrorKey: TranslationKey | null = null;
    private lateRemovePanelOpen = false;
    private lateRemoveDraft = "";
    private lateRemoveErrorKey: TranslationKey | null = null;
    private namesImportErrorKey: TranslationKey | null = null;
    private namesEditing = false;
    private lastPreviewSnapshot: PreviewSnapshot | null = null;
    private documentScrollLockSnapshot: {
        documentOverflow: string;
        documentOverscrollBehavior: string;
        bodyOverflow: string;
        bodyOverscrollBehavior: string;
    } | null = null;

    public constructor(
        private readonly root: HTMLElement,
        private readonly state: RandomTeamsToolState
    ) {
        this.state.groupName = normalizeRandomTeamsName(this.state.groupName);
        this.state.rosterText = typeof this.state.rosterText === "string" ? this.state.rosterText : "";
        this.state.rosterMode = this.state.rosterMode === "names" ? "names" : "ids";
        this.state.resultCreatedAt = typeof this.state.resultCreatedAt === "number" ? this.state.resultCreatedAt : null;
        this.state.showExcludedOnFloor = this.state.showExcludedOnFloor === true;
        this.showExcludedOnFloor = this.state.showExcludedOnFloor;
        this.state.excludedIds = ensureTrailingExclusionInput(this.state.excludedIds);
        this.renderShell();
        // Phase 1 keeps canvas decorative only so the DOM remains the source of
        // truth for fullscreen sizing, order rows, and drag/drop hit targets.
        this.stageCanvasRenderer = this.refs.stageCanvas
            ? new RandomTeamsCanvasRenderer(this.refs.stageCanvas, {
                  onPerfSample: isRandomTeamsPerfEnabled() ? (sample) => this.handleCanvasPerfSample(sample) : null
              })
            : null;
        if (this.refs.stage && typeof IntersectionObserver === "function") {
            this.stageVisibilityObserver = new IntersectionObserver(this.handleStageVisibilityChange, {
                rootMargin: "160px"
            });
            this.stageVisibilityObserver.observe(this.refs.stage);
        }
        this.bindEvents();
        document.addEventListener("keydown", this.handleDocumentKeyDown);
        document.addEventListener("visibilitychange", this.handleDocumentVisibilityChange);
        this.reducedMotionQuery?.addEventListener("change", this.handleReducedMotionChange);
        window.addEventListener("resize", this.handleWindowResize);
        window.visualViewport?.addEventListener("resize", this.handleVisualViewportChange);
        window.visualViewport?.addEventListener("scroll", this.handleVisualViewportChange);
        this.syncMobileExclusionSubmitViewportOffset();
        this.sync();
    }

    public destroy() {
        this.flushCanvasPerfWindow("destroy", true);
        this.releaseDocumentScrollLock();
        this.clearTransientState();
        this.detachDragListeners();
        this.clearDuplicateExclusionFlash();
        this.audio.dispose();
        this.stageCanvasRenderer?.destroy();
        this.stageVisibilityObserver?.disconnect();
        this.stageVisibilityObserver = null;
        if (this.dragFrameId !== null) {
            window.cancelAnimationFrame(this.dragFrameId);
            this.dragFrameId = null;
        }
        if (this.resizeFrameId !== null) {
            window.cancelAnimationFrame(this.resizeFrameId);
            this.resizeFrameId = null;
        }
        if (this.orderPresentationRefreshFrameId !== null) {
            window.cancelAnimationFrame(this.orderPresentationRefreshFrameId);
            this.orderPresentationRefreshFrameId = null;
        }
        this.clearPresentationSettleFrames();
        document.removeEventListener("keydown", this.handleDocumentKeyDown);
        document.removeEventListener("visibilitychange", this.handleDocumentVisibilityChange);
        this.reducedMotionQuery?.removeEventListener("change", this.handleReducedMotionChange);
        window.removeEventListener("resize", this.handleWindowResize);
        window.visualViewport?.removeEventListener("resize", this.handleVisualViewportChange);
        window.visualViewport?.removeEventListener("scroll", this.handleVisualViewportChange);
        this.root.removeEventListener("input", this.handleInput);
        this.root.removeEventListener("change", this.handleChange);
        this.root.removeEventListener("focusin", this.handleFocusIn);
        this.root.removeEventListener("focusout", this.handleFocusOut);
        this.root.removeEventListener("click", this.handleClick);
        this.root.removeEventListener("pointerdown", this.handlePointerDown);
        this.root.removeEventListener("pointerover", this.handleControlTooltipPointer);
        this.root.removeEventListener("pointerout", this.handleControlTooltipPointer);
        this.root.removeEventListener("focusin", this.handleControlTooltipFocus);
        this.root.removeEventListener("focusout", this.handleControlTooltipFocus);
        document.removeEventListener("scroll", this.refreshControlTooltip, true);
        window.removeEventListener("resize", this.refreshControlTooltip);
        this.hideControlTooltip();
        this.controlTooltip?.remove();
        this.controlTooltip = null;
    }

    public refreshLanguage() {
        const activeElement = document.activeElement;
        const activeExclusionInput =
            activeElement instanceof HTMLInputElement &&
            activeElement.hasAttribute("data-random-teams-exclusion-input")
                ? activeElement
                : null;
        const activeExclusionIndex =
            activeExclusionInput !== null
                ? Number.parseInt(activeExclusionInput.dataset.exclusionIndex ?? "", 10)
                : Number.NaN;
        const selectionStart = activeExclusionInput?.selectionStart ?? null;
        const selectionEnd = activeExclusionInput?.selectionEnd ?? null;

        this.root.querySelector<HTMLElement>('[data-random-teams-mode="teams"]')!.textContent =
            t("randomTeamsModeTeams");
        this.root.querySelector<HTMLElement>('[data-random-teams-mode="orders"]')!.textContent =
            t("randomTeamsModeOrder");
        this.root.querySelector<HTMLElement>('[data-random-teams-roster-mode="ids"]')!.textContent =
            t("randomTeamsSourceIds");
        this.root.querySelector<HTMLElement>('[data-random-teams-roster-mode="names"]')!.textContent =
            t("randomTeamsSourceNames");
        const createVerb = this.root.querySelector<HTMLElement>("#random-teams-create-verb");
        const exclusionLabel = this.root.querySelector<HTMLElement>(
            ".random-teams-exclusion-field .landing-field-label"
        );

        if (createVerb) {
            createVerb.textContent = t("createVerb");
        }

        if (exclusionLabel) {
            exclusionLabel.textContent = t("randomTeamsMissingIds");
        }

        if (this.refs.mobileExclusionCommitButton) {
            this.refs.mobileExclusionCommitButton.textContent = t("submitStudentId");
        }

        this.state.excludedIds = ensureTrailingExclusionInput(this.state.excludedIds);
        this.syncForm();

        if (!this.launchInProgress) {
            this.syncResults();
        } else {
            this.syncFullscreenButton();
        }

        if (!Number.isNaN(activeExclusionIndex)) {
            const nextInput = this.refs.exclusionList?.querySelector<HTMLInputElement>(
                `[data-random-teams-exclusion-input][data-exclusion-index="${activeExclusionIndex}"]`
            );

            nextInput?.focus({ preventScroll: true });

            if (selectionStart !== null && selectionEnd !== null) {
                nextInput?.setSelectionRange(selectionStart, selectionEnd);
            }
        }
    }

    private handleCanvasPerfSample(sample: RandomTeamsCanvasFramePerfSample) {
        if (!isRandomTeamsPerfEnabled()) {
            return;
        }

        if (sample.reason !== "tick") {
            this.flushCanvasPerfWindow("state-change", true);
            const phases = formatRandomTeamsPerfPhases(sample.phases);
            logRandomTeamsPerf(
                `canvas reason=${sample.reason} total=${roundRandomTeamsPerfMs(sample.totalMs)}ms size=${sample.canvasCssWidth}x${
                    sample.canvasCssHeight
                }@${roundRandomTeamsPerfMs(sample.devicePixelRatio)}x glows=${sample.connectorGlowCount} bursts=${
                    sample.impactBurstCount
                }${phases ? ` phases=${phases}` : ""}`
            );
            return;
        }

        if (!this.canvasPerfWindow) {
            this.canvasPerfWindow = {
                startedAt: sample.recordedAt,
                frameCount: 0,
                totalMs: 0,
                maxMs: 0,
                slowFrameCount: 0,
                phaseTotals: {},
                maxConnectorGlowCount: 0,
                maxImpactBurstCount: 0,
                canvasCssWidth: sample.canvasCssWidth,
                canvasCssHeight: sample.canvasCssHeight,
                devicePixelRatio: sample.devicePixelRatio
            };
        }

        this.canvasPerfWindow.frameCount += 1;
        this.canvasPerfWindow.totalMs += sample.totalMs;
        this.canvasPerfWindow.maxMs = Math.max(this.canvasPerfWindow.maxMs, sample.totalMs);
        this.canvasPerfWindow.maxConnectorGlowCount = Math.max(
            this.canvasPerfWindow.maxConnectorGlowCount,
            sample.connectorGlowCount
        );
        this.canvasPerfWindow.maxImpactBurstCount = Math.max(
            this.canvasPerfWindow.maxImpactBurstCount,
            sample.impactBurstCount
        );
        this.canvasPerfWindow.canvasCssWidth = sample.canvasCssWidth;
        this.canvasPerfWindow.canvasCssHeight = sample.canvasCssHeight;
        this.canvasPerfWindow.devicePixelRatio = sample.devicePixelRatio;

        Object.entries(sample.phases).forEach(([phase, value]) => {
            this.canvasPerfWindow!.phaseTotals[phase] = (this.canvasPerfWindow!.phaseTotals[phase] ?? 0) + value;
        });

        if (sample.totalMs >= RANDOM_TEAMS_SLOW_FRAME_MS) {
            this.canvasPerfWindow.slowFrameCount += 1;

            if (sample.recordedAt - this.lastSlowCanvasPerfLoggedAt >= RANDOM_TEAMS_CANVAS_SLOW_LOG_COOLDOWN_MS) {
                this.lastSlowCanvasPerfLoggedAt = sample.recordedAt;
                const phases = formatRandomTeamsPerfPhases(sample.phases);
                logRandomTeamsPerf(
                    `canvas-slow total=${roundRandomTeamsPerfMs(sample.totalMs)}ms size=${sample.canvasCssWidth}x${
                        sample.canvasCssHeight
                    }@${roundRandomTeamsPerfMs(sample.devicePixelRatio)}x glows=${sample.connectorGlowCount} bursts=${
                        sample.impactBurstCount
                    }${phases ? ` phases=${phases}` : ""}`
                );
            }
        }

        if (sample.recordedAt - this.canvasPerfWindow.startedAt >= RANDOM_TEAMS_CANVAS_WINDOW_MS) {
            this.flushCanvasPerfWindow("window");
        }
    }

    private flushCanvasPerfWindow(reason: string, _force = false) {
        if (!isRandomTeamsPerfEnabled() || !this.canvasPerfWindow) {
            this.canvasPerfWindow = null;
            return;
        }

        if (this.canvasPerfWindow.frameCount <= 0) {
            return;
        }

        const { frameCount, totalMs, maxMs, slowFrameCount, phaseTotals } = this.canvasPerfWindow;
        const averageMs = frameCount > 0 ? totalMs / frameCount : 0;
        const averagePhases: Record<string, number> = {};
        if (frameCount > 0) {
            for (const [phase, value] of Object.entries(phaseTotals)) {
                averagePhases[phase] = value / frameCount;
            }
        }
        const phases = formatRandomTeamsPerfPhases(averagePhases);

        logRandomTeamsPerf(
            `canvas-${reason} frames=${frameCount} avg=${roundRandomTeamsPerfMs(averageMs)}ms max=${roundRandomTeamsPerfMs(
                maxMs
            )}ms slow${RANDOM_TEAMS_SLOW_FRAME_MS}=${slowFrameCount} size=${this.canvasPerfWindow.canvasCssWidth}x${
                this.canvasPerfWindow.canvasCssHeight
            }@${roundRandomTeamsPerfMs(this.canvasPerfWindow.devicePixelRatio)}x glows=${
                this.canvasPerfWindow.maxConnectorGlowCount
            } bursts=${this.canvasPerfWindow.maxImpactBurstCount}${phases ? ` phases=${phases}` : ""}`
        );

        this.canvasPerfWindow = null;
    }

    private renderShell() {
        const hasGeneratedResult = Boolean(this.state.result);

        this.root.innerHTML = `
            <section
                class="tool-page"
                style="--random-teams-face-stretch:${SITE_TUNING.randomTeams.layout.namedFacialFeatureStretch};--random-teams-named-preview-width:${SITE_TUNING.randomTeams.layout.namedPreviewTokenWidthPx}px;--random-teams-named-token-width:${SITE_TUNING.randomTeams.layout.namedMemberTokenWidthPx}px;--random-teams-named-token-height:${SITE_TUNING.randomTeams.layout.namedMemberTokenHeightPx}px;--random-teams-named-token-width-mobile:${SITE_TUNING.randomTeams.layout.namedMemberTokenWidthMobilePx}px;--random-teams-named-token-height-mobile:${SITE_TUNING.randomTeams.layout.namedMemberTokenHeightMobilePx}px"
            >
                <button
                    class="random-teams-presentation-scrim"
                    type="button"
                    data-random-teams-presentation-close
                    aria-label="${escapeHtml(t("exitFullscreen"))}"
                ></button>
                <div class="random-teams-layout">
                    <article class="card random-teams-config-card${hasGeneratedResult ? "" : " is-active-section"}">
                        <div class="random-teams-mode-row">
                            <button id="random-teams-mode-toggle" class="random-teams-mode-toggle" type="button" role="switch" aria-checked="${this.state.mode === "orders"}" aria-label="${escapeHtml(t("randomTeamsModeTeams") + " / " + t("randomTeamsModeOrder"))}">
                                <span class="mode-chip-button ${this.state.mode === "teams" ? "selected" : ""}" data-random-teams-mode="teams" aria-hidden="true">${escapeHtml(t("randomTeamsModeTeams"))}</span>
                                <span class="mode-chip-button ${this.state.mode === "orders" ? "selected" : ""}" data-random-teams-mode="orders" aria-hidden="true">${escapeHtml(t("randomTeamsModeOrder"))}</span>
                            </button>
                            <button id="random-teams-roster-mode-toggle" class="random-teams-mode-toggle random-teams-roster-mode-toggle" type="button" role="switch" aria-checked="${this.state.rosterMode === "names"}" aria-label="${escapeHtml(t("randomTeamsSourceIds") + " / " + t("randomTeamsSourceNames"))}">
                                <span id="random-teams-ids-toggle" class="mode-chip-button ${this.state.rosterMode === "ids" ? "selected" : ""}" data-random-teams-roster-mode="ids" aria-hidden="true">${escapeHtml(t("randomTeamsSourceIds"))}</span>
                                <span id="random-teams-names-toggle" class="mode-chip-button ${this.state.rosterMode === "names" ? "selected" : ""}" data-random-teams-roster-mode="names" aria-hidden="true">${escapeHtml(t("randomTeamsSourceNames"))}</span>
                            </button>
                        </div>
                        <div class="random-teams-sentence random-teams-creation-sentence">
                            <span id="random-teams-create-verb">${escapeHtml(t("createVerb"))}</span>
                            <label class="random-teams-inline-field">
                                <input
                                    id="random-teams-count-input"
                                    type="text"
                                    inputmode="numeric"
                                    enterkeyhint="next"
                                    pattern="[0-9]*"
                                    autocomplete="off"
                                    aria-label="${escapeHtml(
                                        this.state.mode === "teams" ? t("randomTeamsModeTeams") : t("randomTeamsModeOrder")
                                    )}"
                                />
                            </label>
                            <span id="random-teams-count-suffix">
                                <span data-group-name-prefix></span>
                                <input
                                    id="random-teams-name-input"
                                    type="text"
                                    maxlength="${MAX_RANDOM_TEAMS_NAME_LENGTH}"
                                    autocomplete="off"
                                    enterkeyhint="next"
                                    aria-label="${escapeHtml(t("randomTeamsGroupName"))}"
                                />
                                <span data-group-name-ending></span>
                            </span>
                        </div>
                        <div class="random-teams-sentence random-teams-member-count-section">
                            <span id="random-teams-member-prefix">${escapeHtml(this.getMemberPrefix())}</span>
                            <label class="random-teams-inline-field">
                                <input
                                    id="random-teams-member-count-input"
                                    type="text"
                                    inputmode="numeric"
                                    enterkeyhint="done"
                                    pattern="[0-9]*"
                                    autocomplete="off"
                                    aria-label="${escapeHtml(t("randomTeamsStudentsTotal"))}"
                                />
                            </label>
                            <span id="random-teams-member-label">${escapeHtml(this.getMemberLabel())}</span>
                        </div>
                        <section id="random-teams-names-panel" class="random-teams-names-panel" aria-label="${escapeHtml(t("randomTeamsRosterInputLabel"))}" hidden>
                            <div class="random-teams-names-toolbar">
                                <button id="random-teams-names-edit" class="random-teams-names-edit" type="button" data-random-teams-names-edit></button>
                                <button id="random-teams-names-import" class="random-teams-names-import" type="button" data-random-teams-names-import title="${escapeHtml(t("randomTeamsRosterFileTypes"))}">
                                    ${renderZiarcadiaIcon("upload")}<span>${escapeHtml(t("randomTeamsImportNames"))}</span>
                                </button>
                            </div>
                            <label class="random-teams-names-field">
                                <span class="visually-hidden">${escapeHtml(t("randomTeamsRosterInputLabel"))}</span>
                                <textarea
                                    id="random-teams-names-textarea"
                                    rows="8"
                                    autocomplete="off"
                                    spellcheck="false"
                                    aria-label="${escapeHtml(t("randomTeamsRosterInputLabel"))}"
                                    aria-describedby="random-teams-names-status"
                                    placeholder="${escapeHtml(t("randomTeamsRosterSample"))}"
                                >${escapeHtml(this.state.rosterText ?? "")}</textarea>
                            </label>
                            <div id="random-teams-names-preview" class="random-teams-names-preview" role="list" aria-label="${escapeHtml(t("randomTeamsRosterPreview"))}"></div>
                                <input
                                    id="random-teams-names-file"
                                    class="random-teams-names-file"
                                    type="file"
                                    accept=".txt,.csv,.tsv,.xlsx,text/plain,text/csv"
                                    aria-label="${escapeHtml(t("randomTeamsImportNames"))}"
                                    hidden
                                />
                                <span id="random-teams-names-status" class="random-teams-names-status" role="status" aria-live="polite"></span>
                        </section>
                        <label class="field random-teams-exclusion-field">
                            <span class="landing-field-label">${escapeHtml(t("randomTeamsMissingIds"))}</span>
                            <div class="random-teams-exclusion-list" id="random-teams-exclusion-list"></div>
                        </label>
                        <div id="random-teams-today-count" class="random-teams-today-count" role="status" aria-live="polite" aria-atomic="true" hidden></div>
                        <p id="random-teams-helper" class="random-teams-helper"></p>
                        <button id="random-teams-start-button" class="primary-button random-teams-start-button" type="button">
                            ${escapeHtml(t("start"))}
                        </button>
                    </article>
                    <article class="card random-teams-stage-card${hasGeneratedResult ? " is-active-section" : ""}">
                        <div id="random-teams-stage" class="random-teams-stage">
                            <div class="random-teams-stage-controls">
                                <button
                                    id="random-teams-sound-button"
                                    class="random-teams-sound-button"
                                    type="button"
                                    data-random-teams-sound
                                >
                                    <svg class="random-teams-sound-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                        <path class="random-teams-sound-body" d="M4.5 9.25h3.15l4.6-3.55v12.6l-4.6-3.55H4.5z" />
                                        <path class="random-teams-sound-wave random-teams-sound-wave-small" d="M15 9.4c.78.72 1.18 1.58 1.18 2.6S15.78 13.88 15 14.6" />
                                        <path class="random-teams-sound-wave random-teams-sound-wave-large" d="M17.1 7.25c1.38 1.32 2.06 2.9 2.06 4.75s-.68 3.43-2.06 4.75" />
                                        <path class="random-teams-sound-mute" d="M15.2 8.8l4 6.4m0-6.4-4 6.4" />
                                    </svg>
                                </button>
                                <button
                                    id="random-teams-animation-button"
                                    class="random-teams-animation-button"
                                    type="button"
                                    data-random-teams-animations
                                >
                                    <svg class="random-teams-animation-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                        <path class="random-teams-animation-trail random-teams-animation-trail-top" d="M4 8.4h7.4" />
                                        <path class="random-teams-animation-trail random-teams-animation-trail-mid" d="M2.8 12h8.8" />
                                        <path class="random-teams-animation-trail random-teams-animation-trail-bottom" d="M5.2 15.6h6.2" />
                                        <circle class="random-teams-animation-orb" cx="16.8" cy="12" r="3.7" />
                                        <path class="random-teams-animation-mute" d="M5 5l14 14" />
                                    </svg>
                                </button>
                                <button
                                    id="random-teams-excluded-floor-button"
                                    class="random-teams-excluded-floor-button"
                                    type="button"
                                    data-random-teams-excluded-floor
                                >
                                    <svg class="random-teams-excluded-floor-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                        <rect class="random-teams-excluded-floor-card" x="6.25" y="4.75" width="9.5" height="9.5" rx="2.2" />
                                        <path class="random-teams-excluded-floor-base" d="M4 18.75h16" />
                                        <path class="random-teams-excluded-floor-plus" d="M16.8 8.3v5.7m-2.85-2.85h5.7" />
                                        <path class="random-teams-excluded-floor-slash" d="M5.2 5.2l13.6 13.6" />
                                    </svg>
                                </button>
                                <button
                                    id="random-teams-wipe-button"
                                    class="random-teams-wipe-button"
                                    type="button"
                                    data-random-teams-wipe
                                    aria-label="${escapeHtml(t("wipe"))}"
                                >
                                    ${RANDOM_TEAMS_WIPE_ICON}
                                </button>
                                <div class="random-teams-late-add-anchor">
                                    <button
                                        id="random-teams-late-add-toggle"
                                        class="random-teams-late-add-toggle"
                                        type="button"
                                        data-random-teams-late-add-toggle
                                        hidden
                                    >
                                        <svg class="random-teams-late-add-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                            <rect class="random-teams-late-add-token" x="4.9" y="5.7" width="10.6" height="10.6" rx="2.8" />
                                            <path class="random-teams-late-add-shine" d="M7.1 8.1c1.2-1.25 2.74-1.9 4.6-1.9" />
                                            <circle class="random-teams-late-add-badge" cx="16.95" cy="16.25" r="4.35" />
                                            <path class="random-teams-late-add-plus" d="M16.95 14.35v3.8m-1.9-1.9h3.8" />
                                        </svg>
                                    </button>
                                    <div id="random-teams-late-add-panel" class="random-teams-late-add-panel" hidden>
                                        <label class="random-teams-late-add-field">
                                            <input
                                                id="random-teams-late-add-input"
                                                type="text"
                                                inputmode="numeric"
                                                enterkeyhint="done"
                                                pattern="[0-9]*"
                                                autocomplete="off"
                                                placeholder="${escapeHtml(t("studentIdPlaceholder"))}"
                                                aria-label="${escapeHtml(t("addStudentIdPrompt"))}"
                                            />
                                        </label>
                                        <button
                                            id="random-teams-late-add-submit"
                                            class="random-teams-late-add-submit"
                                            type="button"
                                            data-random-teams-late-add-submit
                                            aria-label="${escapeHtml(t("addStudentId"))}"
                                            title="${escapeHtml(t("addStudentId"))}"
                                        >
                                            <svg class="random-teams-late-add-submit-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                                <path class="random-teams-late-add-submit-arrow" d="M6.2 12h11.6m-5.1-5.1 5.1 5.1-5.1 5.1" />
                                            </svg>
                                        </button>
                                        <p id="random-teams-late-add-helper" class="random-teams-late-add-helper" hidden></p>
                                    </div>
                                </div>
                                <div class="random-teams-late-add-anchor">
                                    <button
                                        id="random-teams-late-remove-toggle"
                                        class="random-teams-late-remove-toggle"
                                        type="button"
                                        data-random-teams-late-remove-toggle
                                        hidden
                                    >
                                        <svg class="random-teams-late-remove-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                            <rect class="random-teams-late-remove-token" x="4.9" y="5.7" width="10.6" height="10.6" rx="2.8" />
                                            <path class="random-teams-late-remove-shine" d="M7.1 8.1c1.2-1.25 2.74-1.9 4.6-1.9" />
                                            <circle class="random-teams-late-remove-badge" cx="16.95" cy="16.25" r="4.35" />
                                            <path class="random-teams-late-remove-minus" d="M15.05 16.25h3.8" />
                                        </svg>
                                    </button>
                                    <div id="random-teams-late-remove-panel" class="random-teams-late-add-panel" hidden>
                                        <label class="random-teams-late-add-field">
                                            <input
                                                id="random-teams-late-remove-input"
                                                type="text"
                                                inputmode="numeric"
                                                enterkeyhint="done"
                                                pattern="[0-9]*"
                                                autocomplete="off"
                                                placeholder="${escapeHtml(t("studentIdPlaceholder"))}"
                                                aria-label="${escapeHtml(t("removeStudentIdPrompt"))}"
                                            />
                                        </label>
                                        <button
                                            id="random-teams-late-remove-submit"
                                            class="random-teams-late-add-submit"
                                            type="button"
                                            data-random-teams-late-remove-submit
                                            aria-label="${escapeHtml(t("removeStudentId"))}"
                                            title="${escapeHtml(t("removeStudentId"))}"
                                        >
                                            <svg class="random-teams-late-add-submit-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                                <path class="random-teams-late-add-submit-arrow" d="M6.2 12h11.6m-5.1-5.1 5.1 5.1-5.1 5.1" />
                                            </svg>
                                        </button>
                                        <p id="random-teams-late-remove-helper" class="random-teams-late-add-helper" hidden></p>
                                    </div>
                                </div>
                                <button
                                    id="random-teams-fullscreen-button"
                                    class="random-teams-fullscreen-button"
                                    type="button"
                                    data-random-teams-fullscreen
                                    aria-label="${escapeHtml(t("fullscreen"))}"
                                >
                                    <span class="random-teams-fullscreen-icon" aria-hidden="true">
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </span>
                                </button>
                            </div>
                            <p
                                id="random-teams-stage-status"
                                class="visually-hidden"
                                role="status"
                                aria-live="polite"
                                aria-atomic="true"
                            ></p>
                            <div id="random-teams-stage-attendance" class="random-teams-stage-attendance" hidden></div>
                            <canvas class="random-teams-stage-canvas" aria-hidden="true"></canvas>
                            <div id="random-teams-board" class="random-teams-board"></div>
                            <div id="random-teams-drag-layer" class="random-teams-drag-layer"></div>
                        </div>
                    </article>
                </div>
                <button
                    class="random-teams-mobile-exclusion-submit"
                    type="button"
                    data-commit-active-exclusion
                    hidden
                >
                    ${escapeHtml(t("submitStudentId"))}
                </button>
            </section>
        `;

        this.refs.configCard = this.root.querySelector(".random-teams-config-card");
        this.refs.modeToggle = this.root.querySelector("#random-teams-mode-toggle");
        this.refs.rosterModeToggle = this.root.querySelector("#random-teams-roster-mode-toggle");
        this.refs.namesModeLabel = this.root.querySelector("#random-teams-names-toggle");
        this.refs.namesPanel = this.root.querySelector("#random-teams-names-panel");
        this.refs.namesTextarea = this.root.querySelector("#random-teams-names-textarea");
        this.refs.namesFileInput = this.root.querySelector("#random-teams-names-file");
        this.refs.namesImportButton = this.root.querySelector("#random-teams-names-import");
        this.refs.namesStatus = this.root.querySelector("#random-teams-names-status");
        this.refs.namesPreview = this.root.querySelector("#random-teams-names-preview");
        this.refs.namesEditButton = this.root.querySelector("#random-teams-names-edit");
        this.refs.countSuffix = this.root.querySelector("#random-teams-count-suffix");
        this.refs.groupNameInput = this.root.querySelector("#random-teams-name-input");
        this.refs.memberPrefix = this.root.querySelector("#random-teams-member-prefix");
        this.refs.memberLabel = this.root.querySelector("#random-teams-member-label");
        this.refs.teamCountInput = this.root.querySelector("#random-teams-count-input");
        this.refs.memberCountInput = this.root.querySelector("#random-teams-member-count-input");
        this.refs.todayCount = this.root.querySelector("#random-teams-today-count");
        this.refs.stageAttendance = this.root.querySelector("#random-teams-stage-attendance");
        this.refs.exclusionList = this.root.querySelector("#random-teams-exclusion-list");
        this.refs.helper = this.root.querySelector("#random-teams-helper");
        this.refs.includedChip = this.root.querySelector("#random-teams-included-chip");
        this.refs.excludedChip = this.root.querySelector("#random-teams-excluded-chip");
        this.refs.mobileExclusionCommitButton = this.root.querySelector("[data-commit-active-exclusion]");
        this.refs.startButton = this.root.querySelector("#random-teams-start-button");
        this.refs.stageCard = this.root.querySelector(".random-teams-stage-card");
        this.refs.stageLead = null;
        this.refs.stageStatus = this.root.querySelector("#random-teams-stage-status");
        this.refs.board = this.root.querySelector("#random-teams-board");
        this.refs.stage = this.root.querySelector("#random-teams-stage");
        this.refs.stageCanvas = this.root.querySelector(".random-teams-stage-canvas");
        this.refs.dragLayer = this.root.querySelector("#random-teams-drag-layer");
        this.refs.fullscreenButton = this.root.querySelector("#random-teams-fullscreen-button");
        this.refs.soundButton = this.root.querySelector("#random-teams-sound-button");
        this.refs.animationButton = this.root.querySelector("#random-teams-animation-button");
        this.refs.excludedFloorButton = this.root.querySelector("#random-teams-excluded-floor-button");
        this.refs.wipeButton = this.root.querySelector("#random-teams-wipe-button");
        this.refs.lateAddToggleButton = this.root.querySelector("#random-teams-late-add-toggle");
        this.refs.lateAddPanel = this.root.querySelector("#random-teams-late-add-panel");
        this.refs.lateAddInput = this.root.querySelector("#random-teams-late-add-input");
        this.refs.lateAddSubmitButton = this.root.querySelector("#random-teams-late-add-submit");
        this.refs.lateAddHelper = this.root.querySelector("#random-teams-late-add-helper");
        this.refs.lateRemoveToggleButton = this.root.querySelector("#random-teams-late-remove-toggle");
        this.refs.lateRemovePanel = this.root.querySelector("#random-teams-late-remove-panel");
        this.refs.lateRemoveInput = this.root.querySelector("#random-teams-late-remove-input");
        this.refs.lateRemoveSubmitButton = this.root.querySelector("#random-teams-late-remove-submit");
        this.refs.lateRemoveHelper = this.root.querySelector("#random-teams-late-remove-helper");
    }

    private bindEvents() {
        this.root.addEventListener("input", this.handleInput);
        this.root.addEventListener("change", this.handleChange);
        this.root.addEventListener("focusin", this.handleFocusIn);
        this.root.addEventListener("focusout", this.handleFocusOut);
        this.root.addEventListener("click", this.handleClick);
        this.root.addEventListener("pointerdown", this.handlePointerDown);
        this.root.addEventListener("pointerover", this.handleControlTooltipPointer);
        this.root.addEventListener("pointerout", this.handleControlTooltipPointer);
        this.root.addEventListener("focusin", this.handleControlTooltipFocus);
        this.root.addEventListener("focusout", this.handleControlTooltipFocus);
        document.addEventListener("scroll", this.refreshControlTooltip, { capture: true, passive: true });
        window.addEventListener("resize", this.refreshControlTooltip);
    }

    private setControlTooltip(button: HTMLButtonElement, label: string) {
        button.dataset.controlTooltip = label;
        button.removeAttribute("title");
        if (this.controlTooltipButton === button) this.refreshControlTooltip();
    }

    private readonly handleControlTooltipPointer = (event: PointerEvent) => {
        if (event.pointerType === "touch") return;
        const button = (event.target as Element | null)?.closest<HTMLButtonElement>("button[data-control-tooltip]");
        if (!button) return;
        if (event.relatedTarget instanceof Node && button.contains(event.relatedTarget)) return;
        if (event.type === "pointerover") {
            this.showControlTooltip(button);
        } else if (!button.matches(":focus-visible")) {
            this.hideControlTooltip();
        }
    };

    private readonly handleControlTooltipFocus = (event: FocusEvent) => {
        const button = (event.target as Element | null)?.closest<HTMLButtonElement>("button[data-control-tooltip]");
        if (!button) return;
        if (event.type === "focusin" && button.matches(":focus-visible")) {
            this.showControlTooltip(button);
        } else if (event.type === "focusout") {
            this.hideControlTooltip();
        }
    };

    private showControlTooltip(button: HTMLButtonElement) {
        this.hideControlTooltip();
        if (!this.controlTooltip) {
            this.controlTooltip = document.createElement("div");
            this.controlTooltip.id = "random-teams-control-tooltip";
            this.controlTooltip.className = "random-teams-control-tooltip";
            this.controlTooltip.setAttribute("role", "tooltip");
            // Portal above the clipped stage so the hint can sit over its top edge.
            document.body.append(this.controlTooltip);
        }
        this.controlTooltipButton = button;
        button.setAttribute("aria-describedby", this.controlTooltip.id);
        this.refreshControlTooltip();
    }

    private readonly refreshControlTooltip = () => {
        const tooltip = this.controlTooltip;
        const button = this.controlTooltipButton;
        if (!tooltip || !button) return;
        const anchor = button.getBoundingClientRect();
        if (!button.isConnected || anchor.width === 0 || anchor.bottom <= 0 || anchor.top >= window.innerHeight) {
            this.hideControlTooltip();
            return;
        }
        tooltip.textContent = button.dataset.controlTooltip ?? "";
        tooltip.hidden = false;
        const rect = tooltip.getBoundingClientRect();
        const gap = Number.parseFloat(getComputedStyle(tooltip).getPropertyValue("--tooltip-gap"));
        tooltip.style.left = `${clampNumber(anchor.left + (anchor.width - rect.width) / 2, gap, window.innerWidth - rect.width - gap)}px`;
        tooltip.style.top = `${Math.max(gap, anchor.top - rect.height - gap)}px`;
    };

    private hideControlTooltip() {
        this.controlTooltipButton?.removeAttribute("aria-describedby");
        this.controlTooltipButton = null;
        if (this.controlTooltip) this.controlTooltip.hidden = true;
    }

    private readonly handleInput = (event: Event) => {
        const target = event.target as HTMLInputElement | HTMLTextAreaElement | null;

        if (!target) {
            return;
        }

        if (this.launchInProgress) {
            return;
        }

        if (target.id === "random-teams-names-textarea") {
            if (this.state.result?.participants) this.resetGeneratedResult();
            this.state.rosterText = target.value;
            this.namesImportErrorKey = null;
            this.sync();
            return;
        }

        if (target.id === "random-teams-name-input") {
            this.state.groupName = normalizeRandomTeamsName(target.value);
            this.sync();
            return;
        }

        if (target.id === "random-teams-count-input") {
            this.state.teamCount = sanitizeDigits(target.value);
            target.value = this.state.teamCount;
            this.sync();
            return;
        }

        if (target.id === "random-teams-member-count-input") {
            this.state.memberCount = sanitizeDigits(target.value);
            target.value = this.state.memberCount;
            this.sync();
            return;
        }

        if (target.id === "random-teams-late-add-input") {
            this.lateAddDraft = sanitizeDigits(target.value);
            this.lateAddErrorKey = null;
            target.value = this.lateAddDraft;
            this.syncLateIdControls();
            return;
        }

        if (target.id === "random-teams-late-remove-input") {
            this.lateRemoveDraft = sanitizeDigits(target.value);
            this.lateRemoveErrorKey = null;
            target.value = this.lateRemoveDraft;
            this.syncLateIdControls();
            return;
        }

        if (target.hasAttribute("data-random-teams-exclusion-input")) {
            const inputIndex = Number.parseInt(target.dataset.exclusionIndex ?? "", 10);

            if (Number.isNaN(inputIndex)) {
                return;
            }

            const sanitizedValue = sanitizeDigits(target.value);
            const previousCursor = target.selectionStart ?? target.value.length;
            this.state.excludedIds[inputIndex] = sanitizedValue;
            this.state.excludedIds = ensureTrailingExclusionInput(this.state.excludedIds);
            this.sync();

            const nextIndex = Math.min(inputIndex, this.state.excludedIds.length - 1);
            const nextInput = this.refs.exclusionList?.querySelector<HTMLInputElement>(
                `[data-exclusion-index="${nextIndex}"]`
            );
            nextInput?.focus();
            const nextCursor = Math.min(previousCursor, nextInput?.value.length ?? 0);
            nextInput?.setSelectionRange(nextCursor, nextCursor);
        }
    };

    private readonly handleChange = async (event: Event) => {
        const target = event.target;
        if (!(target instanceof HTMLInputElement) || target.id !== "random-teams-names-file") return;
        const file = target.files?.[0];
        target.value = "";
        if (!file) return;

        try {
            this.state.rosterText = await readRandomTeamsRosterFile(file);
            this.resetGeneratedResult();
            this.namesImportErrorKey = null;
            this.state.rosterMode = "names";
            this.namesEditing = false;
            this.state.excludedIds = [""];
            this.sync();
        } catch {
            this.namesImportErrorKey = "randomTeamsRosterFileError";
            this.sync();
        }
    };

    private readonly handleFocusIn = (event: FocusEvent) => {
        const target = event.target as HTMLInputElement | null;

        if (target?.id === "random-teams-name-input") {
            target.select();
            return;
        }

        if (!target || !target.hasAttribute("data-random-teams-exclusion-input")) {
            return;
        }

        const inputIndex = Number.parseInt(target.dataset.exclusionIndex ?? "", 10);
        this.focusedExclusionIndex = Number.isNaN(inputIndex) ? null : inputIndex;
        this.syncMobileExclusionSubmitButton();
        this.syncMobileExclusionSubmitViewportOffset();
    };

    private readonly handleFocusOut = (event: FocusEvent) => {
        const target = event.target as HTMLInputElement | null;

        if (target?.id === "random-teams-name-input" && !this.launchInProgress) {
            this.sync();
            return;
        }

        if (
            !target ||
            this.launchInProgress ||
            !target.hasAttribute("data-random-teams-exclusion-input")
        ) {
            return;
        }

        const nextTarget = event.relatedTarget as HTMLElement | null;

        if (nextTarget?.closest("[data-remove-exclusion-index], [data-commit-active-exclusion]")) {
            this.pendingExclusionFocusIndex = null;
            return;
        }

        const nextExclusionInput = nextTarget?.closest<HTMLInputElement>("[data-random-teams-exclusion-input]");
        const nextExclusionIndex = Number.parseInt(nextExclusionInput?.dataset.exclusionIndex ?? "", 10);
        const focusIndex = !Number.isNaN(nextExclusionIndex)
            ? nextExclusionIndex
            : this.pendingExclusionFocusIndex;

        if (sanitizeDigits(target.value)) {
            this.commitExclusionInput(target, { focusBehavior: "none" });
        }

        if (focusIndex !== null && !Number.isNaN(focusIndex)) {
            this.focusExclusionInputSoon(focusIndex);
        }

        this.pendingExclusionFocusIndex = null;
        this.focusedExclusionIndex = focusIndex !== null && !Number.isNaN(focusIndex) ? focusIndex : null;
        this.syncMobileExclusionSubmitButton();
        this.syncMobileExclusionSubmitViewportOffset();
    };

    private readonly handleClick = (event: Event) => {
        const target = event.target as HTMLElement | null;

        if (!target) {
            return;
        }

        if (target.closest("[data-random-teams-names-import]") && !this.launchInProgress) {
            this.refs.namesFileInput?.click();
            return;
        }

        if (target.closest("[data-random-teams-names-edit]") && !this.launchInProgress) {
            this.namesEditing = !this.namesEditing;
            this.syncForm();
            if (this.namesEditing) this.refs.namesTextarea?.focus();
            return;
        }

        if (target.closest("#random-teams-roster-mode-toggle") && !this.launchInProgress) {
            const nextRosterMode = this.state.rosterMode === "ids" ? "names" : "ids";
            this.resetGeneratedResult();
            this.state.rosterMode = nextRosterMode;
            this.namesImportErrorKey = null;
            if (nextRosterMode === "names") this.state.excludedIds = [""];
            this.namesEditing = nextRosterMode === "names";
            this.sync();
            if (nextRosterMode === "names") {
                window.requestAnimationFrame(() => this.refs.namesTextarea?.focus());
            }
            return;
        }

        if (target.closest("#random-teams-mode-toggle") && !this.launchInProgress) {
            this.resetGeneratedResult();
            this.state.mode = this.state.mode === "teams" ? "orders" : "teams";
            this.sync();
            return;
        }

        if (target.closest("[data-commit-active-exclusion]") && !this.launchInProgress) {
            this.commitActiveExclusionInput();
            return;
        }

        const removeExclusionButton = target.closest<HTMLElement>("[data-remove-exclusion-index]");

        if (removeExclusionButton && !this.launchInProgress) {
            const removeIndex = Number.parseInt(removeExclusionButton.dataset.removeExclusionIndex ?? "", 10);

            if (!Number.isNaN(removeIndex)) {
                this.removeExclusionAt(removeIndex);
            }

            return;
        }

        if (target.closest("#random-teams-start-button")) {
            this.generateTeams();
            return;
        }

        if (target.closest("[data-random-teams-sound]")) {
            this.toggleSound();
            return;
        }

        if (target.closest("[data-random-teams-animations]")) {
            this.toggleAnimations();
            return;
        }

        if (target.closest("[data-random-teams-excluded-floor]")) {
            this.toggleExcludedFloor();
            return;
        }

        const finishFrontButton = target.closest<HTMLElement>("[data-random-teams-finish-front]");

        if (finishFrontButton) {
            const teamIndex = Number.parseInt(finishFrontButton.dataset.teamIndex ?? "", 10);

            if (!Number.isNaN(teamIndex)) {
                this.finishFrontOrderMember(teamIndex);
            }
            return;
        }

        if (target.closest("[data-random-teams-wipe]")) {
            this.wipeGeneratedResult();
            return;
        }

        if (target.closest("[data-random-teams-late-add-toggle]")) {
            this.setLateIdPanelOpen("add", !this.lateAddPanelOpen);
            return;
        }

        if (target.closest("[data-random-teams-late-add-submit]")) {
            this.addLateStudentId();
            return;
        }

        if (target.closest("[data-random-teams-late-remove-toggle]")) {
            this.setLateIdPanelOpen("remove", !this.lateRemovePanelOpen);
            return;
        }

        if (target.closest("[data-random-teams-late-remove-submit]")) {
            this.removeLateStudentId();
            return;
        }

        if (target.closest("[data-random-teams-presentation-close]")) {
            this.setPresentationOpen(false);
            return;
        }

        if (target.closest("[data-random-teams-fullscreen]")) {
            this.setPresentationOpen(!this.presentationOpen);
        }
    };

    private readonly handleDocumentKeyDown = (event: KeyboardEvent) => {
        const target = event.target as HTMLElement | null;
        if (event.key === "Escape") this.hideControlTooltip();

        if (event.key === "Enter" && !event.isComposing && target instanceof HTMLInputElement && target.id === "random-teams-name-input") {
            event.preventDefault();
            const nextInput = this.state.rosterMode === "names" ? this.refs.namesTextarea : this.refs.memberCountInput;
            if (this.state.rosterMode === "names") { this.namesEditing = true; this.syncForm(); }
            nextInput?.focus();
            if (nextInput instanceof HTMLInputElement) nextInput.select();
            return;
        }

        if (event.key === "Enter" && target instanceof HTMLInputElement && target.id === "random-teams-count-input") {
            event.preventDefault();
            const nextInput = this.state.rosterMode === "names" ? this.refs.namesTextarea : this.refs.memberCountInput;
            if (this.state.rosterMode === "names") { this.namesEditing = true; this.syncForm(); }
            nextInput?.focus();
            if (nextInput instanceof HTMLInputElement) nextInput.select();
            return;
        }

        if (event.key === "Enter" && target instanceof HTMLInputElement && target.id === "random-teams-member-count-input") {
            event.preventDefault();
            this.generateTeams();
            return;
        }

        if (
            event.key === "Enter" &&
            target instanceof HTMLInputElement &&
            target.hasAttribute("data-random-teams-exclusion-input") &&
            this.root.contains(target)
        ) {
            event.preventDefault();
            this.commitExclusionInput(target);
            return;
        }

        if (event.key === "Enter" && target instanceof HTMLInputElement && target.id === "random-teams-late-add-input") {
            event.preventDefault();
            this.addLateStudentId();
            return;
        }

        if (event.key === "Enter" && target instanceof HTMLInputElement && target.id === "random-teams-late-remove-input") {
            event.preventDefault();
            this.removeLateStudentId();
            return;
        }

        if (event.key === "Escape" && (this.lateAddPanelOpen || this.lateRemovePanelOpen)) {
            if (this.lateAddPanelOpen) {
                this.setLateIdPanelOpen("add", false);
            }

            if (this.lateRemovePanelOpen) {
                this.setLateIdPanelOpen("remove", false);
            }
            return;
        }

        if (event.key === "Escape" && this.presentationOpen) {
            this.setPresentationOpen(false);
        }
    };

    private readonly handleWindowResize = () => {
        if (this.resizeFrameId !== null) {
            return;
        }

        this.resizeFrameId = window.requestAnimationFrame(() => {
            this.resizeFrameId = null;
            const perfEnabled = isRandomTeamsPerfEnabled();
            const resizeStartedAt = perfEnabled ? performance.now() : 0;
            const perfPhases: Record<string, number> = {};
            let action = "connectors+canvas";

            const viewportStartedAt = perfEnabled ? performance.now() : 0;
            this.syncMobileExclusionSubmitViewportOffset();
            this.syncMobileExclusionSubmitButton();
            if (perfEnabled) {
                recordRandomTeamsPerfPhase(perfPhases, "viewport", viewportStartedAt);
            }
            const hasOrderResult = this.state.result?.mode === "orders";

            if (hasOrderResult && this.launchInProgress) {
                this.orderLayoutRefreshPending = true;
                action = "launch-connectors";
                const connectorsStartedAt = perfEnabled ? performance.now() : 0;
                this.syncOrderConnectors();
                if (perfEnabled) {
                    recordRandomTeamsPerfPhase(perfPhases, "connectors", connectorsStartedAt);
                }
                const canvasStartedAt = perfEnabled ? performance.now() : 0;
                this.syncStageCanvas();
                if (perfEnabled) {
                    recordRandomTeamsPerfPhase(perfPhases, "canvas", canvasStartedAt);
                    const phases = formatRandomTeamsPerfPhases(perfPhases);
                    logRandomTeamsPerf(
                        `resize action=${action} total=${roundRandomTeamsPerfMs(performance.now() - resizeStartedAt)}ms mode=${
                            this.state.result?.mode ?? "preview"
                        } launch=1${phases ? ` phases=${phases}` : ""}`
                    );
                }
                return;
            }

            if ((this.presentationOpen || hasOrderResult) && !this.launchInProgress) {
                this.orderLayoutRefreshPending = false;
                action = "sync-results";
                const resultsStartedAt = perfEnabled ? performance.now() : 0;
                this.syncResults();
                if (perfEnabled) {
                    recordRandomTeamsPerfPhase(perfPhases, "results", resultsStartedAt);
                }
                const canvasStartedAt = perfEnabled ? performance.now() : 0;
                this.syncStageCanvas();
                if (perfEnabled) {
                    recordRandomTeamsPerfPhase(perfPhases, "canvas", canvasStartedAt);
                    const phases = formatRandomTeamsPerfPhases(perfPhases);
                    logRandomTeamsPerf(
                        `resize action=${action} total=${roundRandomTeamsPerfMs(performance.now() - resizeStartedAt)}ms mode=${
                            this.state.result?.mode ?? "preview"
                        } presentation=${this.presentationOpen ? 1 : 0}${phases ? ` phases=${phases}` : ""}`
                    );
                }
                return;
            }

            const connectorsStartedAt = perfEnabled ? performance.now() : 0;
            this.syncOrderConnectors();
            if (perfEnabled) {
                recordRandomTeamsPerfPhase(perfPhases, "connectors", connectorsStartedAt);
            }
            const canvasStartedAt = perfEnabled ? performance.now() : 0;
            this.syncStageCanvas();
            if (perfEnabled) {
                recordRandomTeamsPerfPhase(perfPhases, "canvas", canvasStartedAt);
                const phases = formatRandomTeamsPerfPhases(perfPhases);
                logRandomTeamsPerf(
                    `resize action=${action} total=${roundRandomTeamsPerfMs(performance.now() - resizeStartedAt)}ms mode=${
                        this.state.result?.mode ?? "preview"
                    }${phases ? ` phases=${phases}` : ""}`
                );
            }
        });
    };

    private readonly handleVisualViewportChange = () => {
        this.syncMobileExclusionSubmitViewportOffset();
        this.syncMobileExclusionSubmitButton();
        this.syncStageCanvas();
    };

    private readonly handleDocumentVisibilityChange = () => {
        this.syncStageCanvasActivity();
    };

    private readonly handleReducedMotionChange = () => {
        if (this.reducedMotionQuery?.matches) {
            this.launchFrameIds.forEach((frameId) => window.cancelAnimationFrame(frameId));
            this.launchFrameIds = [];
            this.activeAnimations.forEach((animation) => animation.finish());
        }
        this.syncAnimationButton();
        this.syncStageCanvas();
    };

    private readonly handleStageVisibilityChange = (entries: IntersectionObserverEntry[]) => {
        const stageEntry = entries.find((entry) => entry.target === this.refs.stage);
        this.stageIsNearViewport = stageEntry?.isIntersecting ?? true;
        this.syncStageCanvasActivity();
    };

    private setPresentationOpen(isOpen: boolean) {
        this.clearPresentationSettleFrames();
        this.presentationOpen = isOpen;
        this.lastOrderPresentationLayoutLogSignature = null;
        this.root.classList.toggle("is-random-teams-presenting", isOpen);
        this.syncDocumentScrollLock();
        this.syncFullscreenButton();

        if (!isOpen) {
            window.requestAnimationFrame(() => {
                if (!this.launchInProgress) {
                    this.syncResults();
                } else {
                    this.syncOrderConnectorsAfterLayout();
                }
            });
            return;
        }

        this.root.classList.add("is-random-teams-presentation-settling");
        this.syncPresentationLayoutForCurrentState();

        const firstFrameId = window.requestAnimationFrame(() => {
            this.presentationSettleFrameIds = this.presentationSettleFrameIds.filter((frameId) => frameId !== firstFrameId);

            if (!this.presentationOpen) {
                return;
            }

            this.syncPresentationLayoutForCurrentState();

            const secondFrameId = window.requestAnimationFrame(() => {
                this.presentationSettleFrameIds = this.presentationSettleFrameIds.filter((frameId) => frameId !== secondFrameId);

                if (!this.presentationOpen) {
                    return;
                }

                this.syncPresentationLayoutForCurrentState();
                this.root.classList.remove("is-random-teams-presentation-settling");
            });

            this.presentationSettleFrameIds.push(secondFrameId);
        });

        this.presentationSettleFrameIds.push(firstFrameId);
    }

    private clearPresentationSettleFrames() {
        this.presentationSettleFrameIds.forEach((frameId) => window.cancelAnimationFrame(frameId));
        this.presentationSettleFrameIds = [];
        this.root.classList.remove("is-random-teams-presentation-settling");
    }

    private syncPresentationLayoutForCurrentState() {
        if (!this.presentationOpen) {
            return;
        }

        if (!this.launchInProgress) {
            this.syncResults();

            if (this.state.result?.mode === "orders" && this.isOrderPresentationRowRefreshNeeded()) {
                this.syncResults();
            }

            return;
        }

        if (this.state.result?.mode === "orders") {
            this.syncOrderConnectorsAfterLayout();
        }
    }

    private syncDocumentScrollLock() {
        if (this.presentationOpen) {
            if (!this.documentScrollLockSnapshot) {
                this.documentScrollLockSnapshot = {
                    documentOverflow: document.documentElement.style.overflow,
                    documentOverscrollBehavior: document.documentElement.style.overscrollBehavior,
                    bodyOverflow: document.body.style.overflow,
                    bodyOverscrollBehavior: document.body.style.overscrollBehavior
                };
            }

            document.documentElement.style.overflow = "hidden";
            document.documentElement.style.overscrollBehavior = "none";
            document.body.style.overflow = "hidden";
            document.body.style.overscrollBehavior = "none";
            return;
        }

        this.releaseDocumentScrollLock();
    }

    private releaseDocumentScrollLock() {
        if (!this.documentScrollLockSnapshot) {
            return;
        }

        document.documentElement.style.overflow = this.documentScrollLockSnapshot.documentOverflow;
        document.documentElement.style.overscrollBehavior = this.documentScrollLockSnapshot.documentOverscrollBehavior;
        document.body.style.overflow = this.documentScrollLockSnapshot.bodyOverflow;
        document.body.style.overscrollBehavior = this.documentScrollLockSnapshot.bodyOverscrollBehavior;
        this.documentScrollLockSnapshot = null;
    }

    private syncFullscreenButton(validation = this.computeValidation()) {
        if (!this.refs.fullscreenButton) {
            return;
        }

        const label = t(this.presentationOpen ? "exitFullscreen" : "fullscreen");
        const isAvailable =
            !this.launchInProgress && (this.presentationOpen || Boolean(this.state.result) || validation.canGenerate);

        this.refs.fullscreenButton.hidden = false;
        this.refs.fullscreenButton.disabled = !isAvailable;
        this.refs.fullscreenButton.classList.toggle("is-presenting", this.presentationOpen);
        this.refs.fullscreenButton.setAttribute("aria-label", label);
        this.setControlTooltip(this.refs.fullscreenButton, label);
    }

    private syncSoundButton() {
        if (!this.refs.soundButton) {
            return;
        }

        const label = t(this.soundEnabled ? "soundOn" : "soundOff");
        this.refs.soundButton.classList.toggle("is-muted", !this.soundEnabled);
        this.refs.soundButton.setAttribute("aria-label", label);
        this.refs.soundButton.setAttribute("aria-pressed", String(this.soundEnabled));
        this.setControlTooltip(this.refs.soundButton, t(this.soundEnabled ? "randomTeamsSoundOffAction" : "randomTeamsSoundOnAction"));
    }

    private syncAnimationButton() {
        if (!this.refs.animationButton) {
            return;
        }

        this.syncAnimationAutoSuppression();

        const isAutoSuppressed = this.animationAutoSuppressed;
        const isReducedMotion = this.reducedMotionQuery?.matches === true;
        const isEnabled = this.areAnimationsEnabled();
        const label = isReducedMotion
            ? t("animationsReducedMotion")
            : isAutoSuppressed
              ? t("animationsAutoOff")
              : t(isEnabled ? "animationsOn" : "animationsOff");

        this.refs.animationButton.classList.toggle("is-muted", !isEnabled);
        this.refs.animationButton.classList.toggle("is-forced-off", isAutoSuppressed || isReducedMotion);
        this.refs.animationButton.disabled = this.launchInProgress;
        this.refs.animationButton.setAttribute("aria-label", label);
        this.refs.animationButton.setAttribute("aria-pressed", String(isEnabled));
        this.setControlTooltip(this.refs.animationButton, isReducedMotion || isAutoSuppressed
            ? label : t(isEnabled ? "randomTeamsAnimationsOffAction" : "randomTeamsAnimationsOnAction"));
    }

    private syncExcludedFloorButton() {
        if (!this.refs.excludedFloorButton) {
            return;
        }

        const label = t(this.showExcludedOnFloor ? "hideExcludedOnFloor" : "showExcludedOnFloor");

        this.refs.excludedFloorButton.hidden = this.state.rosterMode === "names";
        this.refs.excludedFloorButton.disabled = this.launchInProgress;
        this.refs.excludedFloorButton.classList.toggle("is-active", this.showExcludedOnFloor);
        this.refs.excludedFloorButton.setAttribute("aria-label", label);
        this.refs.excludedFloorButton.setAttribute("aria-pressed", String(this.showExcludedOnFloor));
        this.setControlTooltip(this.refs.excludedFloorButton, t(this.showExcludedOnFloor ? "randomTeamsHideMissingAction" : "randomTeamsShowMissingAction"));
    }

    private syncWipeButton() {
        if (!this.refs.wipeButton) {
            return;
        }

        const label = t("wipe");

        this.refs.wipeButton.disabled = this.launchInProgress || !this.state.result;
        this.refs.wipeButton.setAttribute("aria-label", label);
        this.setControlTooltip(this.refs.wipeButton, t("randomTeamsClearAction"));
    }

    private getLateIdToggleButton(mode: LateIdMode): HTMLButtonElement | null {
        return mode === "add" ? this.refs.lateAddToggleButton : this.refs.lateRemoveToggleButton;
    }

    private getLateIdPanel(mode: LateIdMode): HTMLElement | null {
        return mode === "add" ? this.refs.lateAddPanel : this.refs.lateRemovePanel;
    }

    private getLateIdInput(mode: LateIdMode): HTMLInputElement | null {
        return mode === "add" ? this.refs.lateAddInput : this.refs.lateRemoveInput;
    }

    private getLateIdSubmitButton(mode: LateIdMode): HTMLButtonElement | null {
        return mode === "add" ? this.refs.lateAddSubmitButton : this.refs.lateRemoveSubmitButton;
    }

    private getLateIdHelper(mode: LateIdMode): HTMLElement | null {
        return mode === "add" ? this.refs.lateAddHelper : this.refs.lateRemoveHelper;
    }

    private getLateIdDraft(mode: LateIdMode): string {
        return mode === "add" ? this.lateAddDraft : this.lateRemoveDraft;
    }

    private setLateIdDraft(mode: LateIdMode, value: string) {
        if (mode === "add") {
            this.lateAddDraft = value;
            return;
        }

        this.lateRemoveDraft = value;
    }

    private getLateIdErrorKey(mode: LateIdMode): TranslationKey | null {
        return mode === "add" ? this.lateAddErrorKey : this.lateRemoveErrorKey;
    }

    private setLateIdErrorKey(mode: LateIdMode, value: TranslationKey | null) {
        if (mode === "add") {
            this.lateAddErrorKey = value;
            return;
        }

        this.lateRemoveErrorKey = value;
    }

    private isLateIdPanelOpen(mode: LateIdMode): boolean {
        return mode === "add" ? this.lateAddPanelOpen : this.lateRemovePanelOpen;
    }

    private setLateIdPanelState(mode: LateIdMode, isOpen: boolean) {
        if (mode === "add") {
            this.lateAddPanelOpen = isOpen;
            return;
        }

        this.lateRemovePanelOpen = isOpen;
    }

    private clearLateIdState(mode: LateIdMode) {
        this.setLateIdDraft(mode, "");
        this.setLateIdErrorKey(mode, null);
        this.setLateIdPanelState(mode, false);
    }

    private syncLateIdControls(mode?: LateIdMode) {
        const hasResult = Boolean(this.state.result) && !this.state.result?.participants && this.state.rosterMode !== "names";
        const modes = mode ? [mode] : (["add", "remove"] as const);

        modes.forEach((currentMode) => {
            const isAddMode = currentMode === "add";
            const toggleLabel = t(isAddMode ? "addStudentId" : "removeStudentId");
            const promptLabel = t(isAddMode ? "addStudentIdPrompt" : "removeStudentIdPrompt");
            const helperKey = this.getLateIdErrorKey(currentMode);
            const helperText = helperKey ? t(helperKey) : "";
            const draft = this.getLateIdDraft(currentMode);
            const parsedDraft = parsePositiveInteger(draft);
            const toggleButton = this.getLateIdToggleButton(currentMode);
            const panel = this.getLateIdPanel(currentMode);
            const input = this.getLateIdInput(currentMode);
            const submitButton = this.getLateIdSubmitButton(currentMode);
            const helper = this.getLateIdHelper(currentMode);
            const panelOpen = this.isLateIdPanelOpen(currentMode);

            if (toggleButton) {
                // Collapse the empty anchor so hidden late-ID controls do not leave a phantom flex gap before fullscreen.
                if (toggleButton.parentElement instanceof HTMLElement) {
                    toggleButton.parentElement.hidden = !hasResult;
                }
                toggleButton.hidden = !hasResult;
                toggleButton.disabled = this.launchInProgress || !hasResult;
                toggleButton.classList.toggle("is-active", panelOpen);
                toggleButton.setAttribute("aria-label", toggleLabel);
                toggleButton.setAttribute("aria-pressed", String(panelOpen));
                this.setControlTooltip(toggleButton, toggleLabel);
            }

            if (panel) {
                panel.hidden = !hasResult || !panelOpen;
            }

            if (input) {
                input.disabled = this.launchInProgress || !hasResult;
                input.value = draft;
                input.setAttribute("aria-label", promptLabel);
                input.placeholder = t("studentIdPlaceholder");
            }

            if (submitButton) {
                submitButton.disabled = this.launchInProgress || !hasResult || parsedDraft === null;
                submitButton.setAttribute("aria-label", toggleLabel);
                submitButton.setAttribute("title", toggleLabel);
            }

            if (helper) {
                helper.textContent = helperText;
                helper.hidden = helperText.length === 0;
            }
        });
    }

    private setLateIdPanelOpen(mode: LateIdMode, isOpen: boolean) {
        if (!this.state.result && isOpen) {
            return;
        }

        if (!isOpen) {
            this.clearLateIdState(mode);
            this.syncLateIdControls();
            return;
        }

        const otherMode: LateIdMode = mode === "add" ? "remove" : "add";
        this.clearLateIdState(otherMode);
        this.setLateIdPanelState(mode, true);
        this.syncLateIdControls();
        this.refreshControlTooltip();

        window.requestAnimationFrame(() => {
            const input = this.getLateIdInput(mode);
            const draft = this.getLateIdDraft(mode);
            input?.focus();
            input?.setSelectionRange(draft.length, draft.length);
        });
    }

    private validateLateAddDraft():
        | {
              memberId: number;
              errorKey: null;
          }
        | {
              memberId: null;
              errorKey: TranslationKey;
          } {
        const result = this.state.result;
        const memberId = parsePositiveInteger(this.lateAddDraft);

        if (!result || memberId === null) {
            return {
                memberId: null,
                errorKey: "randomTeamsLateAddInvalid"
            };
        }

        const idsInPlay = new Set([...result.floorIds, ...result.teams.flat()]);

        if (idsInPlay.has(memberId)) {
            return {
                memberId: null,
                errorKey: "randomTeamsLateAddDuplicate"
            };
        }

        if (this.computeValidation().validExcludedIds.includes(memberId)) {
            return {
                memberId: null,
                errorKey: "randomTeamsLateAddExcluded"
            };
        }

        return {
            memberId,
            errorKey: null
        };
    }

    private getSmallestTeamIndices() {
        const result = this.state.result;

        if (!result || result.teams.length === 0) {
            return [];
        }

        const smallestSize = Math.min(...result.teams.map((members) => members.length));
        return result.teams
            .map((members, teamIndex) => ({ teamIndex, size: members.length }))
            .filter(({ size }) => size === smallestSize)
            .map(({ teamIndex }) => teamIndex);
    }

    private getLateAddDestination(memberId: number): DropDestination | null {
        const result = this.state.result;

        if (!result) {
            return null;
        }

        const teamIndex = pickRandomItem(this.getSmallestTeamIndices());

        if (teamIndex === null) {
            return null;
        }

        const nextMembers = result.teams[teamIndex];

        if (result.mode === "orders") {
            return {
                zone: "team",
                teamIndex,
                index: Math.floor(Math.random() * (nextMembers.length + 1))
            };
        }

        return {
            zone: "team",
            teamIndex,
            index: nextMembers.length
        };
    }

    private addLateStudentId() {
        if (this.launchInProgress) {
            return;
        }

        const result = this.state.result;
        const validation = this.validateLateAddDraft();

        if (!result || validation.errorKey) {
            if (result && validation.errorKey === "randomTeamsLateAddDuplicate") {
                const duplicateMemberId = parsePositiveInteger(this.lateAddDraft);

                // Keep duplicate late-add feedback aligned with duplicate exclusions:
                // flash the existing owner of that ID instead of opening a second error lane.
                this.lateAddErrorKey = null;
                if (duplicateMemberId !== null) {
                    this.flashMemberToken(duplicateMemberId);
                }
            } else {
                this.lateAddErrorKey = validation.errorKey;
            }
            this.syncLateIdControls("add");
            return;
        }

        const destination = this.getLateAddDestination(validation.memberId);

        if (!destination || destination.zone !== "team") {
            this.lateAddErrorKey = "randomTeamsLateAddInvalid";
            this.syncLateIdControls("add");
            return;
        }

        const memberId = validation.memberId;
        const nextMembers = result.teams[destination.teamIndex];

        // Late arrivals change only the live result; keep the typed setup count untouched.
        result.memberCount = Math.max(result.memberCount, memberId);
        nextMembers.splice(clampIndex(destination.index, nextMembers.length), 0, memberId);

        if (result.mode === "orders") {
            result.orderSlotCounts[destination.teamIndex] = Math.max(
                result.orderSlotCounts[destination.teamIndex] ?? 0,
                nextMembers.length
            );
        }

        this.setLateIdPanelOpen("add", false);
        this.launchMembers([memberId]);
    }

    private findExclusionIndex(memberId: number): number {
        return this.state.excludedIds.findIndex((value) => parsePositiveInteger(value) === memberId);
    }

    private addExcludedId(memberId: number): number {
        const existingIndex = this.findExclusionIndex(memberId);

        if (existingIndex >= 0) {
            return existingIndex;
        }

        this.state.excludedIds = ensureTrailingExclusionInput([...this.state.excludedIds, String(memberId)]);
        return this.findExclusionIndex(memberId);
    }

    private getMemberReleasePoint(memberId: number) {
        const originToken = this.root.querySelector<HTMLElement>(`[data-member-token][data-member-id="${memberId}"]`);
        const originRect = originToken?.getBoundingClientRect();
        const stageRect = this.refs.stage?.getBoundingClientRect();

        if (originRect) {
            return {
                x: originRect.left + originRect.width / 2,
                y: originRect.top + originRect.height / 2
            };
        }

        if (stageRect) {
            return {
                x: stageRect.left + stageRect.width / 2,
                y: stageRect.top + stageRect.height / 2
            };
        }

        return { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    }

    private removeLateStudentId() {
        if (this.launchInProgress) {
            return;
        }

        const result = this.state.result;
        const memberId = parsePositiveInteger(this.lateRemoveDraft);

        if (!result || memberId === null) {
            this.lateRemoveErrorKey = "randomTeamsLateRemoveInvalid";
            this.syncLateIdControls("remove");
            return;
        }

        const exclusionIndex = this.findExclusionIndex(memberId);

        if (exclusionIndex >= 0) {
            this.lateRemoveErrorKey = null;
            this.syncLateIdControls("remove");
            this.flashExclusionPill(exclusionIndex);
            return;
        }

        const location = this.findCurrentLocation(memberId);

        if (!location) {
            this.lateRemoveErrorKey = "randomTeamsLateRemoveMissing";
            this.syncLateIdControls("remove");
            return;
        }

        this.lateRemoveErrorKey = null;

        if (location.zone === "team") {
            this.setLateIdPanelOpen("remove", false);
            this.prepareAudio();
            this.moveMemberWithAnimation(memberId, { zone: "floor", index: result.floorIds.length }, this.getMemberReleasePoint(memberId));
            return;
        }

        const nextExclusionIndex = this.addExcludedId(memberId);
        this.setLateIdPanelOpen("remove", false);
        this.sync();

        if (nextExclusionIndex >= 0) {
            this.flashExclusionPill(nextExclusionIndex);
        }
    }

    private toggleSound() {
        this.soundEnabled = !this.soundEnabled;
        writeStoredSoundEnabled(this.soundEnabled);
        this.syncSoundButton();

        if (this.soundEnabled) {
            void this.audio.prepare();
        }
    }

    private toggleAnimations() {
        if (this.launchInProgress) {
            return;
        }

        const isLargeMemberCount = this.isAnimationAutoOffRecommended();

        if (this.animationAutoSuppressed) {
            this.animationAutoSuppressed = false;
            this.largeAnimationAutoOffApplied = isLargeMemberCount;
            this.animationsPreferred = true;
        } else {
            this.animationsPreferred = !this.animationsPreferred;

            if (isLargeMemberCount && this.animationsPreferred) {
                this.largeAnimationAutoOffApplied = true;
            }
        }

        writeStoredAnimationsEnabled(this.animationsPreferred);
        this.syncAnimationButton();
        this.syncStageCanvas();
    }

    private toggleExcludedFloor() {
        if (this.launchInProgress) {
            return;
        }

        this.showExcludedOnFloor = !this.showExcludedOnFloor;
        this.state.showExcludedOnFloor = this.showExcludedOnFloor;
        const changed = this.syncExcludedFloorIds({ removeWhenHidden: true });
        this.syncForm();

        if (changed || this.state.result) {
            this.syncResults();
        }
    }

    private syncExcludedFloorIds({
        removeWhenHidden = false,
        validation = this.computeValidation()
    }: {
        removeWhenHidden?: boolean;
        validation?: Validation;
    } = {}): boolean {
        const result = this.state.result;

        if (!result) {
            return false;
        }

        if (validation.invalidExcludedIndices.size > 0) {
            return false;
        }

        const excludedIds = validation.validExcludedIds;
        const excludedSet = new Set(excludedIds);
        const previousExcludedSet = new Set(result.excludedIds);
        let changed = false;

        if (
            result.excludedIds.length !== excludedIds.length ||
            result.excludedIds.some((memberId, index) => memberId !== excludedIds[index])
        ) {
            result.excludedIds = [...excludedIds];
            changed = true;
        }

        const newlyFlooredExcludedIds: number[] = [];

        // Newly excluded IDs should leave the active teams immediately; if the
        // excluded floor is hidden, pulling them to the floor removes them from view.
        result.teams = result.teams.map((members) => {
            const remainingMembers = members.filter((memberId) => {
                if (!excludedSet.has(memberId)) {
                    return true;
                }

                newlyFlooredExcludedIds.push(memberId);
                changed = true;
                return false;
            });

            return remainingMembers;
        });

        if (!this.showExcludedOnFloor) {
            if (removeWhenHidden) {
                const nextFloorIds = result.floorIds.filter((memberId) => !excludedSet.has(memberId));

                if (nextFloorIds.length !== result.floorIds.length) {
                    result.floorIds = nextFloorIds;
                    changed = true;
                }
            }
        } else {
            const placedIds = new Set([...result.floorIds, ...result.teams.flat()]);
            newlyFlooredExcludedIds.forEach((memberId) => {
                if (placedIds.has(memberId)) {
                    return;
                }

                result.floorIds.push(memberId);
                placedIds.add(memberId);
                changed = true;
            });

            excludedIds.forEach((memberId) => {
                if (memberId > result.memberCount || placedIds.has(memberId)) {
                    return;
                }

                result.floorIds.push(memberId);
                placedIds.add(memberId);
                changed = true;
            });
        }

        const placedIds = new Set([...result.floorIds, ...result.teams.flat()]);

        previousExcludedSet.forEach((memberId) => {
            if (excludedSet.has(memberId) || memberId > result.memberCount || placedIds.has(memberId)) {
                return;
            }

            result.floorIds.push(memberId);
            placedIds.add(memberId);
            changed = true;
        });

        return changed;
    }

    private prepareAudio() {
        if (this.soundEnabled) {
            void this.audio.prepare();
        }
    }

    private getCurrentAnimationMemberCount(): number {
        if (this.state.result) {
            return this.state.result.floorIds.length + this.state.result.teams.reduce((total, members) => total + members.length, 0);
        }

        return this.computeValidation().includedIds.length;
    }

    private isAnimationAutoOffRecommended(memberCount = this.getCurrentAnimationMemberCount()): boolean {
        return memberCount > MAX_ANIMATED_LAUNCH_MEMBER_COUNT;
    }

    private syncAnimationAutoSuppression(memberCount = this.getCurrentAnimationMemberCount()) {
        if (!this.isAnimationAutoOffRecommended(memberCount)) {
            this.animationAutoSuppressed = false;
            this.largeAnimationAutoOffApplied = false;
            return;
        }

        if (!this.animationsPreferred) {
            this.animationAutoSuppressed = false;
            return;
        }

        if (!this.largeAnimationAutoOffApplied) {
            this.animationAutoSuppressed = true;
            this.largeAnimationAutoOffApplied = true;
        }
    }

    private areAnimationsEnabled(memberCount = this.getCurrentAnimationMemberCount()): boolean {
        this.syncAnimationAutoSuppression(memberCount);

        return this.animationsPreferred && !this.animationAutoSuppressed && this.reducedMotionQuery?.matches !== true;
    }

    private syncStageCanvas() {
        if (!this.refs.stage || !this.stageCanvasRenderer?.isAvailable) {
            return;
        }

        this.syncStageCanvasActivity();
        this.stageCanvasRenderer.setViewport(this.refs.stage.clientWidth, this.refs.stage.clientHeight);
        this.stageCanvasRenderer.setState({
            hasPreview: this.refs.board?.classList.contains("has-random-teams-preview") ?? false,
            hasResult: Boolean(this.state.result),
            launchInProgress: this.launchInProgress,
            motionEnabled: this.animationsPreferred && !this.animationAutoSuppressed,
            presentationOpen: this.presentationOpen,
            resultMode: this.state.result?.mode ?? null
        });

        if (this.state.result?.mode !== "orders") {
            this.stageCanvasRenderer.setOrderConnectorGlows([]);
        }
    }

    private syncStageCanvasActivity() {
        this.stageCanvasRenderer?.setActive(document.visibilityState !== "hidden" && this.stageIsNearViewport);
    }

    private getStageClipRect(
        element: HTMLElement,
        stageRect: DOMRect
    ): { x: number; y: number; width: number; height: number; radius: number } | null {
        const rect = element.getBoundingClientRect();

        if (rect.width <= 0 || rect.height <= 0) {
            return null;
        }

        const styles = window.getComputedStyle(element);
        const radius = Math.max(parseCssPixelValue(styles.borderTopLeftRadius), parseCssPixelValue(styles.borderRadius));

        return {
            x: rect.left - stageRect.left,
            y: rect.top - stageRect.top,
            width: rect.width,
            height: rect.height,
            radius
        };
    }

    private getCountSuffix() {
        const count = parsePositiveInteger(this.state.teamCount);
        const isSingular = count === 1;

        if (this.state.mode === "orders") {
            return t(isSingular ? "randomOrderSuffixSingular" : "randomOrderSuffixPlural");
        }

        return t(isSingular ? "randomTeamSuffixSingular" : "randomTeamSuffixPlural");
    }

    private getGroupName(count: number, capitalized = false): string {
        const customName = normalizeRandomTeamsName(this.state.groupName);
        const noun = inflectRandomTeamsName(customName || t("randomTeamsDefaultName"), count);
        return capitalized ? noun.charAt(0).toLocaleUpperCase() + noun.slice(1) : noun;
    }

    private getGroupHeading(teamIndex: number, mode: RandomTeamsMode): string {
        return `${mode === "orders" ? t("order") : this.getGroupName(1, true)} ${teamIndex + 1}`;
    }

    private getMemberLabel() {
        return t("randomTeamsStudentsTotal");
    }

    private getMemberPrefix() {
        return t("randomTeamsClassHas");
    }

    private getDropHereLabel() {
        return t(this.state.rosterMode === "names" ? "randomTeamsDropNamesHere" : "randomTeamsDropHere");
    }

    private getTodayStudentCount(validation = this.computeValidation()): number | null {
        const result = this.state.result;

        if (!result) {
            return validation.memberCount === null ? null : validation.includedIds.length;
        }

        const excludedIds = new Set(result.excludedIds);
        return new Set([...result.teams.flat(), ...result.floorIds].filter((memberId) => !excludedIds.has(memberId))).size;
    }

    private findDuplicateExclusionIndex(inputIndex: number, value: string): number {
        const nextId = parsePositiveInteger(value);

        if (nextId === null) {
            return -1;
        }

        return this.state.excludedIds.findIndex(
            (existingValue, existingIndex) => existingIndex !== inputIndex && parsePositiveInteger(existingValue) === nextId
        );
    }

    private focusExclusionInput(index: number) {
        const input = this.refs.exclusionList?.querySelector<HTMLInputElement>(`[data-exclusion-index="${index}"]`);
        input?.focus();
        input?.setSelectionRange(input.value.length, input.value.length);
    }

    private focusExclusionInputSoon(index: number) {
        const focus = () => this.focusExclusionInput(index);
        focus();
        window.requestAnimationFrame(focus);
        window.setTimeout(focus, 60);
    }

    private commitExclusionInput(input: HTMLInputElement, options: { focusBehavior?: "next" | "current" | "none" } = {}) {
        if (this.launchInProgress) {
            return;
        }

        const { focusBehavior = "next" } = options;
        const inputIndex = Number.parseInt(input.dataset.exclusionIndex ?? "", 10);

        if (Number.isNaN(inputIndex)) {
            return;
        }

        const sanitizedValue = sanitizeDigits(input.value);
        this.state.excludedIds[inputIndex] = sanitizedValue;
        this.state.excludedIds = ensureTrailingExclusionInput(this.state.excludedIds);

        const duplicateIndex = this.findDuplicateExclusionIndex(inputIndex, sanitizedValue);

        if (duplicateIndex >= 0) {
            const nextValues = [...this.state.excludedIds];
            const adjustedDuplicateIndex = inputIndex < duplicateIndex ? duplicateIndex - 1 : duplicateIndex;

            if (inputIndex < nextValues.length - 1) {
                nextValues.splice(inputIndex, 1);
            } else {
                nextValues[inputIndex] = "";
            }

            this.state.excludedIds = ensureTrailingExclusionInput(nextValues);
            this.sync();
            if (focusBehavior !== "none") {
                this.focusExclusionInputSoon(Math.min(inputIndex, this.state.excludedIds.length - 1));
            }
            this.flashExclusionPill(adjustedDuplicateIndex);
            return;
        }

        this.sync();

        if (focusBehavior === "none") {
            return;
        }

        if (focusBehavior === "current") {
            this.focusExclusionInputSoon(Math.min(inputIndex, this.state.excludedIds.length - 1));
            return;
        }

        this.focusExclusionInputSoon(Math.min(inputIndex + 1, this.state.excludedIds.length - 1));
    }

    private clearDuplicateExclusionFlash() {
        if (this.duplicateExclusionFlashTimeout !== null) {
            window.clearTimeout(this.duplicateExclusionFlashTimeout);
            this.duplicateExclusionFlashTimeout = null;
        }

        this.duplicateFeedbackFlashElement?.classList.remove("is-duplicate-absorbed");
        this.duplicateFeedbackFlashElement = null;
    }

    private flashDuplicateFeedback(element: HTMLElement | null) {
        this.clearDuplicateExclusionFlash();

        if (!element) {
            return;
        }

        this.duplicateFeedbackFlashElement = element;
        element.classList.remove("is-duplicate-absorbed");
        void element.offsetWidth;
        element.classList.add("is-duplicate-absorbed");
        this.duplicateExclusionFlashTimeout = window.setTimeout(() => {
            this.duplicateExclusionFlashTimeout = null;
            element.classList.remove("is-duplicate-absorbed");
            if (this.duplicateFeedbackFlashElement === element) {
                this.duplicateFeedbackFlashElement = null;
            }
        }, 820);
    }

    private flashExclusionPill(index: number) {
        const pill = this.refs.exclusionList?.querySelector<HTMLElement>(`[data-exclusion-pill-index="${index}"]`) ?? null;
        this.flashDuplicateFeedback(pill);
    }

    private flashMemberToken(memberId: number) {
        const token = this.root.querySelector<HTMLElement>(`[data-member-token][data-member-id="${memberId}"]`);
        this.flashDuplicateFeedback(token);
    }

    private readonly handlePointerDown = (event: PointerEvent) => {
        const target = event.target as HTMLElement | null;

        if (!target || this.launchInProgress) {
            return;
        }

        if (target.closest("[data-commit-active-exclusion]")) {
            event.preventDefault();
            this.prepareAudio();
            return;
        }

        const pendingExclusionInput = target.closest<HTMLInputElement>("[data-random-teams-exclusion-input]");
        const pendingExclusionIndex = Number.parseInt(pendingExclusionInput?.dataset.exclusionIndex ?? "", 10);
        this.pendingExclusionFocusIndex = !Number.isNaN(pendingExclusionIndex) ? pendingExclusionIndex : null;

        this.prepareAudio();

        const token = target.closest<HTMLElement>("[data-member-token]");

        if (!token) {
            return;
        }

        event.preventDefault();

        const memberId = Number.parseInt(token.dataset.memberId ?? "", 10);

        if (Number.isNaN(memberId) || !this.refs.stage || !this.refs.dragLayer) {
            return;
        }

        const perfEnabled = isRandomTeamsPerfEnabled();
        const captureStartedAt = perfEnabled ? performance.now() : 0;
        this.clearHoverState();

        const tokenRect = token.getBoundingClientRect();
        const stageRect = this.refs.stage.getBoundingClientRect();
        const ghost = token.cloneNode(true) as HTMLElement;
        ghost.classList.remove("member-token-floor", "is-incoming");
        ghost.classList.add("random-teams-drag-ghost");
        ghost.style.left = "0";
        ghost.style.top = "0";
        this.refs.dragLayer.append(ghost);
        token.classList.add("is-drag-origin");
        const teamTokenRects = this.captureTeamTokenRects(memberId);
        const teamDropRects = this.captureTeamDropRects();
        const floorRect = this.captureFloorRect();
        const floorTokenRects = this.captureFloorTokenRects(memberId);

        this.drag = {
            memberId,
            pointerId: event.pointerId,
            ghost,
            stageRect,
            teamTokenRects,
            teamDropRects,
            floorRect,
            floorTokenRects,
            offsetX: event.clientX - tokenRect.left,
            offsetY: event.clientY - tokenRect.top,
            lastClientX: event.clientX,
            lastClientY: event.clientY
        };

        if (perfEnabled) {
            this.dragPerf = {
                memberId,
                mode: this.state.result?.mode ?? null,
                startedAt: performance.now(),
                captureMs: performance.now() - captureStartedAt,
                teamDropRectCount: teamDropRects.length,
                teamTokenRectCount: Array.from(teamTokenRects.values()).reduce((total, rects) => total + rects.length, 0),
                floorTokenRectCount: floorTokenRects.length,
                frameCount: 0,
                totalFrameMs: 0,
                maxFrameMs: 0,
                slowFrameCount: 0,
                hoverChangeCount: 0
            };
        } else {
            this.dragPerf = null;
        }

        this.root.classList.add("is-random-teams-dragging");
        this.positionGhost(event.clientX, event.clientY, stageRect);
        window.addEventListener("pointermove", this.handlePointerMove);
        window.addEventListener("pointerup", this.handlePointerUp);
        window.addEventListener("pointercancel", this.handlePointerCancel);
    };

    private readonly handlePointerMove = (event: PointerEvent) => {
        if (!this.drag || !this.refs.stage) {
            return;
        }

        this.drag.lastClientX = event.clientX;
        this.drag.lastClientY = event.clientY;

        if (this.dragFrameId === null) {
            this.dragFrameId = window.requestAnimationFrame(this.flushDragMove);
        }
    };

    private readonly flushDragMove = () => {
        const perfEnabled = isRandomTeamsPerfEnabled() && this.dragPerf !== null;
        const frameStartedAt = perfEnabled ? performance.now() : 0;
        this.dragFrameId = null;

        if (!this.drag) {
            return;
        }

        this.positionGhost(this.drag.lastClientX, this.drag.lastClientY, this.drag.stageRect);

        const nextHighlightedTeamIndex = this.getDragHoverTeamIndex(this.drag.lastClientX, this.drag.lastClientY);

        if (this.highlightedTeamIndex !== nextHighlightedTeamIndex) {
            this.highlightedTeamIndex = nextHighlightedTeamIndex;
            if (this.dragPerf) {
                this.dragPerf.hoverChangeCount += 1;
            }
            this.syncDropTargetHighlight();
        }

        if (perfEnabled && this.dragPerf) {
            const frameMs = performance.now() - frameStartedAt;
            this.dragPerf.frameCount += 1;
            this.dragPerf.totalFrameMs += frameMs;
            this.dragPerf.maxFrameMs = Math.max(this.dragPerf.maxFrameMs, frameMs);
            if (frameMs >= RANDOM_TEAMS_SLOW_FRAME_MS) {
                this.dragPerf.slowFrameCount += 1;
            }
        }
    };

    private readonly handlePointerUp = (event: PointerEvent) => {
        if (!this.drag) {
            return;
        }

        const memberId = this.drag.memberId;
        if (this.dragFrameId !== null) {
            window.cancelAnimationFrame(this.dragFrameId);
            this.dragFrameId = null;
        }
        const releasePoint = {
            x: event.clientX,
            y: event.clientY
        };
        const destination =
            this.getDropDestination(event.clientX, event.clientY, memberId) ??
            ({
                zone: "floor",
                index: this.state.result?.floorIds.length ?? 0
            } satisfies DropDestination);

        this.finishDrag(memberId, destination, releasePoint);
    };

    private readonly handlePointerCancel = () => {
        if (!this.drag) {
            return;
        }

        const memberId = this.drag.memberId;
        if (this.dragFrameId !== null) {
            window.cancelAnimationFrame(this.dragFrameId);
            this.dragFrameId = null;
        }
        const fallbackDestination = this.findCurrentLocation(memberId);

        if (!fallbackDestination) {
            this.clearDragState();
            return;
        }

        const releasePoint = {
            x: this.drag.lastClientX,
            y: this.drag.lastClientY
        };

        this.finishDrag(memberId, fallbackDestination, releasePoint);
    };

    private finishDrag(memberId: number, destination: DropDestination, releasePoint: { x: number; y: number }) {
        const dragPerf = this.dragPerf;
        const moveStartedAt = dragPerf ? performance.now() : 0;
        this.moveMemberWithAnimation(memberId, destination, releasePoint);
        if (dragPerf) {
            const totalMs = performance.now() - dragPerf.startedAt;
            const averageFrameMs = dragPerf.frameCount > 0 ? dragPerf.totalFrameMs / dragPerf.frameCount : 0;
            const moveMs = performance.now() - moveStartedAt;
            logRandomTeamsPerf(
                `drag member=${memberId} mode=${dragPerf.mode ?? "none"} total=${roundRandomTeamsPerfMs(totalMs)}ms capture=${roundRandomTeamsPerfMs(
                    dragPerf.captureMs
                )}ms move=${roundRandomTeamsPerfMs(moveMs)}ms frames=${dragPerf.frameCount} avgFrame=${roundRandomTeamsPerfMs(
                    averageFrameMs
                )}ms maxFrame=${roundRandomTeamsPerfMs(dragPerf.maxFrameMs)}ms slow${RANDOM_TEAMS_SLOW_FRAME_MS}=${
                    dragPerf.slowFrameCount
                } hoverChanges=${dragPerf.hoverChangeCount} teamRects=${dragPerf.teamTokenRectCount} dropRects=${
                    dragPerf.teamDropRectCount
                } floorRects=${dragPerf.floorTokenRectCount} destination=${formatDropDestination(destination)}`
            );
        }
        this.dragPerf = null;
        this.clearDragState();
    }

    private schedulePresentationLayoutRefresh() {
        if (!this.presentationOpen || !this.state.result) {
            return;
        }

        window.requestAnimationFrame(() => {
            if (!this.presentationOpen || !this.state.result) {
                return;
            }

            this.syncPresentationScale();
            this.scheduleOrderPresentationRowRefreshIfNeeded();

            if (this.state.result.mode === "orders") {
                this.syncOrderConnectors();
            }

            window.requestAnimationFrame(() => {
                if (!this.presentationOpen || !this.state.result) {
                    return;
                }

                this.syncPresentationScale();
                this.scheduleOrderPresentationRowRefreshIfNeeded();

                if (this.state.result.mode === "orders") {
                    this.syncOrderConnectors();
                }
            });
        });
    }

    private clearDragState() {
        this.clearHoverState();
        this.detachDragListeners();
        if (this.dragFrameId !== null) {
            window.cancelAnimationFrame(this.dragFrameId);
            this.dragFrameId = null;
        }
        this.drag?.ghost.remove();
        this.drag = null;
        this.dragPerf = null;
        this.root.classList.remove("is-random-teams-dragging");
        this.root.querySelectorAll<HTMLElement>("[data-member-token].is-drag-origin").forEach((token) => {
            token.classList.remove("is-drag-origin");
        });
    }

    private detachDragListeners() {
        window.removeEventListener("pointermove", this.handlePointerMove);
        window.removeEventListener("pointerup", this.handlePointerUp);
        window.removeEventListener("pointercancel", this.handlePointerCancel);
    }

    private positionGhost(clientX: number, clientY: number, stageRect: DOMRect) {
        if (!this.drag) {
            return;
        }

        const x = clientX - stageRect.left - this.drag.offsetX;
        const y = clientY - stageRect.top - this.drag.offsetY;
        this.drag.ghost.style.transform = `translate(${x}px, ${y}px) rotate(4deg) scale(1.04)`;
    }

    private clearTransientState() {
        this.hideControlTooltip();
        this.launchFrameIds.forEach((frameId) => window.cancelAnimationFrame(frameId));
        this.launchFrameIds = [];
        this.activeAnimations.forEach((animation) => animation.cancel());
        this.activeAnimations.clear();
        this.activeTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
        this.activeTimeouts.clear();
        this.animatingMemberIds.clear();
        this.landedTeamMembers.clear();
        this.launchInProgress = false;
        this.root.classList.remove("is-random-teams-launching");
        this.refs.dragLayer?.replaceChildren();
        this.root.querySelectorAll(".random-teams-splash").forEach((splash) => splash.remove());
        this.stageCanvasRenderer?.clearTransientEffects();
        this.syncStageCanvas();
    }

    private resetGeneratedResult() {
        if (this.presentationOpen && this.state.result) {
            this.setPresentationOpen(false);
        }

        this.setLateIdPanelOpen("add", false);
        this.setLateIdPanelOpen("remove", false);
        this.clearTransientState();
        this.orderLayoutRefreshPending = false;
        this.highlightedTeamIndex = null;
        this.highlightedTeamElement = null;
        this.state.result = null;
        this.state.lastGeneratedSignature = null;
        this.state.resultCreatedAt = null;
    }

    private wipeGeneratedResult() {
        if (this.launchInProgress || !this.state.result) {
            return;
        }

        this.resetGeneratedResult();
        this.sync();
    }

    private trackAnimation(animation: Animation) {
        this.activeAnimations.add(animation);
        const clearAnimation = () => {
            this.activeAnimations.delete(animation);
        };
        animation.onfinish = clearAnimation;
        animation.oncancel = clearAnimation;
    }

    private computeValidation(): Validation {
        const teamCount = parsePositiveInteger(this.state.teamCount);
        const rosterText = this.state.rosterText ?? "";
        const isNamedRoster = this.state.rosterMode === "names";
        const parsedRoster = isNamedRoster ? parseRandomTeamsRosterText(rosterText) : null;
        const namedRoster = parsedRoster?.members ?? null;
        const memberCount = isNamedRoster
            ? namedRoster && namedRoster.length > 0
                ? namedRoster.length
                : null
            : parsePositiveInteger(this.state.memberCount);
        const invalidExcludedIndices = new Set<number>();
        const populatedEntries = this.state.excludedIds
            .map((value, index) => ({
                index,
                value: parsePositiveInteger(value)
            }))
            .filter((entry): entry is { index: number; value: number } => entry.value !== null);
        const duplicateMap = new Map<number, number[]>();

        populatedEntries.forEach((entry) => {
            const existing = duplicateMap.get(entry.value) ?? [];
            existing.push(entry.index);
            duplicateMap.set(entry.value, existing);
        });

        duplicateMap.forEach((indices) => {
            if (indices.length < 2) {
                return;
            }

            indices.forEach((index) => invalidExcludedIndices.add(index));
        });

        const memberIdByStudentNumber = new Map(
            (namedRoster ?? [])
                .filter((participant): participant is RandomTeamsRosterMember & { studentNumber: number } =>
                    participant.studentNumber !== null
                )
                .map((participant) => [participant.studentNumber, participant.id])
        );
        if (isNamedRoster) {
            populatedEntries.forEach((entry) => {
                if (!invalidExcludedIndices.has(entry.index) && !memberIdByStudentNumber.has(entry.value)) {
                    invalidExcludedIndices.add(entry.index);
                }
            });
        }
        const validExcludedEntries = populatedEntries.filter((entry) => !invalidExcludedIndices.has(entry.index));
        const validExcludedValues = validExcludedEntries.map((entry) => entry.value);
        const validExcludedIds = validExcludedEntries
            .map((entry) => (isNamedRoster ? memberIdByStudentNumber.get(entry.value) : entry.value))
            .filter((memberId): memberId is number => memberId !== undefined);
        const validExcludedIdSet = new Set(validExcludedIds);
        const rosterIds = namedRoster?.map((participant) => participant.id);
        const includedIds = memberCount === null
            ? []
            : (rosterIds ?? Array.from({ length: memberCount }, (_, index) => index + 1)).filter(
                  (memberId) => !validExcludedIdSet.has(memberId)
              );
        const currentSignature =
            teamCount !== null && memberCount !== null
                ? JSON.stringify({
                      mode: this.state.mode,
                      teamCount,
                      memberCount,
                      excludedIds: validExcludedValues,
                      roster: namedRoster?.map(({ id, name, studentNumber }) => ({ id, name, studentNumber })) ?? null
                  })
                : null;
        const canGenerate =
            currentSignature !== null &&
            invalidExcludedIndices.size === 0 &&
            (parsedRoster?.issues.length ?? 0) === 0 &&
            includedIds.length > 0;
        const isRepeatAction = Boolean(this.state.result) && this.state.lastGeneratedSignature === currentSignature;

        return {
            teamCount,
            memberCount,
            namedRoster,
            rosterIssues: parsedRoster?.issues ?? [],
            isNamedRoster,
            validExcludedIds,
            invalidExcludedIndices,
            includedIds,
            canGenerate,
            currentSignature,
            isRepeatAction,
            actionLabel: isRepeatAction
                  ? t("randomTeamsShuffleAgain")
                  : this.state.mode === "teams"
                    ? t("randomTeamsMakeNamedGroups", { groups: this.getGroupName(teamCount ?? 2) })
                    : t("randomTeamsMakeOrders"),
            helperText: invalidExcludedIndices.size > 0 ? t("randomTeamsExclusionError") : ""
        };
    }

    private sync() {
        this.state.excludedIds = ensureTrailingExclusionInput(this.state.excludedIds);
        writeStoredRandomTeamsSettings(this.state);
        const validation = this.computeValidation();
        this.syncExcludedFloorIds({ removeWhenHidden: true, validation });
        this.syncActiveSectionFocus();
        this.syncForm(validation);
        this.syncResults(validation);
    }

    private syncActiveSectionFocus() {
        const hasGeneratedResult = Boolean(this.state.result);
        this.root.classList.toggle("has-custom-group-name", Boolean(normalizeRandomTeamsName(this.state.groupName)));
        this.root.classList.toggle("has-random-teams-result", hasGeneratedResult);
        this.root.classList.toggle("has-named-roster", this.state.rosterMode === "names");
        this.refs.configCard?.classList.toggle("is-active-section", !hasGeneratedResult);
        this.refs.stageCard?.classList.toggle("is-active-section", hasGeneratedResult);
    }

    private syncForm(validation = this.computeValidation()) {
        const perfEnabled = isRandomTeamsPerfEnabled();
        const syncStartedAt = perfEnabled ? performance.now() : 0;
        const perfPhases: Record<string, number> = {};

        const fieldSyncStartedAt = perfEnabled ? performance.now() : 0;
        if (this.refs.teamCountInput && this.refs.teamCountInput.value !== this.state.teamCount) {
            this.refs.teamCountInput.value = this.state.teamCount;
        }

        if (this.refs.teamCountInput) {
            this.refs.teamCountInput.disabled = this.launchInProgress;
            this.refs.teamCountInput.setAttribute(
                "aria-label",
                this.state.mode === "teams" ? this.getGroupName(2, true) : t("randomTeamsModeOrder")
            );
        }

        const memberCountValue = validation.isNamedRoster
            ? String(validation.namedRoster?.length ?? "")
            : this.state.memberCount;
        if (this.refs.memberCountInput && this.refs.memberCountInput.value !== memberCountValue) {
            this.refs.memberCountInput.value = memberCountValue;
        }

        if (this.refs.memberCountInput) {
            this.refs.memberCountInput.disabled = this.launchInProgress;
            this.refs.memberCountInput.readOnly = false;
            this.refs.memberCountInput.removeAttribute("aria-readonly");
        }
        const memberCountSection = this.root.querySelector<HTMLElement>(".random-teams-member-count-section");
        if (memberCountSection) memberCountSection.hidden = validation.isNamedRoster;

        if (this.refs.namesTextarea) {
            if (this.refs.namesTextarea.value !== (this.state.rosterText ?? "")) {
                this.refs.namesTextarea.value = this.state.rosterText ?? "";
            }
            this.refs.namesTextarea.disabled = this.launchInProgress;
            this.refs.namesTextarea.setAttribute("aria-label", t("randomTeamsRosterInputLabel"));
            this.refs.namesTextarea.placeholder = t("randomTeamsRosterSample");
        }
        const hasRosterText = Boolean(this.state.rosterText?.trim());
        const isEditingNames = this.namesEditing || !hasRosterText || validation.rosterIssues.length > 0;
        const namesField = this.refs.namesTextarea?.closest<HTMLElement>(".random-teams-names-field");
        if (namesField) namesField.hidden = !isEditingNames;
        if (this.refs.namesEditButton) {
            this.refs.namesEditButton.hidden = !hasRosterText;
            this.refs.namesEditButton.innerHTML = `${isEditingNames ? "" : renderZiarcadiaIcon("edit")}<span>${escapeHtml(t(isEditingNames ? "randomTeamsRosterDone" : "randomTeamsRosterEdit"))}</span>`;
            this.refs.namesEditButton.disabled = this.launchInProgress || !hasRosterText || validation.rosterIssues.length > 0;
            this.refs.namesEditButton.setAttribute("aria-pressed", String(isEditingNames));
        }
        if (this.refs.namesModeLabel) {
            this.refs.namesModeLabel.textContent = t("randomTeamsSourceNames");
        }
        if (this.refs.namesPanel) this.refs.namesPanel.hidden = !validation.isNamedRoster;
        if (this.refs.namesImportButton) {
            this.refs.namesImportButton.innerHTML = `${renderZiarcadiaIcon("upload")}<span>${escapeHtml(t("randomTeamsImportNames"))}</span>`;
            this.refs.namesImportButton.title = t("randomTeamsRosterFileTypes");
            this.refs.namesImportButton.disabled = this.launchInProgress;
        }
        if (this.refs.namesFileInput) this.refs.namesFileInput.disabled = this.launchInProgress;
        if (this.refs.namesStatus) {
            const rosterIssue = validation.rosterIssues[0];
            const showIssue = hasRosterText && rosterIssue;
            this.refs.namesStatus.textContent = this.namesImportErrorKey
                ? t(this.namesImportErrorKey)
                : showIssue
                  ? t(getRandomTeamsRosterIssueKey(rosterIssue))
                  : "";
            this.refs.namesStatus.hidden = !this.refs.namesStatus.textContent;
            this.refs.namesStatus.setAttribute("aria-invalid", String(Boolean(this.namesImportErrorKey || showIssue)));
        }
        if (this.refs.namesPreview) {
            this.refs.namesPreview.innerHTML = (validation.namedRoster ?? [])
                .map((participant) => `
                    <div class="random-teams-roster-row" role="listitem">
                        <span class="random-teams-roster-number${participant.studentNumber === null ? " is-empty" : ""}"${participant.studentNumber === null ? " aria-hidden=\"true\"" : ` aria-label="${escapeHtml(t("randomTeamsStudentNumber", { number: participant.studentNumber }))}"`}>${participant.studentNumber ?? ""}</span>
                        <span class="random-teams-roster-name">${escapeHtml(participant.name)}</span>
                    </div>
                `)
                .join("");
            this.refs.namesPreview.hidden = isEditingNames;
        }
        const exclusionField = this.root.querySelector<HTMLElement>(".random-teams-exclusion-field");
        if (exclusionField) {
            exclusionField.hidden = validation.isNamedRoster;
        }
        this.refs.configCard?.classList.toggle("is-using-roster", validation.isNamedRoster);

        if (this.refs.countSuffix) {
            const isTeams = this.state.mode === "teams";
            this.refs.countSuffix.querySelector<HTMLElement>("[data-group-name-prefix]")!.textContent =
                isTeams ? t("randomTeamsNamePrefix") : this.getCountSuffix();
        }

        let pluralEnding = "";
        if (this.refs.groupNameInput) {
            const input = this.refs.groupNameInput;
            input.hidden = this.state.mode !== "teams";
            input.disabled = this.launchInProgress;
            input.setAttribute("aria-label", t("randomTeamsGroupName"));
            const groupName = this.getGroupName(validation.teamCount ?? 2);
            const singularName = this.getGroupName(1);
            // Keep additive endings outside the editor without splitting irregular plurals.
            const hasAdditiveEnding = groupName.startsWith(singularName);
            if (document.activeElement !== input) {
                input.value = hasAdditiveEnding ? singularName : groupName;
            }
            if (hasAdditiveEnding && input.value === singularName) {
                pluralEnding = groupName.slice(singularName.length);
            }
            const characterWidth = Array.from(input.value).reduce((width, char) => width + (/[^\x00-\x7f]/.test(char) ? 2 : 1), 0);
            input.style.width = `${Math.min(Math.max(characterWidth, 4), 22) + 2}ch`;
        }

        if (this.refs.countSuffix) {
            const isTeams = this.state.mode === "teams";
            this.refs.countSuffix.classList.toggle("has-plural-ending", isTeams && Boolean(pluralEnding));
            this.refs.countSuffix.querySelector<HTMLElement>("[data-group-name-ending]")!.textContent =
                isTeams ? pluralEnding + t("randomTeamsNameEnding") : "";
        }

        const teamsModeButton = this.refs.modeToggle?.querySelector<HTMLElement>('[data-random-teams-mode="teams"]');
        if (teamsModeButton) {
            teamsModeButton.textContent = this.getGroupName(2, true);
        }

        if (this.refs.memberPrefix) {
            this.refs.memberPrefix.textContent = this.getMemberPrefix();
        }

        if (this.refs.memberLabel) {
            this.refs.memberLabel.textContent = this.getMemberLabel();
        }

        if (this.refs.todayCount) {
            const todayCount = this.getTodayStudentCount(validation);
            const result = this.state.result;
            // Student IDs can have gaps after a late arrival. Count people, not
            // the largest ID, when showing the live roster's subtraction.
            const totalCount = result && todayCount !== null
                ? todayCount + result.excludedIds.filter((id) => id <= result.memberCount).length
                : validation.memberCount;
            const groupCount = result?.teams.length ?? validation.teamCount;
            const mode = result?.mode ?? this.state.mode;
            const groupSizes = result?.teams.map((team) => team.length);
            const minGroupSize = groupSizes ? Math.min(...groupSizes) : Math.floor((todayCount ?? 0) / (groupCount ?? 1));
            const maxGroupSize = groupSizes ? Math.max(...groupSizes) : Math.ceil((todayCount ?? 0) / (groupCount ?? 1));
            const groupSize = minGroupSize === maxGroupSize ? String(minGroupSize) : `${minGroupSize}–${maxGroupSize}`;
            const groupLabel = mode === "teams"
                ? t("randomTeamsNamedGroupLabel", { groups: this.getGroupName(groupCount ?? 2) })
                : t(groupCount === 1 ? "randomOrdersOneGroupLabel" : "randomOrdersGroupLabel");
            const renderSummaryLabel = (label: string) => label.split(" ")
                .map((word) => `<span>${escapeHtml(word)}</span>`).join(" ");
            this.refs.todayCount.classList.toggle("has-wide-counts", (todayCount ?? 0) >= 100 || (groupCount ?? 0) >= 10);
            this.refs.todayCount.classList.toggle("has-long-group-name", mode === "teams" && this.getGroupName(groupCount ?? 2).length > 8);
            this.refs.todayCount.hidden = todayCount === null;
            this.refs.todayCount.setAttribute("aria-label", todayCount === null ? "" : t("randomTeamsTodayStudentCount", { count: todayCount }));
            this.refs.todayCount.innerHTML = todayCount === null || totalCount === null ? "" : `
                <div class="random-teams-summary-half random-teams-attendance-summary">
                    ${renderZiarcadiaIcon("players", { className: "random-teams-summary-icon" })}
                    <div class="random-teams-summary-headline">
                        <strong class="random-teams-today-number">${todayCount}</strong>
                        <span class="random-teams-today-label">${renderSummaryLabel(t("randomTeamsHereToday"))}</span>
                    </div>
                    ${validation.isNamedRoster ? "" : `<span class="random-teams-attendance-breakdown">
                        <span><b>${totalCount}</b> ${escapeHtml(t("randomTeamsAttendanceTotal"))}</span>
                        <span class="random-teams-attendance-minus">−</span>
                        <span><b>${Math.max(0, totalCount - todayCount)}</b> ${escapeHtml(t("randomTeamsAttendanceMissing"))}</span>
                    </span>`}
                </div>
                ${groupCount === null ? "" : `<div class="random-teams-summary-half random-teams-group-summary">
                    ${renderZiarcadiaIcon("teams", { className: "random-teams-summary-icon" })}
                    <div class="random-teams-summary-headline">
                        <strong class="random-teams-group-number">${groupCount}</strong>
                        <span class="random-teams-group-label">${renderSummaryLabel(groupLabel)}</span>
                    </div>
                    <span class="random-teams-group-size">${escapeHtml(mode === "teams"
                        ? t("randomTeamsNamedGroupSize", { size: groupSize, group: this.getGroupName(1) })
                        : t("randomOrdersGroupSize", { size: groupSize }))}</span>
                </div>`}`;
            if (this.refs.stageAttendance) {
                this.refs.stageAttendance.hidden = !result || todayCount === null;
                this.refs.stageAttendance.innerHTML = todayCount === null ? "" :
                    `<strong>${todayCount}</strong> ${escapeHtml(t("randomTeamsHereToday"))}`;
            }
        }
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "fields", fieldSyncStartedAt);
        }

        const toggleSyncStartedAt = perfEnabled ? performance.now() : 0;
        if (this.refs.modeToggle) {
            this.refs.modeToggle.disabled = this.launchInProgress;
            this.refs.modeToggle.setAttribute("aria-checked", String(this.state.mode === "orders"));
            this.refs.modeToggle.setAttribute("aria-label", `${this.getGroupName(2, true)} / ${t("randomTeamsModeOrder")}`);
            this.refs.modeToggle.querySelectorAll<HTMLElement>("[data-random-teams-mode]").forEach((segment) => {
                segment.classList.toggle("selected", segment.dataset.randomTeamsMode === this.state.mode);
            });
        }
        if (this.refs.rosterModeToggle) {
            this.refs.rosterModeToggle.disabled = this.launchInProgress;
            this.refs.rosterModeToggle.setAttribute("aria-checked", String(this.state.rosterMode === "names"));
            this.refs.rosterModeToggle.setAttribute("aria-label", `${t("randomTeamsSourceIds")} / ${t("randomTeamsSourceNames")}`);
            this.refs.rosterModeToggle.querySelectorAll<HTMLElement>("[data-random-teams-roster-mode]").forEach((segment) => {
                segment.classList.toggle("selected", segment.dataset.randomTeamsRosterMode === this.state.rosterMode);
            });
        }
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "toggles", toggleSyncStartedAt);
        }

        if (this.refs.exclusionList) {
            const exclusionMarkupStartedAt = perfEnabled ? performance.now() : 0;
            this.refs.exclusionList.innerHTML = this.state.excludedIds
                .map(
                    (value, index) => `
                        <div
                            class="random-teams-exclusion-pill ${validation.invalidExcludedIndices.has(index) ? "is-invalid" : ""}"
                            data-exclusion-pill-index="${index}"
                        >
                            <input
                                type="text"
                                inputmode="numeric"
                                enterkeyhint="done"
                                pattern="[0-9]*"
                                autocomplete="off"
                                placeholder="${escapeHtml(t("studentIdPlaceholder"))}"
                                value="${escapeHtml(value)}"
                                data-random-teams-exclusion-input
                                data-exclusion-index="${index}"
                                ${this.launchInProgress ? "disabled" : ""}
                            />
                            ${
                                value
                                    ? `<button
                                            class="random-teams-exclusion-remove"
                                            type="button"
                                            data-remove-exclusion-index="${index}"
                                            aria-label="${escapeHtml(t("removeItem"))}"
                                            ${this.launchInProgress ? "disabled" : ""}
                                        >&times;</button>`
                                    : ""
                            }
                        </div>
                    `
                )
                .join("");
            if (perfEnabled) {
                recordRandomTeamsPerfPhase(perfPhases, "exclusions", exclusionMarkupStartedAt);
            }
        }

        const controlSyncStartedAt = perfEnabled ? performance.now() : 0;
        if (this.refs.helper) {
            this.refs.helper.textContent = validation.helperText;
            this.refs.helper.hidden = validation.helperText.length === 0;
        }

        if (this.refs.startButton) {
            this.refs.startButton.textContent = this.launchInProgress ? t("randomTeamsShuffling") : validation.actionLabel;
            this.refs.startButton.disabled = !validation.canGenerate || this.launchInProgress;
            this.refs.startButton.classList.toggle("is-repeat-action", validation.isRepeatAction);
            this.refs.startButton.classList.toggle("button-accent-teal", validation.isRepeatAction);
            this.refs.startButton.classList.toggle("is-shuffling", this.launchInProgress);
            this.refs.startButton.setAttribute("aria-busy", String(this.launchInProgress));
        }

        if (this.refs.stage) {
            this.refs.stage.setAttribute("aria-busy", String(this.launchInProgress));
        }

        if (this.refs.stageStatus) {
            this.refs.stageStatus.textContent = this.launchInProgress
                ? t("randomTeamsShuffling")
                : this.state.result
                  ? t("randomTeamsReadyTitle")
                  : "";
        }

        this.root.classList.toggle("is-random-teams-motion-muted", !this.areAnimationsEnabled());
        this.syncFullscreenButton(validation);
        this.syncSoundButton();
        this.syncAnimationButton();
        this.syncExcludedFloorButton();
        this.syncWipeButton();
        this.syncLateIdControls();
        this.refreshControlTooltip();
        this.syncMobileExclusionSubmitButton();
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "controls", controlSyncStartedAt);
        }

        const stageCanvasStartedAt = perfEnabled ? performance.now() : 0;
        this.syncStageCanvas();
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "canvas", stageCanvasStartedAt);
            const totalMs = performance.now() - syncStartedAt;
            if (totalMs >= 6) {
                const phases = formatRandomTeamsPerfPhases(perfPhases);
                logRandomTeamsPerf(
                    `sync-form total=${roundRandomTeamsPerfMs(totalMs)}ms mode=${this.state.mode} result=${
                        this.state.result?.mode ?? "preview"
                    } exclusions=${Math.max(this.state.excludedIds.length - 1, 0)} launch=${
                        this.launchInProgress ? 1 : 0
                    }${phases ? ` phases=${phases}` : ""}`
                );
            }
        }
    }

    private syncResults(validation = this.computeValidation()) {
        this.syncActiveSectionFocus();

        if (!this.refs.board) {
            return;
        }

        const perfEnabled = isRandomTeamsPerfEnabled();
        const syncStartedAt = perfEnabled ? performance.now() : 0;
        const perfPhases: Record<string, number> = {};
        const result = this.state.result;
        writeStoredRandomTeamsResultSnapshot(this.state, this.showExcludedOnFloor);

        if (!result) {
            this.refs.stage?.classList.remove("has-order-result");
            this.refs.stage?.classList.remove("has-dense-team-result");
            this.refs.stage?.classList.remove("has-named-roster-result");
            this.refs.board.classList.remove("has-random-teams-result");
            this.refs.board.classList.add("has-random-teams-preview");
            const previewMarkupStartedAt = perfEnabled ? performance.now() : 0;
            this.refs.board.innerHTML = this.renderPreviewMarkup(validation);
            if (perfEnabled) {
                recordRandomTeamsPerfPhase(perfPhases, "preview", previewMarkupStartedAt);
            }
            const presentationScaleStartedAt = perfEnabled ? performance.now() : 0;
            this.syncPresentationScale();
            if (perfEnabled) {
                recordRandomTeamsPerfPhase(perfPhases, "presentation", presentationScaleStartedAt);
                const totalMs = performance.now() - syncStartedAt;
                if (totalMs >= 4) {
                    const phases = formatRandomTeamsPerfPhases(perfPhases);
                    logRandomTeamsPerf(
                        `sync-results mode=preview total=${roundRandomTeamsPerfMs(totalMs)}ms${phases ? ` phases=${phases}` : ""}`
                    );
                }
            }
            return;
        }

        const markupStartedAt = perfEnabled ? performance.now() : 0;
        const teamDensityClass = result.mode === "orders" ? "" : this.getTeamDensityClass(result);
        this.refs.board.classList.remove("has-random-teams-preview");
        this.refs.board.classList.add("has-random-teams-result");
        this.refs.stage?.classList.toggle("has-named-roster-result", Boolean(result.participants?.length));
        this.refs.stage?.classList.toggle("has-order-result", result.mode === "orders");
        this.refs.stage?.classList.toggle(
            "has-dense-team-result",
            result.mode === "teams" && teamDensityClass !== "is-roomy-team-layout"
        );

        const teamsMarkup = `
            <div class="random-teams-teams-grid ${result.mode === "orders" ? "is-order-mode" : teamDensityClass}">
                ${result.teams.map((members, teamIndex) => this.renderTeamMarkup(teamIndex, members)).join("")}
            </div>
        `;
        const floorMarkup = this.renderFloorTrayMarkup(result);
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "markup", markupStartedAt);
        }

        const domWriteStartedAt = perfEnabled ? performance.now() : 0;
        this.refs.board.innerHTML =
            result.mode === "orders"
                ? `
                    <div class="random-teams-results-layout is-order-mode">
                        ${floorMarkup}
                        ${teamsMarkup}
                    </div>
                `
                : `
                    ${teamsMarkup}
                    ${floorMarkup}
                `;
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "dom", domWriteStartedAt);
        }

        const dropHighlightStartedAt = perfEnabled ? performance.now() : 0;
        this.syncDropTargetHighlight();
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "drop-highlight", dropHighlightStartedAt);
        }
        const presentationScaleStartedAt = perfEnabled ? performance.now() : 0;
        this.syncPresentationScale();
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "presentation", presentationScaleStartedAt);
        }
        if (result.mode === "orders") {
            const connectorsStartedAt = perfEnabled ? performance.now() : 0;
            this.syncOrderConnectorsAfterLayout();
            if (perfEnabled) {
                recordRandomTeamsPerfPhase(perfPhases, "connector-schedule", connectorsStartedAt);
            }
        }
        const stageCanvasStartedAt = perfEnabled ? performance.now() : 0;
        this.syncStageCanvas();
        if (result.mode === "orders") {
            this.logOrderPresentationLayoutSnapshot("sync-results");
        }
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "canvas", stageCanvasStartedAt);
            const totalMembers = result.floorIds.length + result.teams.reduce((total, members) => total + members.length, 0);
            const phases = formatRandomTeamsPerfPhases(perfPhases);
            logRandomTeamsPerf(
                `sync-results mode=${result.mode} total=${roundRandomTeamsPerfMs(performance.now() - syncStartedAt)}ms teams=${
                    result.teamCount
                } members=${totalMembers} floor=${result.floorIds.length} presentation=${this.presentationOpen ? 1 : 0}${
                    phases ? ` phases=${phases}` : ""
                }`
            );
        }
    }

    private syncOrderConnectorsAfterLayout() {
        this.syncOrderConnectors();
        window.requestAnimationFrame(() => {
            this.syncPresentationScale();
            this.syncOrderConnectors();
            window.requestAnimationFrame(() => {
                this.syncPresentationScale();
                this.syncOrderConnectors();
            });
        });
    }

    private logOrderPresentationLayoutSnapshot(reason: string) {
        if (!this.presentationOpen || this.state.result?.mode !== "orders" || !this.refs.stage || !this.refs.board) {
            return;
        }

        const stageRect = this.refs.stage.getBoundingClientRect();
        const orders = this.refs.board.querySelector<HTMLElement>(".random-teams-teams-grid.is-order-mode");
        const rowSizes = Array.from(this.root.querySelectorAll<HTMLElement>("[data-order-coil]"))
            .map((coil) => coil.dataset.orderRowSize ?? "")
            .filter(Boolean);
        const uniqueRowSizes = [...new Set(rowSizes)].join(",") || "none";
        const tokens = Array.from(this.root.querySelectorAll<HTMLElement>('[data-member-token][data-zone="team"]'));
        let tokensOutsideCards = 0;
        let tokensOutsideRows = 0;
        let maxCardRightOverflow = 0;
        let maxRowRightOverflow = 0;
        let maxStageRightOverflow = 0;

        tokens.forEach((token) => {
            const tokenRect = token.getBoundingClientRect();
            const card = token.closest<HTMLElement>("[data-team-box]");
            const row = token.closest<HTMLElement>("[data-order-row]");

            maxStageRightOverflow = Math.max(maxStageRightOverflow, tokenRect.right - stageRect.right);

            if (card) {
                const cardRect = card.getBoundingClientRect();
                const cardRightOverflow = tokenRect.right - cardRect.right;
                maxCardRightOverflow = Math.max(maxCardRightOverflow, cardRightOverflow);

                if (tokenRect.left < cardRect.left - 0.5 || tokenRect.right > cardRect.right + 0.5) {
                    tokensOutsideCards += 1;
                }
            }

            if (row) {
                const rowRect = row.getBoundingClientRect();
                const rowRightOverflow = tokenRect.right - rowRect.right;
                maxRowRightOverflow = Math.max(maxRowRightOverflow, rowRightOverflow);

                if (tokenRect.left < rowRect.left - 0.5 || tokenRect.right > rowRect.right + 0.5) {
                    tokensOutsideRows += 1;
                }
            }
        });

        const visualViewport = window.visualViewport;
        const visualViewportText = visualViewport
            ? `${Math.round(visualViewport.width)}x${Math.round(visualViewport.height)}@${roundRandomTeamsPerfMs(visualViewport.scale)}`
            : "none";
        const orderOverflowPx = orders ? Math.max(0, orders.scrollWidth - orders.clientWidth) : 0;
        const boardOverflowX = Math.max(0, this.refs.board.scrollWidth - this.refs.board.clientWidth);
        const boardOverflowY = Math.max(0, this.refs.board.scrollHeight - this.refs.board.clientHeight);
        const signature = [
            window.innerWidth,
            window.innerHeight,
            visualViewportText,
            roundRandomTeamsPerfMs(window.devicePixelRatio || 1),
            roundRandomTeamsPerfMs(this.getOrderLayoutScale()),
            Math.round(stageRect.width),
            Math.round(stageRect.height),
            this.refs.board.clientWidth,
            this.refs.board.clientHeight,
            this.refs.board.scrollWidth,
            this.refs.board.scrollHeight,
            orders?.clientWidth ?? 0,
            orders?.clientHeight ?? 0,
            orders?.scrollWidth ?? 0,
            orders?.scrollHeight ?? 0,
            uniqueRowSizes,
            tokens.length,
            tokensOutsideCards,
            tokensOutsideRows,
            roundRandomTeamsPerfMs(Math.max(0, maxCardRightOverflow)),
            roundRandomTeamsPerfMs(Math.max(0, maxRowRightOverflow)),
            roundRandomTeamsPerfMs(Math.max(0, maxStageRightOverflow)),
            orderOverflowPx,
            boardOverflowX,
            boardOverflowY,
            document.fullscreenElement ? 1 : 0,
            this.isCompactMobileViewport() ? 1 : 0,
            this.isShortLandscapeViewport() ? 1 : 0
        ].join("|");

        if (signature === this.lastOrderPresentationLayoutLogSignature) {
            return;
        }

        this.lastOrderPresentationLayoutLogSignature = signature;
        logRandomTeamsPerf(
            `order-layout reason=${reason} viewport=${window.innerWidth}x${window.innerHeight} vv=${visualViewportText} dpr=${roundRandomTeamsPerfMs(
                window.devicePixelRatio || 1
            )} scale=${roundRandomTeamsPerfMs(this.getOrderLayoutScale())} stage=${Math.round(stageRect.width)}x${Math.round(
                stageRect.height
            )} board=${this.refs.board.clientWidth}x${this.refs.board.clientHeight}/${this.refs.board.scrollWidth}x${
                this.refs.board.scrollHeight
            } orders=${orders?.clientWidth ?? 0}x${orders?.clientHeight ?? 0}/${orders?.scrollWidth ?? 0}x${
                orders?.scrollHeight ?? 0
            } rows=${uniqueRowSizes} tokens=${tokens.length} outCard=${tokensOutsideCards} outRow=${tokensOutsideRows} cardRight=${roundRandomTeamsPerfMs(
                Math.max(0, maxCardRightOverflow)
            )} rowRight=${roundRandomTeamsPerfMs(Math.max(0, maxRowRightOverflow))} stageRight=${roundRandomTeamsPerfMs(
                Math.max(0, maxStageRightOverflow)
            )} orderOverflow=${Math.round(orderOverflowPx)} boardOverflow=${Math.round(boardOverflowX)}x${Math.round(
                boardOverflowY
            )} nativeFullscreen=${document.fullscreenElement ? 1 : 0} compact=${this.isCompactMobileViewport() ? 1 : 0} shortLandscape=${
                this.isShortLandscapeViewport() ? 1 : 0
            }`
        );
    }

    private renderFloorTrayMarkup(result: RandomTeamsResult): string {
        const isOrderMode = result.mode === "orders";
        const canOverflowOrderFloor = isOrderMode && result.floorIds.length > 0;

        return `
            <section class="random-teams-floor-tray ${isOrderMode ? "is-order-mode" : ""} ${
                canOverflowOrderFloor ? "is-scrollable" : ""
            }" data-floor-tray>
                <div class="random-teams-floor-head">
                    <p class="random-teams-floor-name">${escapeHtml(isOrderMode ? t("randomTeamsFinished") : this.state.groupName ? t("randomTeamsNamedFloor", { group: this.getGroupName(1) }) : t("randomTeamsFloor"))}</p>
                    <span class="setup-meta">${escapeHtml(String(result.floorIds.length))}</span>
                </div>
                <div class="random-teams-floor-members ${isOrderMode ? "is-order-mode" : ""}">
                    ${
                        result.floorIds.length > 0
                            ? result.floorIds
                                  .map((memberId, index) =>
                                      this.renderMemberToken(memberId, {
                                          zone: "floor",
                                          index
                                      })
                                  )
                                  .join("")
                            : `<p class="random-teams-floor-empty">${escapeHtml(this.getDropHereLabel())}</p>`
                    }
                </div>
            </section>
        `;
    }

    private syncPresentationScale() {
        if (!this.refs.stage) {
            return;
        }

        const result = this.state.result;
        const perfEnabled = isRandomTeamsPerfEnabled() && this.presentationOpen;
        const syncStartedAt = perfEnabled ? performance.now() : 0;
        const perfPhases: Record<string, number> = {};
        let iterationCount = 0;
        let measuredContentWidth = 0;
        let measuredContentHeight = 0;
        let overflowRight = 0;
        let overflowBottom = 0;

        if (!this.presentationOpen || !result || !this.refs.board) {
            if (this.refs.stage.style.getPropertyValue("--random-teams-presentation-scale")) {
                this.refs.stage.style.removeProperty("--random-teams-presentation-scale");
            }
            if (this.refs.stage.scrollLeft !== 0) {
                this.refs.stage.scrollLeft = 0;
            }
            if (this.refs.stage.scrollTop !== 0) {
                this.refs.stage.scrollTop = 0;
            }
            this.presentationScaleCache = null;
            return;
        }

        this.refs.stage.scrollLeft = 0;
        this.refs.stage.scrollTop = 0;

        const cacheSignature = this.getPresentationScaleSignature(result);
        const cachedScale =
            cacheSignature && this.presentationScaleCache?.signature === cacheSignature
                ? clampNumber(this.presentationScaleCache.scale, this.getMinimumPresentationScale(), this.getMaximumPresentationScale())
                : null;

        if (cachedScale !== null) {
            this.refs.stage.style.setProperty("--random-teams-presentation-scale", cachedScale.toFixed(4));
            this.scheduleOrderPresentationRowRefreshIfNeeded();

            if (perfEnabled) {
                const totalMs = performance.now() - syncStartedAt;
                logRandomTeamsPerf(
                    `presentation-scale total=${roundRandomTeamsPerfMs(totalMs)}ms mode=${result.mode} cached=1 scale=${cachedScale.toFixed(4)}`
                );
            }
            return;
        }

        let scale = this.getPresentationScaleSeed();

        const fitLoopStartedAt = perfEnabled ? performance.now() : 0;
        for (let iteration = 0; iteration < 5; iteration += 1) {
            iterationCount += 1;
            this.refs.stage.style.setProperty("--random-teams-presentation-scale", scale.toFixed(4));

            const stageStyle = window.getComputedStyle(this.refs.stage);
            const horizontalPadding = Number.parseFloat(stageStyle.paddingLeft) + Number.parseFloat(stageStyle.paddingRight);
            const verticalPadding = Number.parseFloat(stageStyle.paddingTop) + Number.parseFloat(stageStyle.paddingBottom);
            const edgeReserve = this.getPresentationEdgeReserve(result);
            const availableWidth = Math.max(1, this.refs.stage.clientWidth - horizontalPadding);
            const availableHeight = Math.max(1, this.refs.stage.clientHeight - verticalPadding - edgeReserve);
            const contentSize = this.measurePresentationContentSize(result);
            measuredContentWidth = contentSize.width;
            measuredContentHeight = contentSize.height;
            const widthFit = availableWidth / contentSize.width;
            const heightFit = availableHeight / contentSize.height;
            // Team presentation already spans the full board width on desktop, so
            // the real fullscreen headroom signal is usually the unused vertical space.
            // Use that to grow carefully, then let the overflow pass trim anything risky.
            const fitScale =
                widthFit < 0.999 || heightFit < 0.999
                    ? Math.min(widthFit, heightFit)
                    : this.shouldUseVerticalPresentationUpscale(result)
                      ? heightFit
                      : Math.min(widthFit, heightFit);
            const adjustedFitScale =
                fitScale < 0.999
                    ? fitScale * 0.992
                    : fitScale > 1.001
                      ? 1 + (fitScale - 1) * PRESENTATION_UPSCALE_DAMPING
                      : 1;
            const nextScale = clampNumber(
                scale * adjustedFitScale,
                this.getMinimumPresentationScale(),
                this.getMaximumPresentationScale()
            );

            if (Math.abs(nextScale - scale) < 0.01) {
                scale = nextScale;
                break;
            }

            scale = nextScale;
        }
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "fit-loop", fitLoopStartedAt);
        }

        scale = Math.min(scale, this.getMaximumPresentationScale());
        this.refs.stage.style.setProperty("--random-teams-presentation-scale", scale.toFixed(4));

        const overflowStartedAt = perfEnabled ? performance.now() : 0;
        const stageOverflow = this.measurePresentationStageOverflow();
        overflowRight = stageOverflow.right;
        overflowBottom = stageOverflow.bottom;

        if (stageOverflow.right > 1 || stageOverflow.bottom > 1) {
            const stageRect = this.refs.stage.getBoundingClientRect();
            const widthFit =
                stageOverflow.right > 0 ? Math.max(0.2, (stageRect.width - stageOverflow.right - 2) / Math.max(stageRect.width, 1)) : 1;
            const heightFit =
                stageOverflow.bottom > 0
                    ? Math.max(0.2, (stageRect.height - stageOverflow.bottom - 2) / Math.max(stageRect.height, 1))
                    : 1;
            const overflowAdjustedScale = clampNumber(
                scale * Math.min(widthFit, heightFit) * 0.994,
                this.getMinimumPresentationScale(),
                this.getMaximumPresentationScale()
            );

            if (overflowAdjustedScale < scale - 0.003) {
                scale = overflowAdjustedScale;
                this.refs.stage.style.setProperty("--random-teams-presentation-scale", scale.toFixed(4));
            }
        }
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "overflow", overflowStartedAt);
        }

        this.refs.stage.scrollLeft = 0;
        this.refs.stage.scrollTop = 0;
        if (cacheSignature) {
            this.presentationScaleCache = {
                signature: cacheSignature,
                scale
            };
        } else {
            this.presentationScaleCache = null;
        }
        const rowRefreshStartedAt = perfEnabled ? performance.now() : 0;
        this.scheduleOrderPresentationRowRefreshIfNeeded();
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "row-refresh", rowRefreshStartedAt);
            const totalMs = performance.now() - syncStartedAt;
            const phases = formatRandomTeamsPerfPhases(perfPhases);
            logRandomTeamsPerf(
                `presentation-scale total=${roundRandomTeamsPerfMs(totalMs)}ms mode=${result.mode} iter=${iterationCount} scale=${
                    scale.toFixed(4)
                } content=${Math.round(measuredContentWidth)}x${Math.round(measuredContentHeight)} overflow=${roundRandomTeamsPerfMs(
                    overflowRight
                )}x${roundRandomTeamsPerfMs(overflowBottom)}${phases ? ` phases=${phases}` : ""}`
            );
        }
    }

    private getPresentationScaleSeed(): number {
        const inlineScale = Number.parseFloat(this.refs.stage?.style.getPropertyValue("--random-teams-presentation-scale") ?? "");
        const scale = this.presentationScaleCache?.scale ?? inlineScale;

        return clampNumber(
            Number.isFinite(scale) && scale > 0 ? scale : 1,
            this.getMinimumPresentationScale(),
            this.getMaximumPresentationScale()
        );
    }

    private getPresentationScaleSignature(result: RandomTeamsResult): string | null {
        if (!this.refs.stage || !this.refs.board) {
            return null;
        }

        const teamSizes = result.teams.map((members) => members.length).join(".");
        const orderSlots = result.mode === "orders" ? result.orderSlotCounts.join(".") : "";
        const renderedOrderRowSize = result.mode === "orders" ? this.getRenderedOrderRowSize() ?? 0 : 0;

        return [
            result.mode,
            this.refs.stage.clientWidth,
            this.refs.stage.clientHeight,
            this.refs.board.clientWidth,
            this.isCompactMobileViewport() ? 1 : 0,
            this.isShortLandscapeViewport() ? 1 : 0,
            result.teamCount,
            result.memberCount,
            result.floorIds.length,
            teamSizes,
            orderSlots,
            renderedOrderRowSize,
            this.showExcludedOnFloor ? 1 : 0,
            this.launchInProgress ? 1 : 0
        ].join("|");
    }

    private getMinimumPresentationScale(): number {
        // Dense named rosters should scroll before names become unreadable.
        if (this.state.result?.participants?.length) {
            return this.isCompactMobileViewport() ? 0.68 : 0.82;
        }
        if (!this.isCompactMobileViewport()) {
            return 0.58;
        }

        const result = this.state.result;

        if (result?.mode === "teams") {
            const largestTeamSize = Math.max(...result.teams.map((members) => members.length), 0);

            if (result.teamCount >= 10) {
                return 0.28;
            }

            if (largestTeamSize >= 24) {
                return 0.32;
            }

            if (result.teamCount >= 8 || largestTeamSize >= 16) {
                return 0.34;
            }

            if (result.teamCount >= 6) {
                return 0.38;
            }

            if (result.floorIds.length >= 6 || result.memberCount >= 20) {
                return 0.38;
            }
        }

        return 0.42;
    }

    private getMaximumPresentationScale(): number {
        if (this.state.result?.mode === "teams" && !this.isCompactMobileViewport() && !this.isShortLandscapeViewport()) {
            return DESKTOP_TEAM_PRESENTATION_MAX_SCALE;
        }

        if (this.state.result?.mode === "orders" && !this.isCompactMobileViewport() && this.isShortLandscapeViewport()) {
            return 0.72;
        }

        return this.isCompactMobileViewport() && this.state.result?.mode === "orders"
            ? (this.state.result.participants?.length ? 0.9 : 0.64) : 1;
    }

    private shouldUseVerticalPresentationUpscale(result: RandomTeamsResult): boolean {
        return result.mode === "teams" && !this.isCompactMobileViewport() && !this.isShortLandscapeViewport();
    }

    private measurePresentationContentSize(result: RandomTeamsResult): { width: number; height: number } {
        if (!this.refs.board) {
            return { width: 1, height: 1 };
        }

        if (result.mode === "orders") {
            const orderTeamsGrid = this.refs.board.querySelector<HTMLElement>(".random-teams-teams-grid.is-order-mode");

            return {
                width: Math.max(1, this.refs.board.scrollWidth, orderTeamsGrid?.scrollWidth ?? 0),
                height: Math.max(1, this.refs.board.scrollHeight, orderTeamsGrid?.scrollHeight ?? 0)
            };
        }

        const boardRect = this.refs.board.getBoundingClientRect();
        let occupiedWidth = 0;
        let occupiedHeight = 0;

        Array.from(this.refs.board.children).forEach((child) => {
            if (!(child instanceof HTMLElement)) {
                return;
            }

            const rect = child.getBoundingClientRect();
            occupiedWidth = Math.max(occupiedWidth, rect.right - boardRect.left);
            occupiedHeight = Math.max(occupiedHeight, rect.bottom - boardRect.top);
        });

        return {
            width: Math.max(1, occupiedWidth || this.refs.board.clientWidth),
            height: Math.max(1, occupiedHeight || this.refs.board.clientHeight)
        };
    }

    private getPresentationEdgeReserve(result: RandomTeamsResult): number {
        if (result.mode === "orders" || result.floorIds.length === 0) {
            return 0;
        }

        const largestTeamSize = Math.max(...result.teams.map((members) => members.length), 0);

        if (this.isCompactMobileViewport()) {
            if (largestTeamSize >= 20 || result.memberCount >= 80) {
                return 62;
            }

            return 42;
        }

        if (window.matchMedia("(orientation: landscape) and (max-height: 820px)").matches) {
            return largestTeamSize >= 20 ? 28 : 18;
        }

        return 12;
    }

    private measurePresentationStageOverflow(): { right: number; bottom: number } {
        if (!this.presentationOpen || !this.refs.stage) {
            return { right: 0, bottom: 0 };
        }

        const stageRect = this.refs.stage.getBoundingClientRect();
        let rightOverflow = 0;
        let bottomOverflow = 0;

        this.root.querySelectorAll<HTMLElement>("[data-team-box], [data-floor-tray]").forEach((element) => {
            const rect = element.getBoundingClientRect();
            rightOverflow = Math.max(rightOverflow, rect.right - stageRect.right);
            bottomOverflow = Math.max(bottomOverflow, rect.bottom - stageRect.bottom);
        });

        if (this.refs.board) {
            rightOverflow = Math.max(rightOverflow, this.refs.board.scrollWidth - this.refs.board.clientWidth);
            bottomOverflow = Math.max(bottomOverflow, this.refs.board.scrollHeight - this.refs.board.clientHeight);
        }

        return {
            right: Math.max(0, rightOverflow),
            bottom: Math.max(0, bottomOverflow)
        };
    }

    private renderPreviewMarkup(validation: Validation): string {
        if (
            validation.canGenerate &&
            validation.teamCount !== null &&
            validation.memberCount !== null &&
            validation.includedIds.length > 0
        ) {
            this.lastPreviewSnapshot = {
                mode: this.state.mode,
                teamCount: validation.teamCount,
                includedCount: validation.includedIds.length
            };
        }

        const previewSnapshot =
            validation.canGenerate && validation.teamCount !== null && validation.memberCount !== null && validation.includedIds.length > 0
                ? {
                      mode: this.state.mode,
                      teamCount: validation.teamCount,
                      includedCount: validation.includedIds.length
                  }
                : this.lastPreviewSnapshot;

        if (!previewSnapshot || previewSnapshot.teamCount <= 0 || previewSnapshot.includedCount <= 0) {
            return `
                <div class="random-teams-stage-empty"></div>
            `;
        }

        const previewCounts = this.getPreviewCounts(previewSnapshot.teamCount, previewSnapshot.includedCount);
        const isOrderMode = previewSnapshot.mode === "orders";

        return `
            <div class="random-teams-stage-empty">
                <div
                    class="random-teams-preview-grid ${isOrderMode ? "is-order-mode" : ""}"
                    aria-hidden="true"
                >
                    ${previewCounts.map((count, teamIndex) => this.renderPreviewTeamMarkup(teamIndex, count, isOrderMode)).join("")}
                </div>
            </div>
        `;
    }

    private getPreviewCounts(teamCount: number, includedCount: number): number[] {
        const baseCount = Math.floor(includedCount / teamCount);
        const remainder = includedCount % teamCount;

        return Array.from({ length: teamCount }, (_, index) => baseCount + (index < remainder ? 1 : 0));
    }

    private renderPreviewTeamMarkup(teamIndex: number, count: number, isOrderMode: boolean): string {
        const visibleOrbCount = Math.min(count, isOrderMode ? 10 : 12);
        const previewWidth = isOrderMode ? Math.max(220, count * 42 + 104) : 0;

        return `
            <section
                class="random-team-box random-team-preview-box ${isOrderMode ? "is-order-mode" : ""}"
                style="${getTeamAccentStyle(teamIndex)}${isOrderMode ? ` --order-card-width: ${previewWidth}px;` : ""}"
            >
                <div class="random-team-head">
                    <h3 class="random-team-name" title="${escapeHtml(this.getGroupHeading(teamIndex, isOrderMode ? "orders" : "teams"))}">${escapeHtml(this.getGroupHeading(teamIndex, isOrderMode ? "orders" : "teams"))}</h3>
                    <span class="roster-count random-team-count">${escapeHtml(String(count))}</span>
                </div>
                <div class="random-team-preview-orbs ${isOrderMode ? "is-order-mode" : ""}">
                    ${Array.from({ length: visibleOrbCount }, (_, orbIndex) => this.renderPreviewOrb(teamIndex, orbIndex)).join("")}
                </div>
            </section>
        `;
    }

    private renderPreviewOrb(teamIndex: number, orbIndex: number): string {
        const previewMemberId = teamIndex * 7 + orbIndex + 1;

        return `
            <span class="random-teams-preview-orb" style="${getMemberStyle(previewMemberId)}">
                ${renderRandomTeamsCapyMarkup("random-teams-preview-capy")}
            </span>
        `;
    }

    private renderFinishFrontOrderButton(teamIndex: number, isDisabled: boolean): string {
        const finishFrontLabel = t("randomTeamsFinishFront");

        return `
            <button
                class="random-order-finish-front-button"
                type="button"
                data-random-teams-finish-front
                data-team-index="${teamIndex}"
                aria-label="${escapeHtml(finishFrontLabel)}"
                title="${escapeHtml(finishFrontLabel)}"
                ${isDisabled ? "disabled" : ""}
            >
                <svg
                    class="random-order-finish-front-icon"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    focusable="false"
                >
                    <path class="random-order-finish-front-arrow" d="M17.1 12H4.9m5.15-5.15L4.9 12l5.15 5.15" />
                    <rect class="random-order-finish-front-token" x="18.15" y="7.35" width="4.9" height="9.3" rx="1.7" />
                </svg>
            </button>
        `;
    }

    private renderTeamMarkup(teamIndex: number, members: number[]): string {
        const result = this.state.result;
        const resultMode = result?.mode ?? this.state.mode;
        const orderTokensPerRow = resultMode === "orders" ? this.getOrderTokensPerRow() : 0;
        const orderSlotCount =
            resultMode === "orders" ? Math.max(result?.orderSlotCounts?.[teamIndex] ?? members.length, members.length, 1) : 0;
        const orderCardWidth = Math.max(220, Math.min(orderSlotCount, Math.max(orderTokensPerRow, 1)) * ORDER_TOKEN_SLOT_WIDTH + 96);
        return `
            <section
                class="random-team-box ${this.highlightedTeamIndex === teamIndex ? "is-drop-target" : ""} ${
                    resultMode === "orders" ? "is-order-mode" : ""
                }"
                data-team-box
                data-team-index="${teamIndex}"
                style="${getTeamAccentStyle(teamIndex, members)}${
                    resultMode === "orders" ? ` --order-card-width: ${orderCardWidth}px;` : ""
                }"
            >
                <div class="random-team-head">
                    ${resultMode === "orders" ? this.renderFinishFrontOrderButton(teamIndex, members.length === 0 || this.launchInProgress) : ""}
                    <h3 class="random-team-name" title="${escapeHtml(this.getGroupHeading(teamIndex, resultMode))}">${escapeHtml(this.getGroupHeading(teamIndex, resultMode))}</h3>
                    <span class="roster-count random-team-count">${escapeHtml(String(members.length))}</span>
                </div>
                <div class="random-team-members ${resultMode === "orders" ? "is-order-mode" : ""}">
                    ${
                        members.length > 0
                            ? resultMode === "orders"
                                ? this.renderOrderRows(teamIndex, members, orderTokensPerRow)
                                : members
                                      .map((memberId, index) =>
                                          this.renderMemberToken(memberId, {
                                              zone: "team",
                                              teamIndex,
                                              index
                                          })
                                      )
                                      .join("")
                            : `<p class="random-team-empty">${escapeHtml(this.getDropHereLabel())}</p>`
                    }
                </div>
            </section>
        `;
    }

    private getRenderedOrderMemberTokenLayoutSize(): number | null {
        const token = this.root.querySelector<HTMLElement>('.random-team-box.is-order-mode [data-member-token][data-zone="team"]');

        if (!token) {
            return null;
        }

        const width = token.getBoundingClientRect().width;

        return width > 0 ? width : null;
    }

    private getRenderedOrderColumnGap(): number | null {
        const row = this.root.querySelector<HTMLElement>("[data-order-row]");

        if (!row) {
            return null;
        }

        const gap = parseCssPixels(window.getComputedStyle(row).columnGap);

        return gap !== null && gap >= 0 ? gap : null;
    }

    private getOrderTokensPerRow(): number {
        const boardWidth = this.refs.board?.clientWidth ?? this.refs.stage?.clientWidth ?? 1180;
        const renderedOrderColumnWidth =
            this.refs.board?.querySelector<HTMLElement>(".random-teams-teams-grid.is-order-mode")?.clientWidth ?? 0;
        const scale = this.getOrderLayoutScale();
        const tokenSize =
            (this.presentationOpen ? this.getRenderedOrderMemberTokenLayoutSize() : null) ??
            this.getOrderMemberTokenLayoutSize(scale);
        const columnGap =
            (this.presentationOpen ? this.getRenderedOrderColumnGap() : null) ?? this.getOrderColumnGap() * scale;
        const rowPadding = this.getOrderRowSidePadding() * 2 * scale;
        const membersInlinePadding = this.getOrderMembersInlinePadding() * scale;
        const connectorLaneReserve = tokenSize * this.getOrderConnectorLaneReserve();
        const floorLaneReserve =
            this.state.result?.mode === "orders"
                ? Math.max(this.getOrderFinishedLaneMinWidth(), this.getOrderFinishedLaneWidth() * scale) +
                  this.getOrderFinishedLaneGap() * scale
                : 0;
        const orderColumnWidth = Math.max(tokenSize, renderedOrderColumnWidth || boardWidth - floorLaneReserve);
        const availableWidth = Math.max(
            tokenSize,
            orderColumnWidth - rowPadding - membersInlinePadding - connectorLaneReserve
        );
        const renderedMembers = this.refs.board?.querySelector<HTMLElement>(".random-team-members.is-order-mode");
        const renderedRow = renderedMembers?.querySelector<HTMLElement>("[data-order-row]");
        const membersStyle = renderedMembers ? window.getComputedStyle(renderedMembers) : null;
        const rowStyle = renderedRow ? window.getComputedStyle(renderedRow) : null;
        // Fit against the same live padding and gap that paint the aligned rows.
        // The header owns the finish action, so no first-row-only reserve is needed.
        const measuredWidth = renderedMembers && membersStyle && rowStyle
            ? renderedMembers.clientWidth - Number.parseFloat(membersStyle.paddingLeft) - Number.parseFloat(membersStyle.paddingRight)
              - Number.parseFloat(rowStyle.paddingLeft) - Number.parseFloat(rowStyle.paddingRight)
            : availableWidth;
        const measuredTokenSize = this.getRenderedOrderMemberTokenLayoutSize() ?? tokenSize;
        const measuredGap = this.getRenderedOrderColumnGap() ?? columnGap;
        const tokenCount = Math.floor((Math.max(measuredTokenSize, measuredWidth) + measuredGap) / (measuredTokenSize + measuredGap));

        const maximumTokenCount = this.isCompactMobileViewport() ? (this.presentationOpen ? 4 : 3) : MAX_ORDER_TOKENS_PER_ROW;

        return clampNumber(tokenCount, MIN_ORDER_TOKENS_PER_ROW, maximumTokenCount);
    }

    private getOrderLayoutScale(): number {
        if (!this.presentationOpen || !this.refs.stage) {
            return 1;
        }

        const scale = Number.parseFloat(this.refs.stage.style.getPropertyValue("--random-teams-presentation-scale"));

        return Number.isFinite(scale) && scale > 0 ? scale : 1;
    }

    private getOrderFinishedLaneMinWidth(): number {
        if (this.state.result?.participants?.length) {
            return this.isCompactMobileViewport() ? 148 : 176;
        }

        if (this.isCompactMobileViewport()) {
            return this.presentationOpen ? 86 : 84;
        }

        return this.presentationOpen && this.isShortLandscapeViewport()
            ? ORDER_FINISHED_LANE_MIN_WIDTH + 20
            : ORDER_FINISHED_LANE_MIN_WIDTH;
    }

    private getOrderFinishedLaneWidth(): number {
        if (this.state.result?.participants?.length) {
            return this.isCompactMobileViewport() ? 156 : 184;
        }

        if (this.isCompactMobileViewport()) {
            return this.presentationOpen ? 86 : 84;
        }

        // Fullscreen short-landscape orders need a touch more finished-lane reserve
        // now that the stage toolbar carries one more result-edit affordance.
        return this.presentationOpen && this.isShortLandscapeViewport() ? ORDER_FINISHED_LANE_WIDTH + 20 : ORDER_FINISHED_LANE_WIDTH;
    }

    private getOrderFinishedLaneGap(): number {
        return this.isCompactMobileViewport() ? 6 : ORDER_FINISHED_LANE_GAP;
    }

    private getOrderColumnGap(): number {
        return this.isCompactMobileViewport() ? ORDER_ROW_COLUMN_GAP_MOBILE : ORDER_ROW_COLUMN_GAP;
    }

    private getOrderConnectorLaneReserve(): number {
        if (!this.isCompactMobileViewport()) {
            return 0.2;
        }

        return this.presentationOpen ? 0.58 : 0.42;
    }

    private getOrderRowSidePadding(): number {
        if (!this.isCompactMobileViewport()) {
            return ORDER_ROW_SIDE_PADDING;
        }

        return this.presentationOpen ? 4 : 6;
    }

    private getOrderMembersInlinePadding(): number {
        if (!this.isCompactMobileViewport()) {
            return ORDER_MEMBERS_INLINE_PADDING;
        }

        return this.presentationOpen ? 0 : 2;
    }

    private getOrderMemberTokenBaseSize(): number {
        if (this.state.result?.participants?.length) {
            return this.isCompactMobileViewport() || this.isShortLandscapeViewport()
                ? SITE_TUNING.randomTeams.layout.namedMemberTokenWidthMobilePx
                : SITE_TUNING.randomTeams.layout.namedMemberTokenWidthPx;
        }

        if (this.isCompactMobileViewport()) {
            return 50;
        }

        return this.isShortLandscapeViewport() ? 62 : MEMBER_TOKEN_SIZE;
    }

    private getOrderMemberTokenLayoutSize(scale = this.getOrderLayoutScale()): number {
        if (this.state.result?.participants?.length) {
            const baseWidth = this.isCompactMobileViewport() || this.isShortLandscapeViewport()
                ? SITE_TUNING.randomTeams.layout.namedMemberTokenWidthMobilePx
                : SITE_TUNING.randomTeams.layout.namedMemberTokenWidthPx;
            return baseWidth * scale;
        }

        if (this.presentationOpen && this.isCompactMobileViewport()) {
            return clampNumber(76 * scale, 40, 48);
        }

        return (this.presentationOpen ? PRESENTATION_MEMBER_TOKEN_SIZE : this.getOrderMemberTokenBaseSize()) * scale;
    }

    private getRenderedOrderRowSize(): number | null {
        const renderedSizes = Array.from(this.root.querySelectorAll<HTMLElement>("[data-order-coil]"))
            .map((coil) => Number.parseInt(coil.dataset.orderRowSize ?? "", 10))
            .filter((size) => Number.isFinite(size) && size > 0);

        return renderedSizes.length > 0 ? renderedSizes[0] : null;
    }

    private isOrderPresentationRowRefreshNeeded(): boolean {
        if (!this.presentationOpen || this.launchInProgress || this.state.result?.mode !== "orders") {
            return false;
        }

        const renderedRowSize = this.getRenderedOrderRowSize();

        if (renderedRowSize === null) {
            return false;
        }

        return renderedRowSize !== this.getOrderTokensPerRow();
    }

    private scheduleOrderPresentationRowRefreshIfNeeded() {
        if (!this.isOrderPresentationRowRefreshNeeded() || this.orderPresentationRefreshFrameId !== null) {
            return;
        }

        this.orderPresentationRefreshFrameId = window.requestAnimationFrame(() => {
            this.orderPresentationRefreshFrameId = null;

            if (!this.isOrderPresentationRowRefreshNeeded()) {
                return;
            }

            this.syncResults();
        });
    }

    private isCompactMobileViewport(): boolean {
        return window.matchMedia("(max-width: 640px)").matches;
    }

    private getMobileKeyboardInset(): number {
        const visualViewport = window.visualViewport;

        if (!visualViewport || !this.isCompactMobileViewport()) {
            return 0;
        }

        return Math.max(0, window.innerHeight - visualViewport.height - visualViewport.offsetTop);
    }

    private isMobileKeyboardVisible(): boolean {
        if (!this.isCompactMobileViewport()) {
            return false;
        }

        if (!window.visualViewport) {
            return true;
        }

        return this.getMobileKeyboardInset() >= MOBILE_KEYBOARD_MIN_INSET_PX;
    }

    private shouldShowMobileExclusionSubmitButton(): boolean {
        // iOS can leave the exclusion input focused after the keyboard disappears,
        // so keep the floating submit bar tied to the real keyboard inset too.
        return (
            this.isCompactMobileViewport() &&
            this.focusedExclusionIndex !== null &&
            !this.launchInProgress &&
            this.isMobileKeyboardVisible()
        );
    }

    private syncMobileExclusionSubmitButton() {
        if (!this.refs.mobileExclusionCommitButton) {
            return;
        }

        const shouldShow = this.shouldShowMobileExclusionSubmitButton();
        this.refs.mobileExclusionCommitButton.hidden = !shouldShow;
        this.refs.mobileExclusionCommitButton.disabled = !shouldShow;
    }

    private syncMobileExclusionSubmitViewportOffset() {
        if (!this.isCompactMobileViewport()) {
            this.root.style.removeProperty("--random-teams-mobile-submit-offset");
            return;
        }

        const viewportInset = this.getMobileKeyboardInset();
        this.root.style.setProperty("--random-teams-mobile-submit-offset", `${Math.round(viewportInset)}px`);
    }

    private getFocusedExclusionInput(): HTMLInputElement | null {
        const activeElement = document.activeElement;

        if (
            activeElement instanceof HTMLInputElement &&
            activeElement.hasAttribute("data-random-teams-exclusion-input") &&
            this.root.contains(activeElement)
        ) {
            return activeElement;
        }

        if (this.focusedExclusionIndex === null) {
            return null;
        }

        return (
            this.refs.exclusionList?.querySelector<HTMLInputElement>(
                `[data-random-teams-exclusion-input][data-exclusion-index="${this.focusedExclusionIndex}"]`
            ) ?? null
        );
    }

    private getNextOpenExclusionIndex(afterIndex: number): number | null {
        for (let index = afterIndex + 1; index < this.state.excludedIds.length; index += 1) {
            if (!this.state.excludedIds[index]) {
                return index;
            }
        }

        return this.state.excludedIds.length > 0 ? this.state.excludedIds.length - 1 : null;
    }

    private commitActiveExclusionInput() {
        const input = this.getFocusedExclusionInput();

        if (!input) {
            return;
        }

        const inputIndex = Number.parseInt(input.dataset.exclusionIndex ?? "", 10);

        if (Number.isNaN(inputIndex)) {
            return;
        }

        this.commitExclusionInput(input, { focusBehavior: "none" });
        const nextOpenIndex = this.getNextOpenExclusionIndex(inputIndex);

        if (nextOpenIndex !== null) {
            this.focusExclusionInputSoon(nextOpenIndex);
        }
    }

    private isShortLandscapeViewport(): boolean {
        return window.matchMedia("(orientation: landscape) and (max-height: 720px)").matches;
    }

    private shouldUseSideBounceLaunch(): boolean {
        return (
            this.isCompactMobileViewport() ||
            window.matchMedia("(orientation: portrait) and (max-aspect-ratio: 4/5)").matches
        );
    }

    private getTeamDensityClass(result: RandomTeamsResult): string {
        const largestTeamSize = Math.max(...result.teams.map((members) => members.length), 0);

        if (result.teams.length <= 4 && largestTeamSize <= 5 && result.memberCount <= 24) {
            return "is-roomy-team-layout";
        }

        return "";
    }

    private renderOrderRows(teamIndex: number, members: number[], tokensPerRow: number): string {
        const rowSize = Math.max(tokensPerRow, 1);
        const rows: number[][] = [];

        for (let index = 0; index < members.length; index += rowSize) {
            rows.push(members.slice(index, index + rowSize));
        }

        return `
            <div class="random-order-coil" data-order-coil data-order-row-size="${rowSize}">
                <svg class="random-order-connector-layer" aria-hidden="true"></svg>
                ${rows
                    .map((rowMembers, rowIndex) => {
                        const rowStartIndex = rowIndex * rowSize;
                        return `
                            <div
                                data-order-row
                                class="random-order-row ${rowIndex > 0 ? "has-continuation" : ""} ${
                                    rowIndex < rows.length - 1 ? "continues-below" : ""
                                }"
                            >
                                ${rowMembers
                                    .map((memberId, memberIndex) =>
                                        this.renderMemberToken(memberId, {
                                            zone: "team",
                                            teamIndex,
                                            index: rowStartIndex + memberIndex
                                        })
                                    )
                                    .join("")}
                            </div>
                        `;
                    })
                    .join("")}
            </div>
        `;
    }

    private syncOrderConnectors() {
        const perfEnabled = isRandomTeamsPerfEnabled();
        const syncStartedAt = perfEnabled ? performance.now() : 0;
        const perfPhases: Record<string, number> = {};
        let coilCount = 0;
        let rowCount = 0;
        let connectorCount = 0;

        const stageStartedAt = perfEnabled ? performance.now() : 0;
        const stageRect = this.refs.stage?.getBoundingClientRect();
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "stage", stageStartedAt);
        }
        const canvasConnectorGlows: RandomTeamsCanvasConnectorGlow[] = [];

        this.root.querySelectorAll<HTMLElement>("[data-order-coil]").forEach((coil) => {
            coilCount += 1;
            const svg = coil.querySelector<SVGSVGElement>(".random-order-connector-layer");

            if (!svg) {
                return;
            }

            const rowMeasureStartedAt = perfEnabled ? performance.now() : 0;
            const rows = Array.from(coil.querySelectorAll<HTMLElement>("[data-order-row]"));
            const coilRect = coil.getBoundingClientRect();
            const teamBox = coil.closest<HTMLElement>("[data-team-box]");
            const clipRect =
                stageRect && teamBox
                    ? this.getStageClipRect(teamBox, stageRect)
                    : null;
            rowCount += rows.length;
            if (perfEnabled) {
                recordRandomTeamsPerfPhase(perfPhases, "row-measure", rowMeasureStartedAt);
            }

            if (rows.length < 2 || coilRect.width <= 0 || coilRect.height <= 0) {
                svg.replaceChildren();
                return;
            }

            svg.setAttribute("viewBox", `0 0 ${coilRect.width} ${coilRect.height}`);
            svg.setAttribute("width", String(coilRect.width));
            svg.setAttribute("height", String(coilRect.height));

            const connectors: string[] = [];

            const geometryStartedAt = perfEnabled ? performance.now() : 0;
            for (let rowIndex = 0; rowIndex < rows.length - 1; rowIndex += 1) {
                const currentTokens = Array.from(rows[rowIndex].querySelectorAll<HTMLElement>(".member-token"));
                const nextTokens = Array.from(rows[rowIndex + 1].querySelectorAll<HTMLElement>(".member-token"));
                const fromToken = currentTokens[currentTokens.length - 1];
                const toToken = nextTokens[0];
                const fromStack = fromToken?.closest<HTMLElement>(".member-token-stack");
                const toStack = toToken?.closest<HTMLElement>(".member-token-stack");

                if (!fromToken || !toToken || !fromStack || !toStack) {
                    continue;
                }

                const start = this.getElementPointInContainer(fromToken, coil, "right");
                const end = this.getElementPointInContainer(toToken, coil, "left");
                // Split at the anchor midpoint: using badge/stack edges gives the
                // upper and lower turns different heights, so they cannot mirror.
                const midY = (start.y + end.y) / 2;
                const isCompactMobile = this.isCompactMobileViewport();
                const edgeInset = clampNumber(coilRect.width * (isCompactMobile ? 0.024 : 0.03), 14, 52);
                const tokenWidth = Math.max(fromToken.offsetWidth, toToken.offsetWidth);
                const exitLaneInset = clampNumber(
                    edgeInset + tokenWidth * (isCompactMobile ? 0.28 : 0.42),
                    isCompactMobile ? 26 : 44,
                    isCompactMobile ? 56 : 92
                );
                const bendRadius = clampNumber(
                    Math.min(exitLaneInset * (isCompactMobile ? 0.4 : 0.5), Math.abs(end.y - start.y) * 0.28),
                    isCompactMobile ? 10 : 12,
                    34
                );
                // Both turns use the same horizontal reach and horizontal tangents.
                // Reserving extra space on the right used to collapse only that bend.
                const cardRect = teamBox?.getBoundingClientRect();
                const leftGutter = cardRect ? coilRect.left - cardRect.left : 0;
                const rightGutter = cardRect ? cardRect.right - coilRect.right : 0;
                const turnReach = Math.max(1, Math.min(
                    bendRadius,
                    coilRect.width - start.x + rightGutter - 4,
                    end.x + leftGutter - 4
                ));
                const rightEdgeX = start.x + turnReach;
                const leftEdgeX = end.x - turnReach;
                const exitControlX = rightEdgeX;
                const enterControlX = leftEdgeX;
                const rightTurnX = start.x;
                const leftTurnX = end.x;
                const wrapPath = [
                    `M ${start.x.toFixed(1)} ${start.y.toFixed(1)}`,
                    `C ${exitControlX.toFixed(1)} ${start.y.toFixed(1)} ${rightEdgeX.toFixed(
                        1
                    )} ${midY.toFixed(1)} ${rightTurnX.toFixed(1)} ${midY.toFixed(1)}`,
                    `H ${leftTurnX.toFixed(1)}`,
                    `C ${leftEdgeX.toFixed(1)} ${midY.toFixed(1)} ${enterControlX.toFixed(
                        1
                    )} ${end.y.toFixed(1)} ${end.x.toFixed(1)} ${end.y.toFixed(1)}`
                ].join(" ");

                connectors.push(`
                    <path class="random-order-connector-path" d="${wrapPath}" />
                    <circle class="random-order-connector-dot" cx="${start.x.toFixed(1)}" cy="${start.y.toFixed(1)}" r="3.2" />
                    <circle class="random-order-connector-dot" cx="${end.x.toFixed(1)}" cy="${end.y.toFixed(1)}" r="3.2" />
                `);
                connectorCount += 1;

                if (stageRect && clipRect) {
                    const coilOffsetX = coilRect.left - stageRect.left;
                    const coilOffsetY = coilRect.top - stageRect.top;
                    canvasConnectorGlows.push({
                        clipRect,
                        start: {
                            x: coilOffsetX + start.x,
                            y: coilOffsetY + start.y
                        },
                        exitControlX: coilOffsetX + exitControlX,
                        rightEdgeX: coilOffsetX + rightEdgeX,
                        rightTurnX: coilOffsetX + rightTurnX,
                        midY: coilOffsetY + midY,
                        leftTurnX: coilOffsetX + leftTurnX,
                        leftEdgeX: coilOffsetX + leftEdgeX,
                        enterControlX: coilOffsetX + enterControlX,
                        end: {
                            x: coilOffsetX + end.x,
                            y: coilOffsetY + end.y
                        }
                    });
                }
            }
            if (perfEnabled) {
                recordRandomTeamsPerfPhase(perfPhases, "geometry", geometryStartedAt);
            }

            const svgWriteStartedAt = perfEnabled ? performance.now() : 0;
            svg.innerHTML = connectors.join("");
            if (perfEnabled) {
                recordRandomTeamsPerfPhase(perfPhases, "svg-write", svgWriteStartedAt);
            }
        });

        const canvasStartedAt = perfEnabled ? performance.now() : 0;
        this.stageCanvasRenderer?.setOrderConnectorGlows(canvasConnectorGlows);
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "canvas", canvasStartedAt);
            const phases = formatRandomTeamsPerfPhases(perfPhases);
            logRandomTeamsPerf(
                `order-connectors total=${roundRandomTeamsPerfMs(performance.now() - syncStartedAt)}ms coils=${coilCount} rows=${rowCount} connectors=${connectorCount} glows=${
                    canvasConnectorGlows.length
                }${phases ? ` phases=${phases}` : ""}`
            );
        }
    }

    private getElementPointInContainer(element: HTMLElement, container: HTMLElement, side: "left" | "right"): { x: number; y: number } {
        let x = side === "right" ? element.offsetWidth : 0;
        let y = element.offsetHeight / 2;
        let current: HTMLElement | null = element;

        while (current && current !== container) {
            x += current.offsetLeft;
            y += current.offsetTop;
            current = current.offsetParent as HTMLElement | null;
        }

        if (current === container) {
            return { x, y };
        }

        const elementRect = element.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();

        return {
            x: side === "right" ? elementRect.right - containerRect.left : elementRect.left - containerRect.left,
            y: elementRect.top - containerRect.top + elementRect.height / 2
        };
    }

    private renderMemberToken(
        memberId: number,
        location:
            | {
                  zone: "team";
                  teamIndex: number;
                  index: number;
              }
            | {
                  zone: "floor";
                  index: number;
              }
    ): string {
        const resultMode = this.state.result?.mode ?? this.state.mode;
        const participant = this.state.result?.participants?.find((entry) => entry.id === memberId);
        const isOrderToken = resultMode === "orders" && location.zone === "team";
        const isFinishedToken = resultMode === "orders" && location.zone === "floor";
        const isOrderShape = resultMode === "orders";
        const locationAttributes =
            location.zone === "team"
                ? `data-zone="team" data-team-index="${location.teamIndex}" data-member-index="${location.index}"`
                : `data-zone="floor" data-member-index="${location.index}"`;

        return `
            <div class="member-token-stack ${isOrderToken ? "is-order-mode" : ""} ${isFinishedToken ? "is-finished" : ""}">
                <button
                    class="member-token ${location.zone === "floor" ? "member-token-floor" : ""} ${participant ? "is-named-member" : ""} ${
                        this.animatingMemberIds.has(memberId) ? "is-incoming" : ""
                    } ${isFinishedToken ? "member-token-finished" : ""} ${isOrderShape ? "is-order-shape" : ""}"
                    type="button"
                    data-member-token
                    data-member-id="${memberId}"
                    ${participant ? `aria-label="${escapeHtml(participant.studentNumber === null ? participant.name : `${participant.studentNumber}, ${participant.name}`)}"` : ""}
                    ${locationAttributes}
                    style="${getMemberStyle(memberId)}"
                >
                    ${renderRandomTeamsCapyMarkup("random-teams-member-capy")}
                    ${renderRandomTeamsMemberLabel(memberId, participant)}
                </button>
                ${
                    isOrderToken
                        ? `<span class="member-token-order ${
                              location.index === 0 ? "is-first-order" : ""
                          }">${escapeHtml(String(location.index + 1))}</span>`
                        : ""
                }
            </div>
        `;
    }

    private launchMembers(memberIds: number[]) {
        const uniqueMemberIds = [...new Set(memberIds)];
        const memberCount = uniqueMemberIds.length;
        const perfEnabled = isRandomTeamsPerfEnabled();
        const launchStartedAt = perfEnabled ? performance.now() : 0;
        const perfPhases: Record<string, number> = {};

        this.syncAnimationAutoSuppression(this.getCurrentAnimationMemberCount());

        if (memberCount === 0 || !this.areAnimationsEnabled(this.getCurrentAnimationMemberCount())) {
            const formStartedAt = perfEnabled ? performance.now() : 0;
            this.syncForm();
            if (perfEnabled) {
                recordRandomTeamsPerfPhase(perfPhases, "form", formStartedAt);
            }
            const resultsStartedAt = perfEnabled ? performance.now() : 0;
            this.syncResults();
            if (perfEnabled) {
                recordRandomTeamsPerfPhase(perfPhases, "results", resultsStartedAt);
                const phases = formatRandomTeamsPerfPhases(perfPhases);
                logRandomTeamsPerf(
                    `launch-members mode=${this.state.result?.mode ?? this.state.mode} members=${memberCount} animated=0 total=${roundRandomTeamsPerfMs(
                        performance.now() - launchStartedAt
                    )}ms${phases ? ` phases=${phases}` : ""}`
                );
            }
            return;
        }

        this.prepareAudio();
        uniqueMemberIds.forEach((memberId) => this.animatingMemberIds.add(memberId));
        this.launchInProgress = true;
        this.root.classList.add("is-random-teams-launching");
        const formStartedAt = perfEnabled ? performance.now() : 0;
        this.syncForm();
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "form", formStartedAt);
        }
        const resultsStartedAt = perfEnabled ? performance.now() : 0;
        this.syncResults();
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "results", resultsStartedAt);
        }
        const animationStartedAt = perfEnabled ? performance.now() : 0;
        this.runLaunchAnimation(uniqueMemberIds);
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "animation-setup", animationStartedAt);
            const phases = formatRandomTeamsPerfPhases(perfPhases);
            logRandomTeamsPerf(
                `launch-members mode=${this.state.result?.mode ?? this.state.mode} members=${memberCount} animated=1 total=${roundRandomTeamsPerfMs(
                    performance.now() - launchStartedAt
                )}ms${phases ? ` phases=${phases}` : ""}`
            );
        }
    }

    private generateTeams() {
        const perfEnabled = isRandomTeamsPerfEnabled();
        const generateStartedAt = perfEnabled ? performance.now() : 0;
        const perfPhases: Record<string, number> = {};

        const validationStartedAt = perfEnabled ? performance.now() : 0;
        const validation = this.computeValidation();
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "validation", validationStartedAt);
        }

        if (this.launchInProgress || !validation.canGenerate || validation.teamCount === null || validation.memberCount === null) {
            return;
        }

        const clearStartedAt = perfEnabled ? performance.now() : 0;
        this.namesEditing = false;
        this.clearTransientState();
        this.setLateIdPanelOpen("add", false);
        this.setLateIdPanelOpen("remove", false);
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "clear", clearStartedAt);
        }
        const buildStartedAt = perfEnabled ? performance.now() : 0;
        this.state.result = buildResult(
            this.state.mode,
            validation.teamCount,
            validation.memberCount,
            validation.validExcludedIds,
            validation.currentSignature ?? "",
            validation.namedRoster
        );
        this.state.resultCreatedAt = Date.now();
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "build", buildStartedAt);
        }
        this.state.lastGeneratedSignature = validation.currentSignature;
        const excludedStartedAt = perfEnabled ? performance.now() : 0;
        this.syncExcludedFloorIds();
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "excluded", excludedStartedAt);
        }
        const launchMembersStartedAt = perfEnabled ? performance.now() : 0;
        this.launchMembers([...this.state.result.teams.flat(), ...this.state.result.floorIds]);
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "launch", launchMembersStartedAt);
            const includedCount =
                this.state.result.floorIds.length + this.state.result.teams.reduce((total, members) => total + members.length, 0);
            const phases = formatRandomTeamsPerfPhases(perfPhases);
            logRandomTeamsPerf(
                `generate mode=${this.state.mode} teams=${validation.teamCount} members=${validation.memberCount} included=${includedCount} excluded=${
                    validation.validExcludedIds.length
                } total=${roundRandomTeamsPerfMs(performance.now() - generateStartedAt)}ms${phases ? ` phases=${phases}` : ""}`
            );
        }
    }

    private collectLaunchTokens(memberIds: number[]) {
        const result = this.state.result;

        if (!result || !this.refs.stage || !this.refs.dragLayer) {
            return [];
        }

        const memberIdSet = new Set(memberIds);

        return shuffle([
            ...result.teams.flat().map((memberId) => ({
                memberId,
                zone: "team" as const,
                element: this.root.querySelector<HTMLElement>(`[data-member-token][data-member-id="${memberId}"]`)
            })),
            ...result.floorIds.map((memberId) => ({
                memberId,
                zone: "floor" as const,
                element: this.root.querySelector<HTMLElement>(`[data-member-token][data-member-id="${memberId}"]`)
            }))
        ]).filter(
            (entry): entry is LaunchToken =>
                memberIdSet.has(entry.memberId) && Boolean(entry.element)
        );
    }

    private runLaunchAnimation(memberIds: number[]) {
        const result = this.state.result;
        const perfEnabled = isRandomTeamsPerfEnabled();
        const launchStartedAt = perfEnabled ? performance.now() : 0;
        const perfPhases: Record<string, number> = {};

        if (!result || !this.refs.stage || !this.refs.dragLayer) {
            this.root.classList.remove("is-random-teams-launching");
            this.launchInProgress = false;
            this.syncStageCanvas();
            return;
        }

        const collectStartedAt = perfEnabled ? performance.now() : 0;
        const tokens = this.collectLaunchTokens(memberIds);
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "collect", collectStartedAt);
        }

        if (tokens.length === 0) {
            memberIds.forEach((memberId) => this.animatingMemberIds.delete(memberId));
            this.launchInProgress = false;
            this.root.classList.remove("is-random-teams-launching");
            this.syncForm();
            this.syncResults();
            this.syncStageCanvas();
            return;
        }

        const stageRectStartedAt = perfEnabled ? performance.now() : 0;
        const stageRect = this.refs.stage.getBoundingClientRect();
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "stage", stageRectStartedAt);
        }
        const useSideBounceLaunch = this.shouldUseSideBounceLaunch() ||
            stageRect.height > stageRect.width * SITE_TUNING.randomTeams.animation.tallStageSideBounceRatio;
        let finishedCount = 0;
        let floorLaunchCount = 0;
        let totalLaunchDuration = 0;
        let maxLaunchDuration = 0;
        let expectedCompletionMs = 0;
        const flights: Array<{ animation: Animation; memberId: number; delay: number; duration: number; floor: boolean; impactOffset: number | null; landingOffset: number }> = [];

        const setupStartedAt = perfEnabled ? performance.now() : 0;
        const baseLaunchStaggerMs =
            (useSideBounceLaunch ? SIDE_BOUNCE_LAUNCH_STAGGER_MS : LAUNCH_STAGGER_MS) * LAUNCH_TIME_SCALE;
        const launchStaggerMs = Math.floor(
            Math.min(baseLaunchStaggerMs, MAX_LAUNCH_STAGGER_WINDOW_MS / Math.max(tokens.length - 1, 1))
        );
        // Measure every destination before inserting/animating orbs; interleaving
        // these reads with DOM writes forces layout once per student.
        const targetRects = tokens.map(({ element }) => element.getBoundingClientRect());
        tokens.forEach(({ memberId, element, zone }, index) => {
            const targetRect = targetRects[index];
            const tokenWidth = targetRect.width;
            const tokenHeight = targetRect.height;
            const targetX = targetRect.left - stageRect.left;
            const targetY = targetRect.top - stageRect.top;
            const isFloorLaunch = zone === "floor";
            if (isFloorLaunch) {
                floorLaunchCount += 1;
            }
            const isOrderFloorLaunch = result.mode === "orders" && isFloorLaunch;
            const motion = createRandomTeamsLaunchMotion({
                width: stageRect.width,
                height: stageRect.height,
                tokenWidth,
                tokenHeight,
                targetX,
                targetY,
                index,
                sideBounce: useSideBounceLaunch,
                floor: isFloorLaunch,
                compact: tokenHeight <= 52
            });
            const launchDuration = motion.duration;
            totalLaunchDuration += launchDuration;
            maxLaunchDuration = Math.max(maxLaunchDuration, launchDuration);
            expectedCompletionMs = Math.max(
                expectedCompletionMs,
                index * launchStaggerMs + launchDuration
            );
            const orb = document.createElement("div");
            const participant = result.participants?.find((entry) => entry.id === memberId);
            orb.className = `random-teams-launch-orb${this.state.result?.mode === "orders" ? " is-order-shape" : ""}${
                isOrderFloorLaunch ? " is-order-finished" : ""
            }${participant ? " is-named-member" : ""}`;
            orb.style.cssText = `${getMemberStyle(memberId)}left:0;top:0;width:${tokenWidth}px;height:${tokenHeight}px;`;
            orb.style.transformOrigin = motion.transformOrigin;
            orb.style.transform = String(motion.keyframes[0].transform);
            orb.innerHTML = `${renderRandomTeamsCapyMarkup("random-teams-member-capy")}${renderRandomTeamsMemberLabel(memberId, participant)}`;
            this.refs.dragLayer?.append(orb);

            const dropAnimation = orb.animate(motion.keyframes, {
                duration: launchDuration,
                delay: index * launchStaggerMs,
                easing: "linear",
                fill: "both"
            });
            dropAnimation.pause();
            this.trackAnimation(dropAnimation);
            flights.push({ animation: dropAnimation, memberId, delay: index * launchStaggerMs, duration: launchDuration, floor: isFloorLaunch, impactOffset: motion.impactOffset, landingOffset: motion.landingOffset });

            // One flight ends exactly on the real token; avoid a second overlapping
            // squash/fade animation and per-student layout reads at the handoff.
            const finishTokenLaunch = () => {
                if (!orb.isConnected) {
                    return;
                }
                this.registerTeamLanding(element, memberId);
                this.animatingMemberIds.delete(memberId);
                element.classList.remove("is-incoming");
                orb.remove();
                finishedCount += 1;
                if (finishedCount >= tokens.length) {
                    this.launchInProgress = false;
                    this.root.classList.remove("is-random-teams-launching");
                    this.syncForm();
                    this.syncFinishedOrderLayout();
                    if (perfEnabled) {
                        logRandomTeamsPerf(
                            `launch-complete mode=${result.mode} tokens=${tokens.length} total=${roundRandomTeamsPerfMs(
                                performance.now() - launchStartedAt
                            )}ms expected=${roundRandomTeamsPerfMs(expectedCompletionMs)}ms floor=${floorLaunchCount}`
                        );
                    }
                }
            };
            void dropAnimation.finished.then(finishTokenLaunch).catch(finishTokenLaunch);
        });

        // Give layout and the first paused paint a rendering opportunity before
        // releasing every flight from one shared clock. Setup cannot eat playback.
        const firstFrame = window.requestAnimationFrame(() => {
            this.launchFrameIds = this.launchFrameIds.filter((id) => id !== firstFrame);
            const secondFrame = window.requestAnimationFrame(() => {
                this.launchFrameIds = this.launchFrameIds.filter((id) => id !== secondFrame);
                if (!this.launchInProgress || this.state.result !== result) {
                    return;
                }
                const startTime = document.timeline.currentTime;
                flights.forEach(({ animation, memberId, delay, duration, impactOffset, landingOffset }) => {
                    animation.play();
                    animation.startTime = startTime;
                    if (impactOffset !== null) {
                        this.scheduleSyncedAudio(delay + duration * impactOffset - AUDIO_SYNC_LEAD_MS, () => this.audio.playBounce());
                    }
                    this.scheduleSyncedAudio(delay + duration * landingOffset - AUDIO_SYNC_LEAD_MS, () => {
                        if (this.animatingMemberIds.has(memberId)) {
                            this.audio.playLatch(this.getLatchPlaybackRate(memberId));
                        }
                    });
                });
            });
            this.launchFrameIds.push(secondFrame);
        });
        this.launchFrameIds.push(firstFrame);
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "setup", setupStartedAt);
            const averageLaunchDuration = tokens.length > 0 ? totalLaunchDuration / tokens.length : 0;
            const phases = formatRandomTeamsPerfPhases(perfPhases);
            logRandomTeamsPerf(
                `launch-setup mode=${result.mode} tokens=${tokens.length} floor=${floorLaunchCount} sideBounce=${useSideBounceLaunch ? 1 : 0} avgDuration=${roundRandomTeamsPerfMs(
                    averageLaunchDuration
                )}ms maxDuration=${roundRandomTeamsPerfMs(maxLaunchDuration)}ms total=${roundRandomTeamsPerfMs(
                    performance.now() - launchStartedAt
                )}ms${phases ? ` phases=${phases}` : ""}`
            );
        }
    }

    private getLatchPlaybackRate(memberId: number): number {
        const memberCount = this.state.result?.memberCount ?? parsePositiveInteger(this.state.memberCount) ?? memberId;

        if (memberCount <= 1) {
            return 1;
        }

        const normalizedPosition = (memberId - 1) / (memberCount - 1);
        return 0.7 + normalizedPosition * 0.6;
    }

    private spawnSplash(teamBox: HTMLElement, target: HTMLElement | DOMRect, tilt: number) {
        const teamRect = teamBox.getBoundingClientRect();
        const targetRect = target instanceof HTMLElement ? target.getBoundingClientRect() : target;
        const stageRect = this.refs.stage?.getBoundingClientRect();

        if (stageRect && this.stageCanvasRenderer?.isAvailable) {
            const clipRect = this.getStageClipRect(teamBox, stageRect);

            if (clipRect) {
                const teamStyles = window.getComputedStyle(teamBox);
                const burst: RandomTeamsCanvasImpactBurst = {
                    clipRect,
                    x: targetRect.left - stageRect.left + targetRect.width / 2,
                    y: targetRect.top - stageRect.top + targetRect.height / 2,
                    tiltDeg: tilt,
                    solid: teamStyles.getPropertyValue("--team-accent-solid").trim() || "#f6fffb",
                    paint: teamStyles.getPropertyValue("--team-accent-paint").trim() || "rgba(164, 255, 250, 0.82)",
                    soft: teamStyles.getPropertyValue("--team-accent-soft").trim() || "rgba(164, 255, 250, 0.18)",
                    glow: teamStyles.getPropertyValue("--team-accent-glow").trim() || "rgba(164, 255, 250, 0.28)"
                };
                this.stageCanvasRenderer.spawnImpactBurst(burst);
                return;
            }
        }

        const splash = document.createElement("span");
        splash.className = "random-teams-splash";
        splash.style.cssText = `left:${targetRect.left - teamRect.left + targetRect.width / 2}px;top:${targetRect.top - teamRect.top + targetRect.height / 2}px;--splash-tilt:${tilt}deg;`;
        teamBox.append(splash);
        const timeoutId = window.setTimeout(() => {
            this.activeTimeouts.delete(timeoutId);
            splash.remove();
        }, 760);
        this.activeTimeouts.add(timeoutId);
    }

    private applyTeamAccent(teamBox: HTMLElement, teamIndex: number, memberIds: number[]) {
        getTeamAccentStyle(teamIndex, memberIds)
            .split(";")
            .map((declaration) => declaration.trim())
            .filter(Boolean)
            .forEach((declaration) => {
                const separatorIndex = declaration.indexOf(":");

                if (separatorIndex < 0) {
                    return;
                }

                teamBox.style.setProperty(declaration.slice(0, separatorIndex), declaration.slice(separatorIndex + 1));
            });
    }

    private revealSettledToken(element: HTMLElement) {
        element.classList.remove("is-incoming");

        element.classList.add("is-landed");

        const timeoutId = window.setTimeout(() => {
            this.activeTimeouts.delete(timeoutId);
            element.classList.remove("is-landed");
        }, 220);

        this.activeTimeouts.add(timeoutId);
    }

    private removeExclusionAt(index: number) {
        const nextValues = [...this.state.excludedIds];

        nextValues.splice(index, 1);
        this.state.excludedIds = ensureTrailingExclusionInput(nextValues);
        this.sync();
    }

    private findCurrentLocation(memberId: number): DropDestination | null {
        const result = this.state.result;

        if (!result) {
            return null;
        }

        for (let teamIndex = 0; teamIndex < result.teams.length; teamIndex += 1) {
            const memberIndex = result.teams[teamIndex].indexOf(memberId);

            if (memberIndex >= 0) {
                return {
                    zone: "team",
                    teamIndex,
                    index: memberIndex
                };
            }
        }

        const floorIndex = result.floorIds.indexOf(memberId);

        if (floorIndex >= 0) {
            return {
                zone: "floor",
                index: floorIndex
            };
        }

        return null;
    }

    private getDragHoverTeamIndex(clientX: number, clientY: number): number | null {
        const drag = this.drag;

        if (!drag) {
            return null;
        }

        return drag.teamDropRects.find(({ rect }) => this.isPointInRect(clientX, clientY, rect))?.teamIndex ?? null;
    }

    private getDropDestination(clientX: number, clientY: number, memberId: number): DropDestination | null {
        const result = this.state.result;

        if (!result) {
            return null;
        }

        if (this.drag?.memberId === memberId) {
            const teamHit = this.drag.teamDropRects.find(({ rect }) => this.isPointInRect(clientX, clientY, rect));

            if (teamHit) {
                return {
                    zone: "team",
                    teamIndex: teamHit.teamIndex,
                    index: this.getTeamInsertionIndex(teamHit.teamIndex, clientX, clientY, memberId)
                };
            }

            if (this.drag.floorRect && this.isPointInRect(clientX, clientY, this.drag.floorRect)) {
                return {
                    zone: "floor",
                    index: this.getFloorInsertionIndex(clientX, clientY)
                };
            }

            return {
                zone: "floor",
                index: result.floorIds.length
            };
        }

        const target = document.elementFromPoint(clientX, clientY) as HTMLElement | null;
        const token = target?.closest<HTMLElement>("[data-member-token]");

        if (token) {
            const targetMemberId = Number.parseInt(token.dataset.memberId ?? "", 10);

            if (!Number.isNaN(targetMemberId) && targetMemberId !== memberId) {
                if (token.dataset.zone === "team") {
                    const teamIndex = Number.parseInt(token.dataset.teamIndex ?? "", 10);

                    if (Number.isNaN(teamIndex)) {
                        return null;
                    }

                    return {
                        zone: "team",
                        teamIndex,
                        index: this.getTeamInsertionIndex(teamIndex, clientX, clientY, memberId)
                    };
                }

                return {
                    zone: "floor",
                    index: Number.parseInt(token.dataset.memberIndex ?? String(result.floorIds.length), 10)
                };
            }
        }

        const teamBox = target?.closest<HTMLElement>("[data-team-box]");

        if (teamBox) {
            const teamIndex = Number.parseInt(teamBox.dataset.teamIndex ?? "", 10);

            if (!Number.isNaN(teamIndex)) {
                return {
                    zone: "team",
                    teamIndex,
                    index: this.getTeamInsertionIndex(teamIndex, clientX, clientY, memberId)
                };
            }
        }

        return {
            zone: "floor",
            index: result.floorIds.length
        };
    }

    private getTeamInsertionIndex(teamIndex: number, clientX: number, clientY: number, draggingMemberId: number): number {
        const result = this.state.result;

        if (!result) {
            return 0;
        }

        const tokens =
            this.drag?.memberId === draggingMemberId
                ? (this.drag.teamTokenRects.get(teamIndex) ?? [])
                : this.measureTeamTokenRects(teamIndex, draggingMemberId);

        if (tokens.length === 0) {
            return 0;
        }

        const tokenHeight = tokens[0]?.rect.height ?? MEMBER_TOKEN_SIZE;
        const rowTolerance = result.mode === "orders" ? tokenHeight * 0.82 : tokenHeight * 0.72;
        const insertionIndex = tokens.findIndex(({ rect }) => {
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            const isEarlierRow = clientY < centerY - rowTolerance;
            const isSameRowBeforeToken = Math.abs(clientY - centerY) <= rowTolerance && clientX < centerX;

            return isEarlierRow || isSameRowBeforeToken;
        });

        return insertionIndex >= 0 ? insertionIndex : tokens.length;
    }

    private getFloorInsertionIndex(clientX: number, clientY: number): number {
        const result = this.state.result;
        const tokens = this.drag?.floorTokenRects ?? [];

        if (!result || tokens.length === 0) {
            return result?.floorIds.length ?? 0;
        }

        const tokenHeight = tokens[0]?.rect.height ?? MEMBER_TOKEN_SIZE;
        const rowTolerance = tokenHeight * 0.72;
        const insertionIndex = tokens.findIndex(({ rect }) => {
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
            const isEarlierRow = clientY < centerY - rowTolerance;
            const isSameRowBeforeToken = Math.abs(clientY - centerY) <= rowTolerance && clientX < centerX;

            return isEarlierRow || isSameRowBeforeToken;
        });

        return insertionIndex >= 0 ? insertionIndex : result.floorIds.length;
    }

    private captureTeamTokenRects(draggingMemberId: number): Map<number, TeamTokenRect[]> {
        const result = this.state.result;
        const tokenRects = new Map<number, TeamTokenRect[]>();

        if (!result) {
            return tokenRects;
        }

        result.teams.forEach((_team, teamIndex) => {
            tokenRects.set(teamIndex, this.measureTeamTokenRects(teamIndex, draggingMemberId));
        });

        return tokenRects;
    }

    private captureTeamDropRects(): TeamDropRect[] {
        return Array.from(this.root.querySelectorAll<HTMLElement>("[data-team-box]"))
            .map((teamBox) => {
                const teamIndex = Number.parseInt(teamBox.dataset.teamIndex ?? "", 10);

                if (Number.isNaN(teamIndex)) {
                    return null;
                }

                return {
                    teamIndex,
                    rect: teamBox.getBoundingClientRect()
                };
            })
            .filter((entry): entry is TeamDropRect => entry !== null);
    }

    private captureFloorRect(): DOMRect | null {
        return this.root.querySelector<HTMLElement>("[data-floor-tray]")?.getBoundingClientRect() ?? null;
    }

    private captureFloorTokenRects(draggingMemberId: number): FloorTokenRect[] {
        return Array.from(this.root.querySelectorAll<HTMLElement>('[data-member-token][data-zone="floor"]'))
            .map((token) => {
                const memberId = Number.parseInt(token.dataset.memberId ?? "", 10);
                const index = Number.parseInt(token.dataset.memberIndex ?? "", 10);

                if (Number.isNaN(memberId) || Number.isNaN(index) || memberId === draggingMemberId) {
                    return null;
                }

                return {
                    index,
                    rect: token.getBoundingClientRect()
                };
            })
            .filter((entry): entry is FloorTokenRect => entry !== null)
            .sort((first, second) => first.index - second.index);
    }

    private isPointInRect(clientX: number, clientY: number, rect: DOMRect): boolean {
        return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
    }

    private measureTeamTokenRects(teamIndex: number, draggingMemberId: number): TeamTokenRect[] {
        return Array.from(this.root.querySelectorAll<HTMLElement>(`[data-member-token][data-zone="team"][data-team-index="${teamIndex}"]`))
            .map((token) => ({
                memberId: Number.parseInt(token.dataset.memberId ?? "", 10),
                index: Number.parseInt(token.dataset.memberIndex ?? "", 10),
                rect: token.getBoundingClientRect()
            }))
            .filter(
                (token): token is TeamTokenRect =>
                    !Number.isNaN(token.memberId) && !Number.isNaN(token.index) && token.memberId !== draggingMemberId
            )
            .sort((first, second) => first.index - second.index);
    }

    private moveMemberWithAnimation(memberId: number, destination: DropDestination, releasePoint: { x: number; y: number }) {
        const perfEnabled = isRandomTeamsPerfEnabled();
        const moveStartedAt = perfEnabled ? performance.now() : 0;
        const perfPhases: Record<string, number> = {};

        const originStartedAt = perfEnabled ? performance.now() : 0;
        const origin = this.findCurrentLocation(memberId);
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "origin", originStartedAt);
        }
        const snapshotStartedAt = perfEnabled ? performance.now() : 0;
        const orderClosureSnapshot = this.captureOrderClosureSnapshot(memberId, origin, destination);
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "snapshot", snapshotStartedAt);
        }

        const stateStartedAt = perfEnabled ? performance.now() : 0;
        this.moveMember(memberId, destination);
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "state", stateStartedAt);
        }
        const resultsStartedAt = perfEnabled ? performance.now() : 0;
        this.syncResults();
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "results", resultsStartedAt);
        }
        const presentationStartedAt = perfEnabled ? performance.now() : 0;
        this.schedulePresentationLayoutRefresh();
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "presentation", presentationStartedAt);
        }
        const closureStartedAt = perfEnabled ? performance.now() : 0;
        this.animateOrderClosure(orderClosureSnapshot);
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "closure", closureStartedAt);
        }
        const animationStartedAt = perfEnabled ? performance.now() : 0;
        this.animateMemberToDestination(memberId, releasePoint, destination);
        if (perfEnabled) {
            recordRandomTeamsPerfPhase(perfPhases, "animate", animationStartedAt);
            const phases = formatRandomTeamsPerfPhases(perfPhases);
            logRandomTeamsPerf(
                `manual-move member=${memberId} from=${formatDropDestination(origin)} to=${formatDropDestination(destination)} total=${roundRandomTeamsPerfMs(
                    performance.now() - moveStartedAt
                )}ms${phases ? ` phases=${phases}` : ""}`
            );
        }
    }

    private moveMember(memberId: number, destination: DropDestination) {
        const result = this.state.result;

        if (!result) {
            return;
        }

        result.teams.forEach((members) => {
            const memberIndex = members.indexOf(memberId);

            if (memberIndex >= 0) {
                members.splice(memberIndex, 1);
            }
        });

        const floorIndex = result.floorIds.indexOf(memberId);

        if (floorIndex >= 0) {
            result.floorIds.splice(floorIndex, 1);
        }

        if (destination.zone === "team") {
            const nextMembers = result.teams[destination.teamIndex];
            nextMembers.splice(clampIndex(destination.index, nextMembers.length), 0, memberId);
            return;
        }

        if (!this.showExcludedOnFloor && this.computeValidation().validExcludedIds.includes(memberId)) {
            return;
        }

        result.floorIds.splice(clampIndex(destination.index, result.floorIds.length), 0, memberId);
    }

    private finishFrontOrderMember(teamIndex: number) {
        const result = this.state.result;

        if (this.launchInProgress || !result || result.mode !== "orders") {
            return;
        }

        const members = result.teams[teamIndex];
        const memberId = members?.[0];

        if (!Number.isInteger(memberId)) {
            return;
        }

        this.prepareAudio();

        const originToken = this.root.querySelector<HTMLElement>(
            `[data-member-token][data-zone="team"][data-team-index="${teamIndex}"][data-member-index="0"]`
        );
        const originRect = originToken?.getBoundingClientRect();
        const stageRect = this.refs.stage?.getBoundingClientRect();
        const releasePoint = originRect
            ? {
                  x: originRect.left + originRect.width / 2,
                  y: originRect.top + originRect.height / 2
              }
            : stageRect
              ? {
                    x: stageRect.left + stageRect.width / 2,
                    y: stageRect.top + stageRect.height / 2
                }
              : { x: window.innerWidth / 2, y: window.innerHeight / 2 };

        this.moveMemberWithAnimation(memberId, { zone: "floor", index: result.floorIds.length }, releasePoint);
    }

    private captureOrderClosureSnapshot(
        memberId: number,
        origin: DropDestination | null,
        destination: DropDestination
    ): Map<number, DOMRect> | null {
        const result = this.state.result;

        if (!result || result.mode !== "orders") {
            return null;
        }

        const teamIndices = new Set<number>();

        if (origin?.zone === "team") {
            teamIndices.add(origin.teamIndex);
        }

        if (destination.zone === "team") {
            teamIndices.add(destination.teamIndex);
        }

        const snapshot = new Map<number, DOMRect>();

        teamIndices.forEach((teamIndex) => {
            result.teams[teamIndex].forEach((shiftingMemberId) => {
                if (shiftingMemberId === memberId) {
                    return;
                }

                const tokenStack = this.root
                    .querySelector<HTMLElement>(`[data-member-token][data-member-id="${shiftingMemberId}"]`)
                    ?.closest<HTMLElement>(".member-token-stack");

                if (tokenStack) {
                    snapshot.set(shiftingMemberId, tokenStack.getBoundingClientRect());
                }
            });
        });

        return snapshot.size > 0 ? snapshot : null;
    }

    private animateOrderClosure(snapshot: Map<number, DOMRect> | null) {
        if (!snapshot) {
            return;
        }

        snapshot.forEach((oldRect, memberId) => {
            const tokenStack = this.root
                .querySelector<HTMLElement>(`[data-member-token][data-member-id="${memberId}"]`)
                ?.closest<HTMLElement>(".member-token-stack");

            if (!tokenStack) {
                return;
            }

            const newRect = tokenStack.getBoundingClientRect();
            const translateX = oldRect.left - newRect.left;
            const translateY = oldRect.top - newRect.top;

            if (Math.abs(translateX) < 1 && Math.abs(translateY) < 1) {
                return;
            }

            const tokenHeight = Math.max(oldRect.height, newRect.height);
            const changesRow = Math.abs(translateY) > tokenHeight * 0.64;
            const exitDirection = translateY > 0 ? -1 : 1;
            const enterDirection = translateY > 0 ? 1 : -1;
            const keyframes = changesRow
                ? [
                      {
                          transform: `translate(${translateX}px, ${translateY}px) scale(1)`,
                          opacity: 1
                      },
                      {
                          transform: `translate(${translateX + exitDirection * 44}px, ${translateY}px) scale(0.96)`,
                          opacity: 0.08,
                          offset: 0.46
                      },
                      {
                          transform: `translate(${enterDirection * 44}px, 0) scale(0.96)`,
                          opacity: 0.08,
                          offset: 0.47
                      },
                      {
                          transform: "translate(0, 0) scale(1)",
                          opacity: 1
                      }
                  ]
                : [
                      {
                          transform: `translate(${translateX}px, ${translateY}px)`,
                          opacity: 1
                      },
                      {
                          transform: "translate(0, 0)",
                          opacity: 1
                      }
                  ];
            const animation = tokenStack.animate(keyframes, {
                duration: changesRow ? 440 : 360,
                easing: "cubic-bezier(0.18, 0.96, 0.24, 1)"
            });
            this.trackAnimation(animation);
        });
    }

    private animateMemberToDestination(memberId: number, releasePoint: { x: number; y: number }, destination: DropDestination) {
        if (!this.refs.stage) {
            return;
        }

        const token = this.root.querySelector<HTMLElement>(`[data-member-token][data-member-id="${memberId}"]`);

        if (!token) {
            return;
        }

        const stageRect = this.refs.stage.getBoundingClientRect();
        const targetRect = token.getBoundingClientRect();
        const targetCenterX = targetRect.left - stageRect.left + targetRect.width / 2;
        const targetCenterY = targetRect.top - stageRect.top + targetRect.height / 2;
        const translateX = releasePoint.x - stageRect.left - targetCenterX;
        const translateY = releasePoint.y - stageRect.top - targetCenterY;
        const keyframes =
            destination.zone === "team"
                ? [
                      {
                          transform: `translate(${translateX}px, ${translateY}px) scale(1.06)`
                      },
                      {
                          transform: `translate(${translateX * 0.18}px, ${translateY * 0.14 - 16}px) scale(1.02)`
                      },
                      {
                          transform: "translate(0, 0) scale(1)"
                      }
                  ]
                : [
                      {
                          transform: `translate(${translateX}px, ${translateY}px) rotate(-10deg) scale(1.08)`
                      },
                      {
                          transform: `translate(${translateX * 0.32}px, ${translateY * 0.18}px) rotate(7deg) scale(1.02)`
                      },
                      {
                          transform: "translate(0, 0) rotate(0deg) scale(1)"
                      }
                  ];
        const duration = destination.zone === "team" ? 420 : 560;
        const animation = token.animate(keyframes, {
            duration,
            easing: "cubic-bezier(0.2, 0.9, 0.2, 1)"
        });
        this.trackAnimation(animation);

        const soundTimeoutId = this.scheduleSyncedAudio(duration - MANUAL_DROP_AUDIO_SYNC_LEAD_MS, () => {
            if (destination.zone === "team") {
                this.audio.playLatch(this.getLatchPlaybackRate(memberId));
                return;
            }

            this.audio.playBounce();
        });

        void animation.finished
            .then(() => {
                window.clearTimeout(soundTimeoutId);
                this.activeTimeouts.delete(soundTimeoutId);
            })
            .catch(() => {
                window.clearTimeout(soundTimeoutId);
                this.activeTimeouts.delete(soundTimeoutId);
            });

        if (destination.zone === "team") {
            const teamBox = token.closest<HTMLElement>("[data-team-box]");

            if (teamBox) {
                this.spawnSplash(teamBox, token, 0);
            }
        }
    }

    private registerTeamLanding(element: HTMLElement, memberId: number) {
        const teamBox = element.closest<HTMLElement>("[data-team-box]");

        if (!teamBox) {
            return;
        }

        const teamIndex = Number.parseInt(teamBox.dataset.teamIndex ?? "", 10);

        if (Number.isNaN(teamIndex)) {
            return;
        }

        const landedMembers = this.landedTeamMembers.get(teamIndex) ?? [];
        landedMembers.push(memberId);
        this.landedTeamMembers.set(teamIndex, landedMembers);
    }

    private syncFinishedOrderLayout() {
        const result = this.state.result;

        if (!result || result.mode !== "orders") {
            return;
        }

        this.orderLayoutRefreshPending = false;
        this.syncResults();
    }

    private scheduleSyncedAudio(delayMs: number, playSound: () => void): number {
        const delay = Math.max(0, delayMs);
        const targetTime = performance.now() + delay;
        const staleDelayMs = this.isCompactMobileViewport() ? 520 : STALE_AUDIO_DELAY_MS;
        const timeoutId = window.setTimeout(() => {
            this.activeTimeouts.delete(timeoutId);

            if (!this.soundEnabled || document.hidden || performance.now() - targetTime > staleDelayMs) {
                return;
            }

            playSound();
        }, delay);

        this.activeTimeouts.add(timeoutId);
        return timeoutId;
    }

    private syncDropTargetHighlight() {
        if (this.highlightedTeamElement) {
            const currentIndex = Number.parseInt(this.highlightedTeamElement.dataset.teamIndex ?? "", 10);

            if (!this.highlightedTeamElement.isConnected || Number.isNaN(currentIndex) || currentIndex !== this.highlightedTeamIndex) {
                this.highlightedTeamElement.classList.remove("is-drop-target");
                this.highlightedTeamElement = null;
            }
        }

        if (this.highlightedTeamIndex === null) {
            return;
        }

        if (this.highlightedTeamElement) {
            return;
        }

        const nextElement = this.root.querySelector<HTMLElement>(`[data-team-box][data-team-index="${this.highlightedTeamIndex}"]`);

        if (!nextElement) {
            return;
        }

        nextElement.classList.add("is-drop-target");
        this.highlightedTeamElement = nextElement;
    }

    private clearHoverState() {
        this.highlightedTeamIndex = null;
        this.syncDropTargetHighlight();
    }
}

export const createRandomTeamsTool = (
    root: HTMLElement,
    state: RandomTeamsToolState
): { destroy: () => void; refreshLanguage: () => void } => new RandomTeamsTool(root, state);
